import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAppContext, noteItemRevRows } from './AppContext';
import Thread from './Thread';
import ItemDocuments from './ItemDocuments';
import ItemImageGallery, { uploadImage } from './ItemImageGallery';
import CancelItemModal from './CancelItemModal';
import ItemMessagesPanel from './ItemMessagesPanel';
import ChangeOrderModal from './ChangeOrderModal';
import { isItemReadyForFinalInvoice } from '../utils';
import { COMEmailModal, EmailPanel, ComponentQuotesPanel } from './emailPanels';
import { FabricPanel, TrimPanel, HardwarePanel, WorkroomPanel, RollerShadePanel, BLANK_COM } from './panels';
import { IS, Lbl, Inp, Txta, Sel, Row, SHead, Btn, TabBtn, QtyTypeField, ReadValue, CurrencyInput, Dropdown } from './ui';
import VendorPicker from './VendorPicker';
import { fmtInput, parsePrice, convertDimensions, STATUSES, MANUALLY_SELECTABLE_STATUSES, statusColor, isFinanciallyCommitted, isCancelled, calcClientPrice, calcNetTotal, calcClientTotal, calcClientDeposit, calcVendorDeposit, calcVendorDepositWithFees, calcDepositInvoiceTotal, calcFinalInvoiceTotal, applyFreightMarkup, customFeeClient, itemFeeMarkupPct, itemFeeBreakdown, resolveMarkupDefaults, computeItemTax, salesTaxItemStatus, salesTaxStateRuleFor, salesTaxBucketDate, salesTaxRemittancesForItem, salesTaxReturnLabel, salesTaxShortDate, salesTaxJurisdiction, fmtMoney, resolveAuthorName, sortNotesDesc, noteInsertRow, hasAdminAccess, computeSidemark, isNonProcured, activeProjects, manuallySelectableStatusesForProcurementClass, estimatedShipDate, itemPaidShare, threadStateFor } from '../utils';
import { ItemSheetButton, PresentationSpecButton } from './documentTabs';
import TrackingTimeline from './TrackingTimeline';
import { dateStr, saveDraft, loadDraft, clearDraft } from './helpers';
import QuoteSummaryPanel from './QuoteSummaryPanel';
import TearSheet from './TearSheet';

const CHILD_TYPES = ["Fabric","Trim","Hardware","Workroom","Bulb"];
const CHILD_SCHEDULES_WT = ["Window Treatments"];
const CHILD_SCHEDULES_COM = ["Furniture","Cushions & Throw Pillows"];

// Component type buttons shown when adding an item to an assembly, keyed by schedule
const ASSEMBLY_COMPONENTS = {
  'Window Treatments':        ['Fabric','Trim','Workroom','Hardware','Roller Shades'],
  'Cushions & Throw Pillows': ['Fabric','Trim','Workroom'],
  'Furniture':                ['Fabric','Trim','Workroom','Hardware'],
  'Lighting':                 ['Bulb'],
  'Plumbing':                 ['Fixture','Trim'],
  'Millwork':                 ['Hardware'],
};

const BULB_VENDORS = ["Emery Allen","Tala"];
const EMERY_ALLEN_BULBS = [
  // A-Series
  {sku:"EA-A19-5.5W-001-D",  shape:"A19",   watts:5.5,  lumens:450,  kelvin:2700, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"Replaces 40W"},
  {sku:"EA-A19-7W-001-D",    shape:"A19",   watts:7,    lumens:650,  kelvin:2700, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"Replaces 60W"},
  {sku:"EA-A19-9W-001-D",    shape:"A19",   watts:9,    lumens:800,  kelvin:2700, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"Replaces 60W"},
  {sku:"EA-A19-9W-002-D",    shape:"A19",   watts:9,    lumens:800,  kelvin:3000, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"Replaces 60W / 3000K"},
  {sku:"EA-A19-11W-001-D",   shape:"A19",   watts:11,   lumens:1100, kelvin:2700, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"Replaces 75W"},
  {sku:"EA-A19-11W-002-D",   shape:"A19",   watts:11,   lumens:1100, kelvin:3000, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"Replaces 75W / 3000K"},
  // Globe
  {sku:"EA-G16-3W-001-D",    shape:"G16",   watts:3,    lumens:180,  kelvin:2700, cri:95, base:"E12",   voltage:"120V", finish:"Clear",  notes:"Candelabra globe"},
  {sku:"EA-G16-3W-002-D",    shape:"G16",   watts:3,    lumens:180,  kelvin:2700, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"Medium base globe"},
  {sku:"EA-G25-5W-001-D",    shape:"G25",   watts:5,    lumens:360,  kelvin:2700, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"G25 globe"},
  {sku:"EA-G25-5W-002-D",    shape:"G25",   watts:5,    lumens:360,  kelvin:2700, cri:95, base:"E26",   voltage:"120V", finish:"Frosted",notes:"G25 globe frosted"},
  {sku:"EA-G30-7W-001-D",    shape:"G30",   watts:7,    lumens:550,  kelvin:2700, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"G30 globe"},
  {sku:"EA-G40-7W-001-D",    shape:"G40",   watts:7,    lumens:550,  kelvin:2700, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"G40 globe"},
  // Candelabra
  {sku:"EA-B11-3W-001-D",    shape:"B11",   watts:3,    lumens:200,  kelvin:2700, cri:95, base:"E12",   voltage:"120V", finish:"Clear",  notes:"Torpedo E12"},
  {sku:"EA-B11-4W-001-D",    shape:"B11",   watts:4,    lumens:300,  kelvin:2700, cri:95, base:"E12",   voltage:"120V", finish:"Clear",  notes:"Torpedo E12"},
  {sku:"EA-B11-5W-001-D",    shape:"B11",   watts:5,    lumens:400,  kelvin:2700, cri:95, base:"E12",   voltage:"120V", finish:"Clear",  notes:"Torpedo E12"},
  {sku:"EA-B11-3W-002-D",    shape:"B11",   watts:3,    lumens:200,  kelvin:2700, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"Torpedo E26"},
  {sku:"EA-CA10-4W-001-D",   shape:"CA10",  watts:4,    lumens:300,  kelvin:2700, cri:95, base:"E12",   voltage:"120V", finish:"Clear",  notes:"Bent tip E12"},
  {sku:"EA-CA10-4W-002-D",   shape:"CA10",  watts:4,    lumens:300,  kelvin:2700, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"Bent tip E26"},
  // G8 Bi-Pin Series (2700K)
  {sku:"EA-G8-2.5W-001-279F-D", shape:"G8", watts:2.5, lumens:250, kelvin:2700, cri:90, base:"G8", voltage:"120V", finish:"Clear", notes:"Replaces 20W halogen"},
  {sku:"EA-G8-4.5W-001-279F-D", shape:"G8", watts:4.5, lumens:450, kelvin:2700, cri:90, base:"G8", voltage:"120V", finish:"Clear", notes:"Replaces 50W halogen"},
  {sku:"EA-G8-5.0W-001-279F-D", shape:"G8", watts:5.0, lumens:550, kelvin:2700, cri:90, base:"G8", voltage:"120V", finish:"Clear", notes:"Replaces 60W halogen"},
  {sku:"EA-G8-6.0W-001-279F-D", shape:"G8", watts:6.0, lumens:600, kelvin:2700, cri:90, base:"G8", voltage:"120V", finish:"Clear", notes:"Replaces 70W halogen"},
  {sku:"EA-G8-7.0W-001-279F-D", shape:"G8", watts:7.0, lumens:700, kelvin:2700, cri:90, base:"G8", voltage:"120V", finish:"Clear", notes:"Replaces 80W halogen"},
  // PAR / Reflector
  {sku:"EA-PAR16-4W-001-D",  shape:"PAR16", watts:4,    lumens:300,  kelvin:2700, cri:95, base:"GU10",  voltage:"120V", finish:"Clear",  notes:"PAR16 flood 40°"},
  {sku:"EA-PAR16-4W-002-D",  shape:"PAR16", watts:4,    lumens:300,  kelvin:3000, cri:95, base:"GU10",  voltage:"120V", finish:"Clear",  notes:"PAR16 flood 40° / 3000K"},
  {sku:"EA-PAR20-7W-001-D",  shape:"PAR20", watts:7,    lumens:550,  kelvin:2700, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"PAR20 flood"},
  {sku:"EA-PAR30-10W-001-D", shape:"PAR30", watts:10,   lumens:900,  kelvin:2700, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"PAR30 flood"},
  {sku:"EA-PAR38-12W-001-D", shape:"PAR38", watts:12,   lumens:1100, kelvin:2700, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"PAR38 flood"},
  {sku:"EA-PAR38-15W-001-D", shape:"PAR38", watts:15,   lumens:1400, kelvin:3000, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"PAR38 flood / 3000K"},
  // MR16
  {sku:"EA-MR16-4W-001-D",   shape:"MR16",  watts:4,    lumens:300,  kelvin:2700, cri:95, base:"GU5.3", voltage:"12V",  finish:"Clear",  notes:"Requires 12V transformer"},
  {sku:"EA-MR16-5.5W-001-D", shape:"MR16",  watts:5.5,  lumens:450,  kelvin:2700, cri:95, base:"GU5.3", voltage:"12V",  finish:"Clear",  notes:"Requires 12V transformer"},
  {sku:"EA-MR16-6W-001-D",   shape:"MR16",  watts:6,    lumens:550,  kelvin:3000, cri:95, base:"GU5.3", voltage:"12V",  finish:"Clear",  notes:"3000K · Requires 12V transformer"},
  // Tubular
  {sku:"EA-T9-4W-001-D",     shape:"T9",    watts:4,    lumens:280,  kelvin:2700, cri:95, base:"E12",   voltage:"120V", finish:"Clear",  notes:"Tubular candelabra"},
  {sku:"EA-T14-7W-001-D",    shape:"T14",   watts:7,    lumens:550,  kelvin:2700, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"Tubular medium"},
  // Vintage / ST
  {sku:"EA-ST18-4W-001-D",   shape:"ST18",  watts:4,    lumens:280,  kelvin:2200, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"Vintage Edison / 2200K"},
  {sku:"EA-ST18-4W-002-D",   shape:"ST18",  watts:4,    lumens:280,  kelvin:2700, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"Vintage Edison"},
  {sku:"EA-ST19-4.5W-001-D", shape:"ST19",  watts:4.5,  lumens:350,  kelvin:2200, cri:95, base:"E26",   voltage:"120V", finish:"Clear",  notes:"Squirrel cage / 2200K"},
  // GU24
  {sku:"EA-A19-9W-GU24-D",   shape:"A19",   watts:9,    lumens:800,  kelvin:2700, cri:95, base:"GU24",  voltage:"120V", finish:"Clear",  notes:"GU24 twist-lock"},
  {sku:"EA-A19-11W-GU24-D",  shape:"A19",   watts:11,   lumens:1100, kelvin:2700, cri:95, base:"GU24",  voltage:"120V", finish:"Clear",  notes:"GU24 twist-lock"},
];

const SCHEDULE_FIELDS = {
  "Furniture":[],
  "Lighting":[
    {key:"technology",label:"Technology",type:"text"},
    {key:"bulb",label:"Bulb",type:"text"},
    {key:"base",label:"Base",type:"text"},
    {key:"wattage",label:"Wattage",type:"text"},
    {key:"lumens",label:"Lumens",type:"text"},
    {key:"kelvins",label:"Kelvins",type:"text"},
    {key:"cri",label:"CRI",type:"text"},
    {key:"voltage",label:"Voltage",type:"text"},
    {key:"circuitry",label:"Circuitry",type:"text"},
  ],
  "Plumbing / Fixtures":[
    {key:"roughInNotes",label:"Rough-in Notes",type:"text"},
    {key:"gpm",label:"GPM / Flow Rate",type:"text"},
    {key:"voltage",label:"Voltage",type:"text"},
  ],
  "Art & Objects":[
    {key:"artistMaker",label:"Artist / Maker",type:"text"},
    {key:"medium",label:"Medium",type:"text"},
    {key:"framingMount",label:"Framing / Mount",type:"text"},
  ],
  "Hardware":[
    {key:"material",label:"Material",type:"text"},
    {key:"application",label:"Application",type:"text"},
  ],
  "Equipment":[
    {key:"voltageFuel",label:"Voltage / Fuel Type",type:"text"},
    {key:"installNotes",label:"Install Notes",type:"textarea"},
  ],
  "Appliances":[
    {key:"voltageFuel",label:"Voltage / Fuel Type",type:"text"},
    {key:"btu",label:"BTU / Capacity",type:"text"},
    {key:"energyStar",label:"Energy Star",type:"text"},
    {key:"installNotes",label:"Install Notes",type:"textarea"},
  ],
  "Tile & Stone":[
    {key:"material",label:"Material",type:"text"},
    {key:"size",label:"Tile Size",type:"text"},
    {key:"thickness",label:"Thickness",type:"text"},
    {key:"coverageNotes",label:"Coverage / Qty Notes",type:"text"},
    {key:"groutSpec",label:"Grout Spec",type:"text"},
  ],
  "Finish Schedule":[
    {key:"type",label:"Type",type:"select",options:["Paint","Wallpaper","Wall Covering","Flooring","Tile","Stone","Other"]},
    {key:"space",label:"Space",type:"text"},
    {key:"element",label:"Element",type:"text"},
    {key:"collection",label:"Collection",type:"text"},
    {key:"colorPattern",label:"Color / Pattern",type:"text"},
    {key:"colorNumber",label:"Color / Product Number",type:"text"},
    {key:"sheenFinish",label:"Sheen / Finish",type:"text"},
    {key:"coverage",label:"Coverage",type:"text"},
    {key:"applicationArea",label:"Application Area",type:"text"},
  ],
  "Window Treatments":[
    // Construction
    {key:"style",label:"Style",type:"select",options:["Ripple Fold","Pinch Pleat","Flat Panel","Goblet Pleat","Inverted Box Pleat","Roman Shade","Roller Shade","Woven Wood","Sheer Panel"]},
    {key:"draw",label:"Draw",type:"select",options:["Stack Right","Stack Left","Center Split"]},
    {key:"mountType",label:"Mount Type",type:"select",options:["Inside Mount","Outside Mount","Ceiling Mount"]},
    {key:"controlType",label:"Control Type",type:"select",options:["Manual","Motorized","Automated"]},
    {key:"lining",label:"Lining",type:"select",options:["Unlined","Lined","Blackout","Interlining","Interlining + Blackout"]},
    // Fabric
    {key:"collection",label:"Fabric Collection",type:"text",placeholder:"e.g. Schumacher Celerie"},
    {key:"patternColorway",label:"Pattern / Colorway",type:"text",placeholder:"e.g. Leaf Weave / Ivory"},
    {key:"content",label:"Content",type:"text",placeholder:"e.g. 100% Linen"},
    {key:"fabricWidth",label:"Fabric Width",type:"text",placeholder:'e.g. 54"'},
    {key:"repeatH",label:"Repeat H",type:"text",placeholder:'e.g. 13.5"'},
    {key:"repeatV",label:"Repeat V",type:"text",placeholder:'e.g. 13.5"'},
    {key:"yardage",label:"Yardage",type:"text",placeholder:"e.g. 12 yards"},
    {key:"cutDirection",label:"Cut Direction",type:"text",placeholder:"e.g. Railroaded or Straight cut"},
    {key:"comInstructions",label:"COM Instructions",type:"textarea",placeholder:"e.g. Match repeat at center seam, double fullness..."},
    // Trim
    {key:"trimDescription",label:"Trim Description",type:"text",placeholder:"e.g. Samuel & Sons tape trim, 1.5in"},
    {key:"trimSku",label:"Trim SKU / Pattern",type:"text",placeholder:"e.g. STL-1254"},
    {key:"trimColorway",label:"Trim Colorway",type:"text",placeholder:"e.g. Antique Gold"},
    {key:"trimYardage",label:"Trim Yardage",type:"text",placeholder:"e.g. 8 yards"},
    // Hardware
    {key:"hardwareDescription",label:"Hardware Description",type:"text",placeholder:"e.g. Lutron Caseta motor, rod + brackets"},
    {key:"hardwareSku",label:"Hardware SKU / Model",type:"text",placeholder:"e.g. Lutron RRD-6CL-WH"},
    // Workroom / Ship-to
    {key:"workroomName",label:"Ship to Workroom",type:"text",placeholder:"e.g. ABC Drapery Workroom"},
    {key:"shipToAddress",label:"Workroom Address",type:"text",placeholder:"e.g. 123 Main St, San Francisco CA 94103"},
  ],
  "Cushions & Throw Pillows":[
    {key:"cushionSource",label:"Source",type:"select",options:["Custom / Workroom","Ready-Made"]},
    {key:"fabricFace",label:"Face Fabric",type:"text"},
    {key:"fabricBack",label:"Back Fabric",type:"text"},
    {key:"fillType",label:"Fill / Insert Type",type:"select",options:["Down & Feather","Down Alternative","Foam","Kapok","Other"]},
    {key:"closure",label:"Closure",type:"select",options:["Invisible Zip","Envelope","Button","Hook & Loop","Sewn Closed"]},
    {key:"welt",label:"Welt / Trim",type:"text"},
    {key:"flangeWidth",label:"Flange Width",type:"text"},
    {key:"finishedSize",label:"Finished Size",type:"text"},
  ],
  // Softgoods (2026-09-08) — bedding, towels, linens. No bespoke spec
  // panel by design (Cimon: "basic specs on the main item list" —
  // dimension/size, finish, notes are core item fields), so it mirrors
  // Furniture: an empty field list that prints through the standard
  // template columns.
  "Softgoods":[],
  "Millwork":[
    {key:"material",label:"Material",type:"text"},
    {key:"speciesGrade",label:"Species / Grade",type:"text"},
    {key:"profileStyle",label:"Profile / Style",type:"text"},
    {key:"paintStainColor",label:"Paint / Stain Color",type:"text"},
    {key:"linearFootage",label:"Linear Footage",type:"text"},
    {key:"hardwareSpec",label:"Hardware Spec",type:"text"},
    {key:"installNotes",label:"Install Notes",type:"textarea"},
    {key:"shopDrawingRef",label:"Shop Drawing Ref",type:"text"},
  ],
};
// Paint & Wallpaper removed — items auto-migrate to Finish Schedule on load

const SCHEDULE_NAMES = Object.keys(SCHEDULE_FIELDS);


function ChildItemForm(p){
  var child=p.child;
  var vendors=p.vendors||[];
  var siblings=p.siblings||[];
  var us=useState(false);var uploading=us[0];var setUploading=us[1];
  var ue=useState("");var uploadErr=ue[0];var setUploadErr=ue[1];
  var imgRef=useRef(null);
  function s(k){return function(v){var n=Object.assign({},child);n[k]=v;p.onChange(n);};}
  function sSpecs(specs){var n=Object.assign({},child);n.specs=specs;p.onChange(n);}
  var removeBtn=p.onRemove?<button onClick={p.onRemove} style={{background:"none",border:"none",color:"#aaa",cursor:"pointer",fontSize:14,padding:"2px 6px",lineHeight:1}}>✕</button>:null;
  var typeColors={"Fabric":["#666","#fafafa","#E0E0DC"],"COM/COL Fabric":["#666","#fafafa","#E0E0DC"],"Trim":["#e65100","#fff8e1","#ffe082"],"Hardware":["#2e7d32","#e8f5e9","#a5d6a7"],"Workroom":["#555","#FAFAFA","#E0E0DC"],"Bulb":["#f9a825","#fffde7","#fff9c4"]};
  var tc=typeColors[child.childType]||["#666","#fafafa","#e0e0e0"];
  var vendorList=vendors;
  var typePanel=null;
  if(child.childType==="Fabric"||child.childType==="COM/COL Fabric"){typePanel=<FabricPanel specs={child.specs||{}} onChange={sSpecs} />;}
  else if(child.childType==="Trim"){typePanel=<TrimPanel specs={child.specs||{}} onChange={sSpecs} />;}
  else if(child.childType==="Hardware"){typePanel=<HardwarePanel specs={child.specs||{}} onChange={sSpecs} />;}
  else if(child.childType==="Workroom"){typePanel=<WorkroomPanel specs={child.specs||{}} onChange={sSpecs} siblings={siblings} />;}
  var defaultQtyType=child.childType==="Fabric"||child.childType==="Trim"?"Yard":"Each";

  var imagePanel=null;
  if(child.childType==="COM/COL Fabric"){
    imagePanel=<div style={{marginTop:10,paddingTop:10,borderTop:"1px solid #E0E0DC"}}>
      <div style={{fontSize:11,fontWeight:500,color:"#999",marginBottom:6,letterSpacing:0.3}}>FABRIC / MATERIAL PHOTO</div>
      {child.imageUrl?<div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
        <img src={child.imageUrl} alt="fabric" style={{width:80,height:80,objectFit:"cover",borderRadius:6,border:"1px solid #E0E0DC",cursor:"pointer"}} onClick={function(){window.open(child.imageUrl,'_blank');}} />
        <div>
          <button onClick={function(){p.onChange(Object.assign({},child,{imageUrl:"",images:[]}));}} style={{display:"block",padding:"4px 10px",background:"none",border:"1px solid #E0E0DC",borderRadius:5,fontSize:11,cursor:"pointer",color:"#888",marginBottom:6}}>Remove</button>
          <button onClick={function(){imgRef.current&&imgRef.current.click();}} style={{display:"block",padding:"4px 10px",background:"none",border:"1px solid #999",borderRadius:5,fontSize:11,cursor:"pointer",color:"#999"}}>Replace</button>
        </div>
      </div>:
      <button
        onClick={function(){imgRef.current&&imgRef.current.click();}}
        disabled={uploading}
        style={{padding:"8px 14px",background:"#fff",border:"1.5px dashed #D0D0CC",borderRadius:6,fontSize:12,cursor:"pointer",color:"#999",fontWeight:500}}
      >{uploading?"Uploading…":"+ Upload Photo"}</button>}
      {uploadErr&&<div style={{fontSize:11,color:"#c62828",marginTop:4}}>{uploadErr}</div>}
      <input ref={imgRef} type="file" accept="image/*" style={{display:"none"}} onChange={function(e){
        var file=e.target.files&&e.target.files[0];
        if(!file)return;
        setUploading(true);setUploadErr("");
        uploadImage(file,String(child.id)).then(function(res){
          p.onChange(Object.assign({},child,{imageUrl:res.url,images:[{url:res.url,path:res.path,type:"Primary"}]}));
          setUploading(false);
        }).catch(function(err){
          setUploadErr("Upload failed: "+err.message);
          setUploading(false);
        });
        e.target.value="";
      }} />
    </div>;
  }

  return <div style={{background:tc[1],border:"1px solid "+tc[2],borderRadius:8,padding:14,marginBottom:10,borderLeft:"3px solid "+tc[0]}}>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
      <div style={{fontSize:12,fontWeight:700,color:tc[0]}}>{"↳ "+child.childType+(p.parentItem?" — "+p.parentItem:"")}</div>
      {removeBtn}
    </div>
    <Row>
      <div style={{flex:2}}><Inp label="Description" value={child.item||""} onChange={s("item")}
        placeholder={child.childType==="Fabric"?"e.g. Schumacher Linen Velvet":child.childType==="Trim"?"e.g. Samuel &amp; Sons Tape Trim":child.childType==="Hardware"?"e.g. Lutron Caseta Motor + Rod":child.childType==="Workroom"?"e.g. Custom drapery fabrication":""} /></div>
      <div style={{flex:1}}><Inp label="Cost" value={child.netPrice||""} onChange={s("netPrice")} placeholder="$0.00" /></div>
    </Row>
    <VendorPicker
      label={child.childType==="Workroom"?"Workroom":"Vendor"}
      value={child.vendor||""}
      onChange={s("vendor")}
      vendors={vendorList}
      items={p.items}
      purchaseOrders={p.purchaseOrders}
      onAddVendor={p.onAddVendor} />
    <Row>
      <div style={{flex:1}}><Inp label="Qty" value={child.qty||"1"} onChange={s("qty")} placeholder="1" /></div>
      <div style={{flex:1}}><QtyTypeField value={child.qtyType||defaultQtyType} onChange={s("qtyType")} /></div>
      <div style={{flex:2}}>{(function(){
        // Past On Proposal, status is owned by cascades (proposal approval,
        // deposit invoice paid, etc.) — render read-only to match the lock-mode
        // UX. Manual selection is restricted to MANUALLY_SELECTABLE_STATUSES.
        var cur=child.status||"Specifying";
        var statusLocked=MANUALLY_SELECTABLE_STATUSES.indexOf(cur)<0;
        return <Sel label="Status" value={cur} onChange={s("status")} dataField="status" locked={statusLocked}>{MANUALLY_SELECTABLE_STATUSES.map(function(st){return <option key={st} value={st}>{st}</option>;})}</Sel>;
      })()}</div>
    </Row>
    <Sel label="Ships to" value={String(child.shipToVendorId||'')} onChange={function(v){var n=Object.assign({},child);n.shipToVendorId=v===''?null:isNaN(Number(v))?v:parseInt(v);p.onChange(n);}}>
      <option value="">— Not set —</option>
      <option value="install_on_site">Install on site</option>
      {vendors.filter(function(v){return v.vendorType==="Receiver"||v.vendorType==="Workroom";}).map(function(v){return <option key={v.id} value={String(v.id)}>{v.name+(v.vendorType==="Workroom"?" (Workroom)":"")}</option>;})}
    </Sel>
    <Txta label={"Ship-to Address"+(child.shipToVendorId?" (overrides receiver above)":"")} value={child.shipToAddress||""} onChange={s("shipToAddress")} placeholder="Custom shipping address for this component..." rows={2} />
    {typePanel}
    {imagePanel}
    <Txta label="Notes" value={child.notes||""} onChange={s("notes")} placeholder="Additional notes..." rows={1} />
    <Row>
      <div style={{flex:1}}><Inp label="Vendor Deposit %" value={child.depositPct!=null?String(child.depositPct):""} onChange={s("depositPct")} placeholder="50" /></div>
      <div style={{flex:1}}><Inp label="Freight / Shipping" value={(child.fees||{}).freight||""} onChange={function(v){s("fees")(Object.assign({},child.fees||{},{freight:v}));}} placeholder="$0.00" /></div>
      <div style={{flex:1}}><Inp label="Tariff" value={(child.fees||{}).tariff||""} onChange={function(v){s("fees")(Object.assign({},child.fees||{},{tariff:v}));}} placeholder="$0.00" /></div>
    </Row>
    <Row>
      <div style={{flex:1}}><Inp label="Sales Tax" value={(child.fees||{}).salesTax||""} onChange={function(){ /* Phase 2: tax is derived from project config */ }} placeholder="$0.00" locked={true} /></div>
      <div style={{flex:1}}><Inp label="Crating" value={(child.fees||{}).crating||""} onChange={function(v){s("fees")(Object.assign({},child.fees||{},{crating:v}));}} placeholder="$0.00" /></div>
      <div style={{flex:1}}><Inp label="Installation" value={(child.fees||{}).installation||""} onChange={function(v){s("fees")(Object.assign({},child.fees||{},{installation:v}));}} placeholder="$0.00" /></div>
      <div style={{flex:1}}></div>
    </Row>
    <label style={{display:"flex",alignItems:"center",gap:8,marginTop:10,cursor:"pointer",userSelect:"none"}}>
      <input type="checkbox" checked={!!(child.importSource&&(child.importSource.indexOf("Pre-App")>=0||child.importSource.indexOf("ABD")>=0))} onChange={function(e){s("importSource")(e.target.checked?"ABD":"");}} style={{width:14,height:14,cursor:"pointer",flexShrink:0}} />
      <span style={{fontSize:12,color:"#666"}}>ABD item <span style={{color:"#bbb"}}>— excluded from financial totals</span></span>
    </label>
    <label style={{display:"flex",alignItems:"center",gap:8,marginTop:8,cursor:"pointer",userSelect:"none"}}>
      <input type="checkbox" checked={!!child.offScheduleDefault} onChange={function(e){s("offScheduleDefault")(e.target.checked);}} style={{width:14,height:14,cursor:"pointer",flexShrink:0}} />
      <span style={{fontSize:12,color:"#666"}}>Off schedule by default <span style={{color:"#bbb"}}>— appears in the schedule picker but starts unchecked</span></span>
    </label>
  </div>;
}

function SpecFields(p){
  var schedule=p.schedule;var specs=p.specs||{};var onChange=p.onChange;
  if(!schedule)return <div style={{padding:"16px",textAlign:"center",color:"#bbb",fontSize:13,background:"#fafafa",borderRadius:6}}>Select a schedule to see specification fields</div>;
  var fields=SCHEDULE_FIELDS[schedule]||[];
  // Cushions: hide construction fields when Ready-Made selected
  if(schedule==="Cushions & Throw Pillows"&&(specs.cushionSource||"")==="Ready-Made"){
    fields=fields.filter(function(f){return["cushionSource","description","manufacturer","model","sourceUrl","notes"].indexOf(f.key)>=0;});
  }
  var fieldKeys=fields.map(function(f){return f.key;});
  // Also show any populated fields from other schedules (e.g. lighting item imported as furniture)
  var extraFields=[];Object.keys(specs).forEach(function(k){if(!fieldKeys.includes(k)&&specs[k]&&specs[k].toString().trim()){extraFields.push({key:k,label:k.replace(/([A-Z])/g," $1").replace(/^./,function(s){return s.toUpperCase();}),type:"text",_extra:true});}});
  var allFields=fields.concat(extraFields);
  function set(key){return function(val){var n=Object.assign({},specs);n[key]=val;onChange(n);};}
  var pairs=[];for(var i=0;i<allFields.length;i+=2){pairs.push(allFields.slice(i,i+2));}
  return <>
    {pairs.map(function(pair,pi){return <Row key={pi} gap={12}>{pair.map(function(f){var val=specs[f.key]||"";var inner;if(f.type==="textarea"){inner=<Txta label={f.label} value={val} onChange={set(f.key)} placeholder={f.placeholder||""} />;}else if(f.type==="select"){inner=<Sel label={f.label} value={val} onChange={set(f.key)}><option value="">— Select —</option>{(f.options||[]).map(function(o){return <option key={o} value={o}>{o}</option>;})}</Sel>;}else if(f.type==="url"){var openLink=val?<a href={val} target="_blank" rel="noreferrer" style={{padding:"7px 10px",border:"1px solid #ddd",borderRadius:6,fontSize:11,background:"#f5f5f5",color:"#1a56db",textDecoration:"none",whiteSpace:"nowrap"}}>Open ↗</a>:null;inner=<div style={{marginBottom:12}}><Lbl>{f.label}</Lbl><div style={{display:"flex",gap:6}}><input type="url" value={val} onChange={function(e){set(f.key)(e.target.value);}} placeholder={f.placeholder||"https://..."} style={IS} />{openLink}</div></div>;}else{inner=<Inp label={f.label} value={val} onChange={set(f.key)} placeholder={f.placeholder||""} />;}return <div key={f.key} style={{flex:1,minWidth:0}}>{inner}</div>;})};</Row>;})}
  </>;
}

// Strict room picker for ItemForm — rooms must be selected from project list, not free-typed

function RoomAC(p){
  var rooms=p.rooms||[];
  var os=useState(false);var open=os[0];var setOpen=os[1];
  var ref=useRef(null);
  var filtered=rooms.filter(function(r){return r.name.toLowerCase().indexOf(p.nameVal.toLowerCase())>=0&&p.nameVal.length>0;});
  useEffect(function(){function handler(e){if(ref.current&&!ref.current.contains(e.target)){setOpen(false);}}document.addEventListener("mousedown",handler);return function(){document.removeEventListener("mousedown",handler);};},[]);
  var dropdown=null;
  if(open&&filtered.length>0){dropdown=<div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1px solid #ddd",borderRadius:6,boxShadow:"0 4px 12px rgba(0,0,0,.1)",zIndex:100,maxHeight:200,overflowY:"auto"}}>
    {filtered.map(function(r,i){return <div key={i} onMouseDown={function(){p.onName(r.name);p.onNumber(r.number);setOpen(false);}} style={{padding:"8px 12px",fontSize:13,cursor:"pointer",borderBottom:"1px solid #f5f5f5",display:"flex",justifyContent:"space-between"}}>
      <span>{r.name}</span>
      <span style={{fontSize:11,color:"#999",fontWeight:600}}>{"#"+r.number}</span>
    </div>;})}
  </div>;}
  return <Row gap={12}>
    <div style={{flex:2,marginBottom:12,position:"relative"}} ref={ref}>
      <Lbl required={true}>Room Name</Lbl>
      <input value={p.nameVal} onChange={function(e){p.onName(e.target.value);setOpen(true);var m=rooms.find(function(r){return r.name.toLowerCase()===e.target.value.toLowerCase();});if(m)p.onNumber(m.number);}} onFocus={function(){setOpen(true);}} placeholder="e.g. Primary Suite Bedroom" style={IS} />
      {dropdown}
    </div>
    <div style={{flex:1,marginBottom:12}}>
      <Lbl required={true}>Room #</Lbl>
      <input value={p.numberVal} onChange={function(e){p.onNumber(e.target.value);}} placeholder="101" style={IS} />
    </div>
  </Row>;
}


// ── VISION SCANNER ──────────────────────────────────────────────────────────
// Accepts screenshot paste, drag-drop, or file upload. Uses Claude vision API.
function SpecScanner(p){
  var schedule=p.schedule;var onApply=p.onApply;var itemId=p.itemId;var onSaveImage=p.onSaveImage;
  var is=useState(null);var image=is[0];var setImage=is[1]; // {dataUrl, mimeType, name}
  var ls=useState(false);var loading=ls[0];var setLoad=ls[1];
  var rs=useState(null);var result=rs[0];var setResult=rs[1];
  var ds=useState(false);var dragging=ds[0];var setDragging=ds[1];
  var fileRef=useRef(null);
  var zoneRef=useRef(null);

  // Listen for paste anywhere in the document while scanner is visible
  useEffect(function(){
    function onPaste(e){
      var items=e.clipboardData&&e.clipboardData.items;
      if(!items)return;
      for(var i=0;i<items.length;i++){
        if(items[i].type.indexOf('image')>=0){
          var file=items[i].getAsFile();
          if(file)loadImageFile(file);
          break;
        }
      }
    }
    document.addEventListener('paste',onPaste);
    return function(){document.removeEventListener('paste',onPaste);};
  },[]);

  function loadImageFile(file){
    if(!file)return;
    var isPDF=file.type==='application/pdf'||file.name.toLowerCase().endsWith('.pdf');
    var isImage=file.type.startsWith('image/');
    if(!isImage&&!isPDF)return;
    var reader=new FileReader();
    reader.onload=function(e){
      setImage({dataUrl:e.target.result,mimeType:isPDF?'application/pdf':file.type,name:file.name||'screenshot',isPDF:isPDF});
      setResult(null);
    };
    reader.readAsDataURL(file);
  }

  function handleDrop(e){
    e.preventDefault();setDragging(false);
    var file=e.dataTransfer.files[0];
    if(file)loadImageFile(file);
  }

  function handleFileInput(e){
    var file=e.target.files[0];
    if(file)loadImageFile(file);
  }

  async function scanImage(){
    if(!image||!schedule)return;
    setLoad(true);setResult(null);
    var fields=SCHEDULE_FIELDS[schedule]||[];
    var fieldList=fields.filter(function(f){return f.key!=='sourceUrl';}).map(function(f){return f.key+' ('+f.label+')';}).join(', ');
    var b64=image.dataUrl.split(',')[1];
    var promptText='You are a product specification extractor for Cimon Starr Interior Design, a high-end residential design firm.\n\nLook at this carefully — it may be a screenshot of a vendor product page, a spec sheet, a quote, or a PDF with product details.\n\nExtract all visible product information and return JSON only, no markdown.\n\nFields to look for: '+fieldList+'\n\nAlso extract:\n- itemName: the product name\n- vendor: the brand or manufacturer name\n- netPrice: any price shown (net/trade price preferred)\n- leadTime: any lead time or delivery estimate\n\nFor Finish Schedule items, determine if it is Paint, Wallpaper, Wall Covering, Tile, Stone, Flooring, or Other and set the "type" field.\n\nReturn format:\n{"fields":{"key":"value"},"itemName":"","vendor":"","netPrice":"","leadTime":"","flags":[{"key":"","issue":""}],"notFound":[]}\n\nLeave fields empty string if not visible. Do not guess. For dimensions always use W x D x H format.';

    // Build content block — PDFs use document type, images use image type
    var contentBlock=image.isPDF
      ?{type:'document',source:{type:'base64',media_type:'application/pdf',data:b64}}
      :{type:'image',source:{type:'base64',media_type:image.mimeType,data:b64}};

    try{
      var res=await fetch('/api/claude',{
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({
          user: typeof contentBlock==='string' ? contentBlock+' '+promptText : promptText,
          max_tokens:2000
        })
      });
      var d=await res.json();
      var txt={text:d.text||''};
      var parsed=JSON.parse((txt.text||'').replace(/```json|```/g,'').trim());
      setResult(parsed);
    }catch(e){
      setResult({fields:{},itemName:'',vendor:'',netPrice:'',leadTime:'',flags:[],notFound:[],parseError:'Could not read file. For PDFs try the first page only. For images try a clearer screenshot.'});
    }
    setLoad(false);
  }

  function applyResult(){
    if(!result)return;
    // Apply specs to form
    onApply(result.fields||{},result.itemName||'',result.vendor||'',result.netPrice||'',result.leadTime||'');
    // Also save the scanned image to the item gallery if a callback is provided
    if(onSaveImage&&image){
      onSaveImage(image);
    }
    setResult(null);setImage(null);
  }

  if(!schedule)return <div style={{background:'#FAFAFA',border:'1.5px dashed #D0D0CC',borderRadius:8,padding:14,marginBottom:12,fontSize:12,color:'#999',textAlign:'center'}}>Select a schedule above to enable scanning</div>;

  var dropZone=<div
    ref={zoneRef}
    onDragOver={function(e){e.preventDefault();setDragging(true);}}
    onDragLeave={function(){setDragging(false);}}
    onDrop={handleDrop}
    onClick={function(){if(!image)fileRef.current.click();}}
    style={{border:'1.5px dashed '+(dragging?'#999':'#D0D0CC'),borderRadius:10,padding:image?'8px':'28px 16px',
      textAlign:'center',cursor:image?'default':'pointer',
      background:'#fafafa',transition:'all .15s',marginBottom:10}}
  >
    {image?<div style={{display:'flex',alignItems:'center',gap:12}}>
      {image.isPDF
        ?<div style={{width:80,height:60,background:'#ffebee',borderRadius:6,border:'1px solid #ffcdd2',display:'flex',alignItems:'center',justifyContent:'center',fontSize:28}}>📄</div>
        :<img src={image.dataUrl} alt="preview" style={{width:80,height:60,objectFit:'cover',borderRadius:6,border:'1px solid #ddd'}} />}
      <div style={{textAlign:'left',flex:1}}>
        <div style={{fontWeight:600,fontSize:12,color:'#2e7d32',marginBottom:2}}>✓ Image ready</div>
        <div style={{fontSize:11,color:'#888',marginBottom:6}}>{image.name}</div>
        <button onClick={function(e){e.stopPropagation();setImage(null);setResult(null);}}
          style={{fontSize:11,color:'#888',background:'none',border:'none',cursor:'pointer',padding:0,textDecoration:'underline'}}>
          Remove
        </button>
      </div>
    </div>:<div>
      <div style={{fontSize:28,marginBottom:8}}>📸</div>
      <div style={{fontWeight:600,fontSize:13,color:'#333',marginBottom:4}}>Screenshot, photo, or spec sheet</div>
      <div style={{fontSize:11,color:'#666',lineHeight:1.6}}>
        <span style={{background:'#1a1a1a',color:'#fff',padding:'1px 6px',borderRadius:3,fontSize:10,fontWeight:700,marginRight:4}}>⌘V</span>
        {'Paste screenshot  ·  Drop file  ·  Click to browse'}
        <div style={{fontSize:10,color:'#aaa',marginTop:4}}>JPG, PNG, screenshots or PDF spec sheets</div>
      </div>
    </div>}
    <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={handleFileInput} style={{display:'none'}} />
  </div>;

  var scanBtn=image&&!loading?<Btn onClick={scanImage}>Scan for Specs →</Btn>:null;
  var loadingEl=loading?<div style={{padding:'16px',textAlign:'center',color:'#888',fontSize:13}}>Reading image…</div>:null;

  var resultPanel=null;
  if(result){
    var foundKeys=Object.keys(result.fields||{}).filter(function(k){return result.fields[k];});
    var extras=[];
    if(result.itemName)extras.push({l:'Product Name',v:result.itemName,highlight:true});
    if(result.vendor)extras.push({l:'Vendor / Brand',v:result.vendor,highlight:true});
    if(result.netPrice)extras.push({l:'Price',v:result.netPrice,highlight:true});
    if(result.leadTime)extras.push({l:'Lead Time',v:result.leadTime,highlight:true});

    var fieldRows=extras.concat(foundKeys.map(function(k){
      var fDef=(SCHEDULE_FIELDS[schedule]||[]).find(function(f){return f.key===k;});
      return{l:fDef?fDef.label:k,v:result.fields[k]};
    })).map(function(row,i){
      return <div key={i} style={{display:'flex',gap:8,padding:'5px 0',borderBottom:'1px solid #f0f0f0'}}>
        <div style={{fontSize:11,fontWeight:600,color:row.highlight?'#0277bd':'#555',width:130,flexShrink:0}}>{row.l}</div>
        <div style={{flex:1,fontSize:12,color:'#222'}}>{row.v}</div>
      </div>;
    });

    resultPanel=<div style={{background:'#f0f8ff',border:'1px solid #90caf9',borderRadius:8,padding:12,marginTop:8}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
        <div style={{fontSize:11,fontWeight:700,color:'#0277bd',textTransform:'uppercase',letterSpacing:.5}}>Scan Results — Review Before Applying</div>
        <button onClick={function(){setResult(null);}} style={{background:'none',border:'none',color:'#999',cursor:'pointer',fontSize:14}}>✕</button>
      </div>
      {result.parseError?<div style={{fontSize:12,color:'#c62828',marginBottom:8}}>⚠ {result.parseError}</div>:null}
      {foundKeys.length>0||extras.length>0?<div style={{marginBottom:10}}>{fieldRows}</div>:<div style={{fontSize:12,color:'#999',marginBottom:8}}>No fields could be extracted — try a clearer screenshot.</div>}
      {(result.flags||[]).length>0?<div style={{fontSize:11,color:'#e65100',marginBottom:8}}>{(result.flags||[]).map(function(f){return f.issue;}).join(' · ')}</div>:null}
      <div style={{display:'flex',gap:8}}>
        {(foundKeys.length>0||result.itemName)&&<Btn onClick={applyResult}>Apply to Form</Btn>}
        <Btn variant="secondary" onClick={function(){setResult(null);}}>Discard</Btn>
      </div>
    </div>;
  }

  return <div style={{background:'#fafafa',border:'1px solid #e0e0e0',borderRadius:8,padding:14,marginBottom:12}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
      <SHead>Scan Specs from Image</SHead>
      <div style={{fontSize:11,color:'#888'}}>{schedule}</div>
    </div>
    {dropZone}
    {scanBtn}
    {loadingEl}
    {resultPanel}
  </div>;
}

// VendorField (inline typeahead) removed 2026-05-13. The shared
// VendorPicker component now handles every vendor input across the app.


// Phase 5 (2026-06-27) — FeeInp replaced with CurrencyInput.
//
// Pre-fix, FeeInp was a bare <input type="text"> that stored whatever
// string the user typed: "10,000" (with a comma) or "875." (trailing
// dot) persisted verbatim into items.fees jsonb, breaking downstream
// numeric casts. Empty fields persisted as empty strings "" which
// failed ::numeric SQL coercion. Migration 20260601 cleaned up 510
// existing malformed rows but the input never normalized on entry —
// new bad data could land at any save.
//
// CurrencyInput (src/components/ui.jsx) handles all three:
//   • format-on-blur: strips $/commas/spaces, parses, rounds to cents
//   • onValueChange returns a NUMBER (not a string)
//   • disabled prop replaces the old readOnly affordance
//
// Wrapper preserves the FeeInp visual shape (width 120, right-aligned,
// $ prefix inside the formatted value) so the form layout doesn't
// shift. The salesTax read-only state maps to disabled={true}.
var _feeInpStyle=null;
var _feeInpReadOnlyStyle=null;
function FeeInp(fp){
  if(!_feeInpStyle)_feeInpStyle=Object.assign({},IS,{width:120,textAlign:"right"});
  if(!_feeInpReadOnlyStyle)_feeInpReadOnlyStyle=Object.assign({},_feeInpStyle,{background:"#FAFAF8",color:"#444",cursor:"default"});
  var style = fp.readOnly ? _feeInpReadOnlyStyle : _feeInpStyle;
  // Coerce stored value (string-or-number-or-empty) to a number for
  // CurrencyInput. Empty strings + non-numeric garbage → 0.
  var numericValue = (function() {
    if (fp.value === '' || fp.value == null) return 0;
    if (typeof fp.value === 'number') return fp.value;
    var cleaned = String(fp.value).replace(/[$,\s]/g, '');
    var n = parseFloat(cleaned);
    return isNaN(n) ? 0 : n;
  })();
  return (
    <CurrencyInput
      value={numericValue}
      onValueChange={fp.onValueChange}
      disabled={!!fp.readOnly}
      style={style}
    />
  );
}

// ── Sales tax block (2026-10-15, design handoff design_handoff_sales_tax_filing
// screen 05). READ-ONLY on both edit and lock modes. "Charged to client"
// reads the FIN tax snapshot when the item is on a committed final, else
// computeItemTax live. "Remitted" reads the sales_tax_filing_items row the
// Finance → Sales Tax filing wrote — a stored fact, never recomputed (Cimon,
// 2026-10-15: payments are hard facts).
function SalesTaxBlock(p){
  var ctx=useAppContext();
  var item=p.item||{};
  var project=p.project||null;
  var items=ctx.items||[];
  var invoices=ctx.invoices||[];
  var snap=(ctx.finalInvoiceItems||[]).find(function(r){return r.itemId===item.id&&r.finalInvoiceId===item.finalInvoiceId;})||null;
  var tax=snap?snap.tax:computeItemTax(item,project,items);
  var rate=snap?snap.taxRate:(project?parseFloat(project.taxRate)||0:0);
  var base=snap?snap.taxable:(rate>0?tax/rate:0);
  var status=salesTaxItemStatus(item,{invoices:invoices,paymentAllocations:ctx.paymentAllocations||[],clientPayments:ctx.clientPayments||[],salesTaxFilingItems:ctx.salesTaxFilingItems||[],salesTaxFilings:ctx.salesTaxFilings||[],salesTaxFilingFiles:ctx.salesTaxFilingFiles||[],stateTaxRecords:ctx.stateTaxRecords||[],projects:ctx.projects||[]});
  // 2026-10-16 delivery basis (handoff v2 screen 05): the 'Delivered to
  // {ST}' row shows only when the project's state buckets by delivery.
  var stateRule=project?salesTaxStateRuleFor(ctx.stateTaxRecords||[],project.state):null;
  var deliveryBasis=!!(stateRule&&stateRule.taxBasis==='delivery');
  var bucket=deliveryBasis?salesTaxBucketDate(stateRule,item,{paidDate:status.paidDate||''},project):null;
  var deliveredDefault=bucket&&bucket.basis!=='delivery'&&bucket.basis!=='fallback'?bucket.date:'';
  var fin=status.fin||(item.finalInvoiceId?invoices.find(function(inv){return inv.id===item.finalInvoiceId;}):null);
  var place=project?[project.state,salesTaxJurisdiction(project).split(',')[0]].filter(Boolean).join(', '):'';
  var pct=rate>0?String(Number((rate*100).toFixed(3)))+'%':'';
  var rem=status.state==='remitted'?status.remittance:null;
  // 2026-10-16: one Remitted line per RETURN (Colorado + Town of Vail).
  var rems=salesTaxRemittancesForItem(item.id,{salesTaxFilingItems:ctx.salesTaxFilingItems||[],salesTaxFilings:ctx.salesTaxFilings||[],salesTaxFilingFiles:ctx.salesTaxFilingFiles||[]});
  var multiReturn=rems.length>1||(rems.length===1&&!!rems[0].filing.returnKey);
  async function openFile(f){if(!ctx.salesTaxFilingFileUrl)return;var url=await ctx.salesTaxFilingFileUrl(f.storagePath);if(url)window.open(url,'_blank','noopener');}
  var lab={color:'#888880'};
  return <div style={{marginBottom:12}} data-testid="item-sales-tax-block">
    <SHead>Sales tax</SHead>
    <div style={{display:'grid',gridTemplateColumns:'120px 1fr',gap:'6px 12px',fontSize:12}}>
      <span style={lab}>Charged to client</span>
      <span style={{fontFeatureSettings:'"tnum","lnum"'}}>{fmtMoney(tax)}{(pct||place)&&<span style={lab}> · {[pct?pct+' on '+fmtMoney(base):'',place].filter(Boolean).join(' · ')}</span>}</span>
      <span style={lab}>Billed on</span>
      <span>{fin?<span>{fin.invoiceRef||fin.invoice_ref}{status.paidDate&&<span style={lab}> · client {status.paidBasis==='closed'?'closed':'paid'} {salesTaxShortDate(status.paidDate,true)}</span>}</span>:<span style={lab}>not yet on a final invoice</span>}</span>
      {deliveryBasis&&<span style={lab}>Delivered to {project.state}</span>}
      {deliveryBasis&&<span style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}} data-testid="item-sales-tax-delivered">
        <input type="date" value={item.deliveredToStateOn||''} placeholder={deliveredDefault||''} onChange={function(e){if(p.onDeliveredChange)p.onDeliveredChange(e.target.value);}}
          style={{padding:'4px 8px',fontSize:12,fontFamily:'inherit',border:'0.5px solid #D8D7D2',borderRadius:4,background:'#fff',color:'#111',outline:'none'}} data-field="deliveredToStateOn" />
        <span style={lab}>
          {item.deliveredToStateOn?'':(deliveredDefault?'default '+salesTaxShortDate(deliveredDefault,true)+' ('+(bucket.basis==='install'?'install date':'planned install')+') · ':'no date yet · ')}
          sets the {project.state} period (delivery basis)
        </span>
      </span>}
      <span style={lab}>Remitted</span>
      <span style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}} data-testid="item-sales-tax-remitted">
        {rem?<span style={{display:'grid',gap:4}}>{rems.map(function(r){return <span key={r.filingItem.id||r.filing.id} style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
          <span style={{width:8,height:8,borderRadius:'50%',background:'#1A5C38',display:'inline-block'}} />
          {multiReturn&&<span style={lab}>{salesTaxReturnLabel(r.filing,ctx.salesTaxCodes||[])} ·</span>}
          <span>{[salesTaxShortDate(r.filing.paidDate,true),r.filing.method,r.filing.confirmationNo?'conf. '+r.filing.confirmationNo:''].filter(Boolean).join(' · ')}</span>
          {multiReturn&&<span style={lab}>· {fmtMoney(r.filingItem.taxAmount)}</span>}
          {r.files.map(function(f,i){return <span key={f.id} onClick={function(){openFile(f);}} style={{color:'#111111',textDecoration:'underline',textUnderlineOffset:3,cursor:'pointer'}}>↓ {r.files.length>1?'PDF '+(i+1):'Return PDF'}</span>;})}
        </span>;})}</span>
        :status.state==='collected'?<span style={{color:'#D4007A'}}>not yet remitted</span>
        :<span style={lab}>not yet billed</span>}
      </span>
    </div>
    <div style={{fontSize:10,color:'#888880',marginTop:10,lineHeight:1.5}}>Read-only — set by the filing on Finance → Sales Tax.{deliveryBasis?' Delivered to '+project.state+' is the one editable date here.':''}</div>
  </div>;
}

function FeesPanel(p){
  // Phase 5 (2026-06-27) — defensive default uses "0" instead of empty
  // strings. Pre-fix, items saved without ever touching the fees panel
  // would persist freight="" / crating="" / etc., breaking downstream
  // ::numeric SQL casts. Migration 20260601 cleaned up 510 such rows;
  // this default + the CurrencyInput swap prevent regression at the
  // source. BLANK item template already uses "0" everywhere.
  var fees=p.fees||{freight:"0",crating:"0",installation:"0",salesTax:"0",tariff:"0",custom:[]};
  function set(k){return function(v){p.onChange(Object.assign({},fees,{[k]:v}));};}
  function addCustom(){p.onChange(Object.assign({},fees,{custom:(fees.custom||[]).concat([{label:"",amount:""}])}));}
  function updateCustom(i,field,v){var c=(fees.custom||[]).map(function(x,j){return j===i?Object.assign({},x,{[field]:v}):x;});p.onChange(Object.assign({},fees,{custom:c}));}
  function removeCustom(i){p.onChange(Object.assign({},fees,{custom:(fees.custom||[]).filter(function(_,j){return j!==i;})}));}

  // Sales-tax — read-only display since Fix C Phase 2 (2026-05-27).
  //
  // Tax is no longer entered or stored per-item. It derives at document-
  // generation time from project-level config (taxRate + category
  // booleans on the project record) via computeItemTax (utils.js). The
  // display here calls the same helper that every payload builder calls,
  // so what you see in the form is what lands on the proposal /
  // deposit / final invoice / billing summary PDF.
  //
  // Falls back to the legacy stored fees.salesTax value (rendered as-is)
  // when the project lacks tax config — same fallback every payload
  // builder uses. Phase 5 removes the fallback path along with the rest
  // of the legacy tax code.
  var projectForTax = p.project || null;
  var allItemsForTax = p.allItems || [];
  var derivedSalesTax = computeItemTax(
    { netPrice: p.itemNetPrice, markupPct: p.itemMarkupPct, qty: p.itemQty, fees: fees, assemblyId: p.assemblyId },
    projectForTax,
    allItemsForTax
  );

  // Phase 5 follow-up (2026-06-27): values can be NUMBERS now (since
  // FeeInp → CurrencyInput swap). Wrap with String() before .replace()
  // — pre-fix this threw "(v||'').replace is not a function" when a
  // bookkeeper edited a shipping cost on a freshly created item whose
  // fees were stored as numbers, not strings.
  var feeTotal=[fees.freight,fees.crating,fees.installation,fees.salesTax,fees.tariff].concat((fees.custom||[]).map(function(c){return c.amount;}))
    .reduce(function(s,v){var n=parseFloat(String(v==null?"":v).replace(/[$,]/g,""));return s+(isNaN(n)?0:n);},0);
  function fpN(s){var n=parseFloat(String(s==null?"":s).replace(/[$,]/g,""));return isNaN(n)?0:n;}
  // Billed total + rate come from itemFeeBreakdown with the item's own fee
  // markup (2026-10-06) — the same arithmetic every invoice builder uses.
  var feeRate=itemFeeMarkupPct(p.feeMarkupPct);
  var feeBilledTotal=itemFeeBreakdown({fees:fees,feeMarkupPct:feeRate}).totals.client+fpN(fees.salesTax);
  var feeRateLocked=!!p.feeRateLocked;

  return <div style={{marginBottom:12}}>
    <SHead>Fees (per item)</SHead>
    <div style={{fontSize:11,color:"#888",marginBottom:10}}>
      Enter your net cost. Freight, crating, installation, and custom fees are billed at +{feeRate}%. Sales tax and tariff are pass-through at cost. Installation is its own line on invoices so state-specific tax rules apply (CA non-taxable, NY taxable, etc.).
    </div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
      <div>
        <Lbl>Fee markup %</Lbl>
        {feeRateLocked
          ?<ReadValue dataField="feeMarkupPct">{String(feeRate)}</ReadValue>
          :<input data-field="feeMarkupPct" value={p.feeMarkupPct!=null?p.feeMarkupPct:""} onChange={function(e){p.onFeeMarkupPctChange&&p.onFeeMarkupPctChange(e.target.value);}} onFocus={function(e){e.target.select();}} placeholder={String(feeRate)} style={Object.assign({},IS,{width:80})} />}
      </div>
      <div style={{fontSize:11,color:"#888",alignSelf:"end",paddingBottom:10}}>{feeRateLocked?"Locked — final invoice committed.":"Freight, crating, installation and custom fees without their own %. Locks when the final invoice is committed."}</div>
      <div>
        <Lbl>Freight / Shipping</Lbl>
        <FeeInp value={fees.freight} onValueChange={function(n){set("freight")(n);}} />
      </div>
      <div>
        <Lbl>Crating</Lbl>
        <FeeInp value={fees.crating} onValueChange={function(n){set("crating")(n);}} />
      </div>
      <div>
        <Lbl>Installation / Labor</Lbl>
        <FeeInp value={fees.installation} onValueChange={function(n){set("installation")(n);}} />
      </div>
      <div>
        <Lbl>Sales Tax</Lbl>
        <FeeInp
          value={derivedSalesTax > 0 ? derivedSalesTax : (fees.salesTax || 0)}
          onValueChange={function(){ /* Phase 2: tax is derived from project config — see subtext */ }}
          readOnly={true}
        />
        <div style={{fontSize:10,color:"#888",marginTop:4}}>
          Computed from project tax config at document time. Edit per-project rules in
          Project Settings.
        </div>
      </div>
      <div>
        <Lbl>Tariff / Import Duty</Lbl>
        <FeeInp value={fees.tariff} onValueChange={function(n){set("tariff")(n);}} />
      </div>
    </div>
    {(fees.custom||[]).map(function(c,i){
      return <Row key={i}>
        <div style={{flex:2}}>
          <Lbl>Custom Fee Label</Lbl>
          <input type="text" value={c.label||""} onChange={function(e){updateCustom(i,"label",e.target.value);}} placeholder="e.g. White glove delivery" style={IS} />
        </div>
        <div style={{flex:1}}>
          <Lbl>Amount</Lbl>
          <FeeInp value={c.amount} onValueChange={function(n){updateCustom(i,"amount",n);}} />
        </div>
        <div style={{flex:"0 0 72px"}}>
          <Lbl>Markup %</Lbl>
          <input type="number" min="0" max="100" step="1"
            value={c.markupPct==null?feeRate:c.markupPct}
            onChange={function(e){updateCustom(i,"markupPct",e.target.value===""?null:parseFloat(e.target.value));}}
            style={IS} />
        </div>
        <div style={{flex:"0 0 auto",paddingTop:20}}>
          <button onClick={function(){removeCustom(i);}} style={{background:"none",border:"none",color:"#aaa",cursor:"pointer",fontSize:13,padding:"7px 6px"}}>x</button>
        </div>
      </Row>;
    })}
    <div style={{marginTop:8}}>
      <Btn variant="secondary" small={true} onClick={addCustom}>+ Add Custom Fee</Btn>
      {feeTotal>0?<div style={{marginTop:10,background:"#fff",border:"1px solid #D0D0CC",borderRadius:6,padding:"8px 12px",fontSize:12}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
          <span style={{color:"#999",fontSize:11,textTransform:"uppercase",letterSpacing:"0.07em"}}>Net fees (your cost)</span>
          <span style={{fontWeight:500,color:"#111"}}>{"$"+feeTotal.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
        </div>
        <div style={{display:"flex",justifyContent:"space-between"}}>
          <span style={{color:"#555",fontSize:11}}>{"Billed to client (freight/crating/other +"+feeRate+"%)"}</span>
          <span style={{fontWeight:600,color:"#111"}}>{"$"+feeBilledTotal.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
        </div>
      </div>:null}
    </div>
  </div>;
}


// fmtInput — imported from ../utils

// TearSheet extracted to ./TearSheet.jsx


function ItemHistory(p){
  var itemId=p.itemId;
  var rs=React.useState([]);var rows=rs[0];var setRows=rs[1];
  var ls=React.useState(true);var loading=ls[0];var setLoading=ls[1];
  var es=React.useState(null);var queryError=es[0];var setQueryError=es[1];

  React.useEffect(function(){
    if(!itemId)return;
    supabase.from('change_history')
      .select('*')
      .eq('item_id',itemId)
      .order('changed_at',{ascending:false})
      .limit(50)
      .then(function(r){
        if(r.error){setQueryError(r.error.message||'Unknown error');}
        else{setRows(r.data||[]);}
        setLoading(false);
      });
  },[itemId]);

  if(!itemId)return <div style={{padding:16,color:"#aaa",fontSize:13}}>Save item first to see history</div>;
  if(loading)return <div style={{padding:16,color:"#aaa",fontSize:13}}>Loading...</div>;
  if(queryError)return <div style={{padding:16,color:"#c62828",fontSize:13}}>History query error: {queryError}</div>;
  if(rows.length===0)return <div style={{padding:16,color:"#aaa",fontSize:13}}>No changes recorded yet. History is tracked from this point forward.</div>;

  return <div style={{padding:"0 4px"}}>
    <div style={{fontSize:11,color:"#888",marginBottom:12}}>{rows.length+" changes recorded"}</div>
    {rows.map(function(row,i){
      var d=new Date(row.changed_at);
      var label=d.toLocaleDateString()+" "+d.toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"});
      var fieldLabel=row.field_changed.replace(/([A-Z])/g," $1").replace(/^./,function(s){return s.toUpperCase();});
      return <div key={i} style={{borderBottom:"1px solid #f5f5f5",padding:"8px 0",fontSize:12}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
          <span style={{fontWeight:600,color:"#333"}}>{fieldLabel}</span>
          <span style={{color:"#aaa",fontSize:11}}>{label+(row.changed_by?" · "+row.changed_by:"")}</span>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
          <div style={{flex:1,background:"#ffebee",padding:"3px 6px",borderRadius:4,color:"#c62828",fontSize:11,wordBreak:"break-word"}}>
            {row.old_value||<em>empty</em>}
          </div>
          <div style={{color:"#aaa",flexShrink:0}}>→</div>
          <div style={{flex:1,background:"#e8f5e9",padding:"3px 6px",borderRadius:4,color:"#2e7d32",fontSize:11,wordBreak:"break-word"}}>
            {row.new_value||<em>empty</em>}
          </div>
        </div>
      </div>;
    })}
  </div>;
}

// ── BULB PICKER ─────────────────────────────────────────────────────────────
function BulbPicker(p){
  var onAdd=p.onAdd; var onClose=p.onClose;
  var ss=useState('All'); var shapeFilter=ss[0]; var setShapeFilter=ss[1];
  var bs=useState('All'); var baseFilter=bs[0]; var setBaseFilter=bs[1];
  var ks=useState('All'); var kelvinFilter=ks[0]; var setKelvinFilter=ks[1];
  var vs=useState('Emery Allen'); var vendorFilter=vs[0]; var setVendorFilter=vs[1];
  var qs=useState('1'); var qty=qs[0]; var setQty=qs[1];

  var shapes=['All'].concat([...new Set(EMERY_ALLEN_BULBS.map(function(b){return b.shape;}))].sort());
  var bases=['All'].concat([...new Set(EMERY_ALLEN_BULBS.map(function(b){return b.base;}))].sort());
  var kelvins=['All'].concat([...new Set(EMERY_ALLEN_BULBS.map(function(b){return String(b.kelvin);}))].sort());

  var filtered=EMERY_ALLEN_BULBS.filter(function(b){
    return(shapeFilter==='All'||b.shape===shapeFilter)&&
           (baseFilter==='All'||b.base===baseFilter)&&
           (kelvinFilter==='All'||String(b.kelvin)===kelvinFilter);
  });

  var sel=Object.assign({},IS,{padding:'5px 8px',background:'#fff'});

  return <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:400,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={onClose}>
    <div style={{background:'#fff',borderRadius:12,padding:24,width:700,maxWidth:'95vw',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 8px 32px rgba(0,0,0,.2)'}} onClick={function(e){e.stopPropagation();}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <div style={{fontWeight:700,fontSize:16}}>Add Bulb</div>
        <button onClick={onClose} style={{background:'none',border:'none',fontSize:18,cursor:'pointer',color:'#999'}}>x</button>
      </div>
      <div style={{display:'flex',gap:8,marginBottom:14}}>
        {BULB_VENDORS.map(function(v){
          return <button key={v} onClick={function(){setVendorFilter(v);}}
            style={{padding:'6px 16px',borderRadius:20,border:'1px solid #ddd',fontSize:12,fontWeight:600,cursor:'pointer',
              background:vendorFilter===v?'#1a1a1a':'#fff',color:vendorFilter===v?'#fff':'#555'}}>{v}</button>;
        }).concat([<div key="note" style={{marginLeft:'auto',fontSize:11,color:'#aaa',alignSelf:'center'}}>
          {vendorFilter==='Tala'?'Tala catalog coming soon':'Emery Allen 2026 catalog'}
        </div>])}
      </div>
      {vendorFilter==='Tala'
        ?<div style={{background:'#f5f5f5',borderRadius:8,padding:24,textAlign:'center',color:'#888',fontSize:13}}>
            Tala catalog not yet loaded. <br />
            You can add bulbs manually using the fields below.
            <br /><br />
            <Btn onClick={function(){onAdd({id:Date.now(),childType:'Bulb',vendor:'Tala',item:'',bulbShape:'',watts:'',lumens:'',kelvin:'',cri:'95',base:'',voltage:'120V',qty:qty,qtyType:'Each',notes:'',status:'Specifying',parentId:null,vendorCustom:'',com:Object.assign({},BLANK_COM),workroomNotes:'',trimSpec:'',cost:'',total:'',imageData:'',imageUrl:'',date:''});onClose();}}>Add blank Tala bulb</Btn>
          </div>
        :<div>
          <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap'}}>
            <div><Lbl>Shape</Lbl><select value={shapeFilter} onChange={function(e){setShapeFilter(e.target.value);}} style={sel}>{shapes.map(function(s){return <option key={s}>{s}</option>;})}</select></div>
            <div><Lbl>Base</Lbl><select value={baseFilter} onChange={function(e){setBaseFilter(e.target.value);}} style={sel}>{bases.map(function(b){return <option key={b}>{b}</option>;})}</select></div>
            <div><Lbl>Kelvin</Lbl><select value={kelvinFilter} onChange={function(e){setKelvinFilter(e.target.value);}} style={sel}>{kelvins.map(function(k){return <option key={k}>{k==='All'?k:k+'K'}</option>;})}</select></div>
            <div><Lbl>Qty per fixture</Lbl><input type="number" min="1" value={qty} onChange={function(e){setQty(e.target.value);}} style={Object.assign({},sel,{width:60})} /></div>
          </div>
          {filtered.length===0
            ?<div style={{padding:20,textAlign:'center',color:'#bbb',fontSize:13}}>No bulbs match filters</div>
            :<div style={{border:'1px solid #e0e0e0',borderRadius:8,overflow:'hidden',maxHeight:380,overflowY:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'#f5f5f5',position:'sticky',top:0}}>
                  {['Shape','SKU','W','Lm','K','CRI','Base','V','Notes',''].map(function(hd){
                    return <th key={hd} style={{padding:'7px 8px',textAlign:'left',fontWeight:600,color:'#555',borderBottom:'1px solid #e0e0e0',whiteSpace:'nowrap'}}>{hd}</th>;})}
                </tr></thead>
                <tbody>{filtered.map(function(b,i){
                  return <tr key={b.sku} style={{borderBottom:'1px solid #f0f0f0',background:i%2===0?'#fff':'#fafafa'}}>
                    <td style={{padding:'6px 8px',fontWeight:600}}>{b.shape}</td>
                    <td style={{padding:'6px 8px',color:'#888',fontSize:10,fontFamily:'monospace'}}>{b.sku}</td>
                    <td style={{padding:'6px 8px'}}>{b.watts}</td>
                    <td style={{padding:'6px 8px'}}>{b.lumens}</td>
                    <td style={{padding:'6px 8px',color:b.kelvin<=2400?'#c0392b':b.kelvin<=2700?'#e65100':'#f39c12',fontWeight:600}}>{b.kelvin+'K'}</td>
                    <td style={{padding:'6px 8px'}}>{b.cri}</td>
                    <td style={{padding:'6px 8px',fontWeight:600}}>{b.base}</td>
                    <td style={{padding:'6px 8px',color:'#888'}}>{b.voltage}</td>
                    <td style={{padding:'6px 8px',color:'#777',fontSize:11}}>{b.notes}</td>
                    <td style={{padding:'6px 8px'}}>
                      <button
                        onClick={function(){
                          onAdd({id:Date.now(),childType:'Bulb',vendor:'Emery Allen',
                            item:b.shape+' '+b.watts+'W '+b.kelvin+'K — '+b.sku,
                            bulbShape:b.shape,watts:String(b.watts),lumens:String(b.lumens),
                            kelvin:String(b.kelvin),cri:String(b.cri),base:b.base,
                            voltage:b.voltage,finish:b.finish,qty:qty,qtyType:'Each',notes:b.notes,
                            sku:b.sku,status:'Specifying',parentId:null,vendorCustom:'',
                            com:Object.assign({},BLANK_COM),workroomNotes:'',trimSpec:'',
                            cost:'',total:'',imageData:'',imageUrl:'',date:''});
                          onClose();
                        }}
                        style={{padding:'4px 12px',background:'#1a1a1a',color:'#fff',border:'none',
                          borderRadius:5,fontSize:11,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}
                      >+ Add</button>
                    </td>
                  </tr>;
                })}</tbody>
              </table>
            </div>}
        </div>}
    </div>
  </div>;
}

// ── COMBINED SCAN + UPLOAD TOOL ────────────────────────────────────────────

function ScanUploadTool(p){
  var itemId=p.itemId; var schedule=p.schedule; var fRef=p.fRef;
  var setF=p.setF; var setSpecs=p.setSpecs; var set=p.set;
  var sb=p.supabase; var saveItemDirect=p.saveItemDirect;

  var ps=useState(null);var pendingFile=ps[0];var setPending=ps[1];
  var pds=useState(null);var pendingDataUrl=pds[0];var setPendingDataUrl=pds[1];
  var pms=useState(null);var pendingMime=pms[0];var setPendingMime=pms[1];
  var ls=useState(false);var loading=ls[0];var setLoading=ls[1];
  var ds=useState(false);var dragging=ds[0];var setDragging=ds[1];
  var es=useState('');var err=es[0];var setErr=es[1];
  var ss=useState('');var status=ss[0];var setStatus=ss[1];
  var fileRef=useRef(null);

  // Paste support
  useEffect(function(){
    function onPaste(e){
      if(!itemId)return;
      var items=e.clipboardData&&e.clipboardData.items;
      if(!items)return;
      for(var i=0;i<items.length;i++){
        if(items[i].kind==='file'){
          var f=items[i].getAsFile();
          if(f)prepareFile(f);
          break;
        }
      }
    }
    window.addEventListener('paste',onPaste);
    return function(){window.removeEventListener('paste',onPaste);};
  },[itemId]);

  function prepareFile(file){
    if(!file)return;
    setErr(''); setStatus('');
    // Read as dataUrl for preview and scan
    var reader=new FileReader();
    reader.onload=function(e){
      setPendingDataUrl(e.target.result);
      setPendingMime(file.type||'image/jpeg');
      // Auto-trigger scan after file is loaded
      setTimeout(function(){ autoScan(file, e.target.result, file.type||'image/jpeg'); }, 0);
    };
    reader.readAsDataURL(file);
    setPending(file);
  }

  function autoScan(file, dataUrl, mimeType) {
    if (!itemId) return;
    setLoading(true);
    var b64 = dataUrl ? dataUrl.split(',')[1] : '';
    var isPDF = mimeType === 'application/pdf';
    var contentBlock = isPDF
      ? {type:'document',source:{type:'base64',media_type:'application/pdf',data:b64}}
      : {type:'image',source:{type:'base64',media_type:mimeType,data:b64}};
    var fields = SCHEDULE_FIELDS[schedule] || [];
    var fieldList = fields.map(function(f){return f.label+' (key: '+f.key+')';}).join(', ');
    var sys = 'You are a spec reader for an interior design procurement app. '
      + 'Extract product specs from the image/PDF. Return ONLY valid JSON, no markdown. '
      + 'Keys must match exactly: ' + fieldList + '. '
      + 'Also include "itemName" (product name), "vendor" (brand/manufacturer), '
      + '"netPrice" (number only, no $), "leadTime" (e.g. "12-14 weeks"). '
      + 'Omit fields you cannot find.';
    setErr('');
    setStatus('Reading specs with AI…');
    fetch('/api/claude', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        system: sys,
        user: [contentBlock],
        max_tokens: 1000,
      }),
    })
    .then(function(res) {
      if (!res.ok) throw new Error('API error ' + res.status);
      return res.json();
    })
    .then(function(d) {
      var result = d.text || '';
      var cleaned = result.replace(/```json|```/g,'').trim();
      var jsonMatch = cleaned.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        setErr('Could not read specs — save file manually below.');
        setLoading(false);
        return;
      }
      try {
        var parsed = JSON.parse(jsonMatch[0]);
        var fCurrent = fRef.current || {};
        if (parsed.itemName && !fCurrent.item) set('item')(parsed.itemName);
        if (parsed.vendor && !fCurrent.vendor) set('vendor')(parsed.vendor);
        if (parsed.netPrice && !fCurrent.netPrice) set('netPrice')(String(parsed.netPrice));
        if (parsed.leadTime && !fCurrent.leadTime) set('leadTime')(parsed.leadTime);
        var specKeys = fields.map(function(f){ return f.key; });
        var newSpecs = {};
        specKeys.forEach(function(k){
          if (parsed[k]) newSpecs[k] = parsed[k];
        });
        setSpecs(Object.assign({}, fCurrent.specs || {}, newSpecs));
        setStatus('Specs filled — save this file?');
      } catch(e) {
        setErr('Could not read specs — save file manually below.');
      }
      setLoading(false);
    })
    .catch(function(e) {
      setErr('Could not read specs — save file manually below.');
      setLoading(false);
    });
  }

  function onDrop(e){
    e.preventDefault(); setDragging(false);
    var files=e.dataTransfer&&e.dataTransfer.files;
    if(files&&files[0])prepareFile(files[0]);
  }

  async function handleType(typeId){
    if(!pendingFile||!itemId)return;
    setLoading(true); setErr(''); setStatus('');

    if(typeId==='scan'){
      // Use SpecScanner logic - direct fetch to /api/claude
      setStatus('Reading specs with AI…');
      var mimeType=pendingMime||pendingFile.type||'image/jpeg';
      var b64=pendingDataUrl?pendingDataUrl.split(',')[1]:'';
      var isPDF=mimeType==='application/pdf';
      var contentBlock=isPDF
        ?{type:'document',source:{type:'base64',media_type:'application/pdf',data:b64}}
        :{type:'image',source:{type:'base64',media_type:mimeType,data:b64}};
      var fields=SCHEDULE_FIELDS[schedule]||[];
      var fieldList=fields.map(function(f){return f.label+' (key: '+f.key+')';}).join(', ');
      var sys='You are a spec reader for an interior design procurement app. Extract product specs from the image/PDF. Return ONLY valid JSON, no markdown. Keys must match exactly: '+fieldList+'. Also include "itemName" (product name), "vendor" (brand/manufacturer), "netPrice" (number only, no $), "leadTime" (e.g. "12-14 weeks"). Omit fields you cannot find.';
      fetch('/api/claude',{
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({system:sys,user:[contentBlock],max_tokens:1000}),
      })
      .then(function(res){if(!res.ok)throw new Error('API error '+res.status);return res.json();})
      .then(function(d){
        var result=d.text||'';
        var cleaned=result.replace(/```json|```/g,'').trim();
        var jsonMatch=cleaned.match(/\{[\s\S]*\}/);
        if(!jsonMatch){setErr('Could not read specs — try a clearer image.');setLoading(false);return;}
        try{
          var parsed=JSON.parse(jsonMatch[0]);
          if(parsed.itemName&&!fRef.current.item)set('item')(parsed.itemName);
          if(parsed.vendor&&!fRef.current.vendor)set('vendor')(parsed.vendor);
          if(parsed.netPrice&&!fRef.current.netPrice)set('netPrice')(String(parsed.netPrice));
          if(parsed.leadTime&&!fRef.current.leadTime)set('leadTime')(parsed.leadTime);
          var specKeys=fields.map(function(f){return f.key;});
          var newSpecs={};
          specKeys.forEach(function(k){if(parsed[k])newSpecs[k]=parsed[k];});
          setSpecs(Object.assign({},fRef.current.specs||{},newSpecs));
          setStatus('\u2713 Specs filled in \u2014 would you like to save this file as a Spec Sheet?');
        }catch(e){
          setErr('Could not read specs \u2014 try a clearer image.');
        }
        setLoading(false);
      })
      .catch(function(e){
        setErr('Could not read specs: '+(e.message||'Try a clearer image'));
        setLoading(false);
      });
      // Don't clear pending yet — let user decide after specs are filled
      return;
      setPending(null); setPendingDataUrl(null); setPendingMime(null);
      return;
    }

    if(typeId==='hero'){
      // Upload to item-images bucket, set as hero
      try {
        setStatus('Uploading image…');
        var imgPath=itemId+'/'+Date.now()+'-'+pendingFile.name.replace(/[^a-zA-Z0-9._-]/g,'_');
        var contentType=pendingFile.type||'image/jpeg';
        var r=await sb.storage.from('item-images').upload(imgPath,pendingFile,{upsert:true,contentType:contentType});
        if(r.error){setErr('Upload failed: '+r.error.message);setLoading(false);return;}
        var pubData=sb.storage.from('item-images').getPublicUrl(imgPath);
        var url=pubData&&pubData.data&&pubData.data.publicUrl?pubData.data.publicUrl:'';
        var newImg={url:url,path:imgPath,label:'Primary',type:'Primary',addedAt:new Date().toISOString()};
        var currentItem=fRef.current||{};
        var updatedImages=[newImg].concat(currentItem.images||[]);
        var safeSpecs=currentItem.specs&&typeof currentItem.specs==='object'&&!Array.isArray(currentItem.specs)?currentItem.specs:{};
        var mergedItem=Object.assign({},currentItem,{images:updatedImages,imageUrl:url,heroIndex:0,specs:safeSpecs});
        if(typeof setF==='function'){setF(mergedItem);}else{console.error('[Hero] setF is not a function:',typeof setF);}
        if(typeof saveItemDirect==='function') saveItemDirect(mergedItem);
        setStatus('✓ Hero photo saved to gallery');
        setLoading(false);
        setPending(null); setPendingDataUrl(null); setPendingMime(null);
      } catch(heroErr) {
        console.error('[Hero upload error]',heroErr);
        setErr('Upload error: '+heroErr.name+': '+heroErr.message);
        setLoading(false);
      }
      return;
    }

    // All other types → item-documents bucket
    var docTypeMap={spec:'Spec Sheet',quote:'Quote',po:'Purchase Order',fin:'Final Invoice',drawing:'Drawing',other:'Other'};
    var docType=docTypeMap[typeId]||'Other';
    setStatus('Uploading document…');
    var docPath='item-docs/'+itemId+'/'+Date.now()+'-'+pendingFile.name.replace(/[^a-zA-Z0-9._-]/g,'_');
    var r2=await sb.storage.from('item-documents').upload(docPath,pendingFile,{upsert:true});
    if(r2.error){setErr('Upload failed: '+r2.error.message);setLoading(false);return;}
    var pubR=sb.storage.from('item-documents').getPublicUrl(docPath);
    var docUrl=(pubR&&pubR.data&&pubR.data.publicUrl)?pubR.data.publicUrl:'';
    var label=pendingFile.name.replace(/\.[^.]+$/,'');
    var r3=await sb.from('item_documents').insert({
      item_id:itemId, name:pendingFile.name, path:docPath, url:docUrl,
      type:docType, size_bytes:pendingFile.size, doc_type:docType, label:label,
      file_name:pendingFile.name, file_path:docPath,
      mime_type:pendingFile.type||'application/octet-stream',
    });
    if(r3.error)setErr('Could not record file: '+r3.error.message);
    else setStatus('✓ '+docType+' saved');
    setLoading(false);
    setPending(null); setPendingDataUrl(null); setPendingMime(null);
  }

  var BG='#999'; var BGLIGHT='#FFFFFF'; var BORDER='#D0D0CC';

  return <div style={{marginBottom:12}}>
    <div
      onDragOver={function(e){e.preventDefault();setDragging(true);}}
      onDragLeave={function(){setDragging(false);}}
      onDrop={onDrop}
      onClick={function(){if(!pendingFile&&!loading&&itemId)fileRef.current&&fileRef.current.click();}}
      style={{
        border:'1.5px dashed '+(dragging?BG:BORDER),
        borderRadius:10,
        padding:16,
        background:dragging?'#F8F8F8':BGLIGHT,
        cursor:itemId?'pointer':'default',
        textAlign:'center',
        transition:'all 0.15s',
        marginBottom:8,
      }}
    >
      {!itemId && (
        <div style={{fontSize:10,color:'#888880',marginBottom:6}}>
          Save item first to enable scanning
        </div>
      )}
      {!pendingFile && !loading && (
        <div>
          <div style={{fontSize:22,marginBottom:6,opacity:0.3}}>↑</div>
          <div style={{fontSize:12,fontWeight:500,color:'#111111',marginBottom:3}}>
            Prism
          </div>
          <div style={{fontSize:10,color:'#888880'}}>
            Specs · photos · quotes · drawings
          </div>
          <div style={{fontSize:10,color:'#D8D7D2',marginTop:3}}>
            Click · Drop · <span style={{fontFamily:'monospace',
              background:'#111111',color:'#FFFFFF',padding:'1px 4px',
              borderRadius:2,fontSize:9}}>⌘V</span> Paste
          </div>
        </div>
      )}
      {loading && (
        <div style={{fontSize:11,color:'#888880',fontStyle:'italic'}}>
          {status || 'Reading…'}
        </div>
      )}
      {!loading && pendingFile && !err && (
        <div style={{fontSize:11,color:'#555552'}}>
          {pendingFile.name}
        </div>
      )}
    </div>
    <input ref={fileRef} type="file" style={{display:'none'}}
      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.heic,.csv,.xls,.xlsx,.txt"
      onChange={function(e){if(e.target.files[0])prepareFile(e.target.files[0]);e.target.value='';}} />

    {err && (
      <div style={{fontSize:11,color:'#C8322A',padding:'5px 8px',
        background:'#FDECEA',borderRadius:5,marginBottom:6}}>
        {err}
      </div>
    )}

    {pendingFile && !loading && (
      <div style={{marginTop:6}}>
        {status && (
          <div style={{fontSize:11,color:'#1A5C38',padding:'5px 8px',
            background:'#EAF3EE',borderRadius:5,marginBottom:6}}>
            {status}
          </div>
        )}
        <div style={{display:'flex',gap:5,flexWrap:'wrap',alignItems:'center'}}>
          <button
            onClick={async function(){
              if(!itemId||!pendingFile)return;
              setLoading(true);
              var path='item-docs/'+itemId+'/'+Date.now()+'-'
                +pendingFile.name.replace(/[^a-zA-Z0-9._-]/g,'_');
              var r=await sb.storage.from('item-documents')
                .upload(path,pendingFile,{upsert:true});
              if(r.error){setErr('Could not save: '+r.error.message);setLoading(false);return;}
              var pubR=sb.storage.from('item-documents').getPublicUrl(path);
              var savedUrl=(pubR&&pubR.data&&pubR.data.publicUrl)?pubR.data.publicUrl:'';
              var label=pendingFile.name.replace(/\.[^.]+$/,'');
              await sb.from('item_documents').insert({
                item_id:itemId,name:pendingFile.name,path:path,url:savedUrl,
                type:'Spec Sheet',size_bytes:pendingFile.size,doc_type:'Spec Sheet',
                label:label,file_name:pendingFile.name,file_path:path,
                mime_type:pendingFile.type||'application/octet-stream',
              });
              setStatus('✓ Saved as Spec Sheet');
              setLoading(false);
              setPending(null);setPendingDataUrl(null);setPendingMime(null);
            }}
            style={{height:26,padding:'0 10px',borderRadius:4,
              background:'#111111',color:'#FFFFFF',border:'none',
              cursor:'pointer',fontSize:10,fontWeight:500,
              fontFamily:'inherit',whiteSpace:'nowrap'}}>
            Save as Spec Sheet
          </button>
          {['Hero photo','Quote','Purchase Order','Final Invoice','Drawing','Other'].map(function(lbl){
            var typeMap={
              'Hero photo':'hero','Quote':'Quote',
              'Purchase Order':'Purchase Order',
              'Final Invoice':'Final Invoice',
              'Drawing':'Drawing','Other':'Other'
            };
            return (
              <button key={lbl}
                onClick={async function(){
                  if(!itemId||!pendingFile)return;
                  if(lbl==='Hero photo'){
                    handleType('hero'); return;
                  }
                  setLoading(true);
                  var docType=typeMap[lbl]||'Other';
                  var path='item-docs/'+itemId+'/'+Date.now()+'-'
                    +pendingFile.name.replace(/[^a-zA-Z0-9._-]/g,'_');
                  var r=await sb.storage.from('item-documents')
                    .upload(path,pendingFile,{upsert:true});
                  if(r.error){setErr('Could not save: '+r.error.message);setLoading(false);return;}
                  var pubR=sb.storage.from('item-documents').getPublicUrl(path);
                  var savedUrl=(pubR&&pubR.data&&pubR.data.publicUrl)?pubR.data.publicUrl:'';
                  var docLbl=pendingFile.name.replace(/\.[^.]+$/,'');
                  await sb.from('item_documents').insert({
                    item_id:itemId,name:pendingFile.name,path:path,url:savedUrl,
                    type:docType,size_bytes:pendingFile.size,doc_type:docType,
                    label:docLbl,file_name:pendingFile.name,file_path:path,
                    mime_type:pendingFile.type||'application/octet-stream',
                  });
                  setStatus('✓ Saved as '+docType);
                  setLoading(false);
                  setPending(null);setPendingDataUrl(null);setPendingMime(null);
                }}
                style={{height:26,padding:'0 9px',borderRadius:4,
                  border:'0.5px solid #D8D7D2',background:'#FFFFFF',
                  cursor:'pointer',fontSize:10,color:'#555552',
                  fontFamily:'inherit',whiteSpace:'nowrap'}}>
                {lbl}
              </button>
            );
          })}
          <button
            onClick={function(){
              setPending(null);setPendingDataUrl(null);setPendingMime(null);
              setStatus('');setErr('');
            }}
            style={{height:26,padding:'0 9px',borderRadius:4,
              border:'0.5px solid #D8D7D2',background:'#FFFFFF',
              cursor:'pointer',fontSize:10,color:'#888880',
              fontFamily:'inherit',marginLeft:'auto'}}>
            Cancel
          </button>
        </div>
      </div>
    )}
  </div>;
}


// ── DIMENSIONS FIELD WITH UNIT CONVERTER ──────────────────────────────────────

// convertDimensions — imported from ../utils


function DimensionsField(p) {
  // Lock mode: render the dimensions string as plain text. The cm→in
  // converter button is hidden in this mode -- it's an auto-fill that
  // would silently rewrite the locked field.
  if (p.locked) {
    return (
      <div style={{ marginBottom: 12 }}>
        <Lbl>Dimensions</Lbl>
        <ReadValue dataField="dimensions">{p.value}</ReadValue>
      </div>
    );
  }
  var vs = useState(false); var showConverter = vs[0]; var setShowConverter = vs[1];
  var ms = useState(''); var metricInput = ms[0]; var setMetricInput = ms[1];
  var us = useState('cm'); var metricUnit = us[0]; var setMetricUnit = us[1];
  var cs = useState(''); var converted = cs[0]; var setConverted = cs[1];

  // Convert a raw number string using the selected unit
  function applyUnit(raw, unit) {
    // Insert the unit after each number so convertDimensions can process it
    return raw.replace(/([\d.]+)/g, function(n) { return n + unit; });
  }

  function runConvert() {
    var withUnits = applyUnit(metricInput, metricUnit);
    setConverted(convertDimensions(withUnits));
  }

  function applyConverted() {
    p.onChange(converted);
    setShowConverter(false);
    setMetricInput(''); setConverted('');
  }

  return <div style={{marginBottom:12}}>
    <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:4}}>
      <Lbl>Dimensions</Lbl>
      <button
        onClick={function(){ setShowConverter(!showConverter); }}
        title="Metric converter"
        style={{background:'none',border:'1px solid #ddd',borderRadius:4,padding:'1px 7px',fontSize:10,cursor:'pointer',color:showConverter?'#0277bd':'#888',borderColor:showConverter?'#90caf9':'#ddd'}}
      >cm→in</button>
    </div>
    <input
      data-field="dimensions"
      value={p.value || ''}
      onChange={function(e) {
        var val = e.target.value;
        // Auto-convert only if explicit units are present
        if (/[\d.]+\s*(mm|cm|m)/i.test(val)) {
          p.onChange(convertDimensions(val));
        } else {
          p.onChange(val);
        }
      }}
      placeholder='e.g. W 24" × D 18" × H 30"'
      style={Object.assign({}, IS)}
    />
    <div style={{fontSize:10,color:'#bbb',marginTop:3}}>
      L · W · H · SH · D · DIA · Sq. Ft. · Sq. In.
    </div>
    {showConverter && <div style={{background:'#f0f4ff',border:'1px solid #c7d7f8',borderRadius:6,padding:10,marginTop:6}}>
      <div style={{fontSize:11,fontWeight:600,color:'#0277bd',marginBottom:8}}>Metric → Imperial Converter</div>
      <div style={{fontSize:11,color:'#555',marginBottom:6}}>
        {'Select your unit, then paste the dimension numbers (e.g. "61 × 46 × 76" or "610 457 762")'}
      </div>
      <div style={{display:'flex',gap:6,alignItems:'center',marginBottom:6}}>
        <select
          value={metricUnit}
          onChange={function(e){ setMetricUnit(e.target.value); }}
          style={{padding:'5px 8px',border:'1px solid #c7d7f8',borderRadius:5,fontSize:12,background:'#fff',fontWeight:600,color:'#0277bd'}}
        >
          <option value='mm'>mm</option>
          <option value='cm'>cm</option>
          <option value='m'>m</option>
        </select>
        <input
          value={metricInput}
          onChange={function(e){ setMetricInput(e.target.value); }}
          placeholder='e.g. 61 × 46 × 76'
          style={Object.assign({}, IS, {flex:1,fontSize:12})}
        />
        <button
          onClick={runConvert}
          style={{padding:'5px 14px',background:'#0277bd',color:'#fff',border:'none',borderRadius:5,fontSize:11,cursor:'pointer',whiteSpace:'nowrap'}}
        >Convert</button>
      </div>
      {converted && <div style={{background:'#e8f5e9',border:'1px solid #a5d6a7',borderRadius:5,padding:'8px 10px',marginTop:4}}>
        <div style={{fontSize:11,color:'#2e7d32',fontWeight:600,marginBottom:6}}>{'→ ' + converted}</div>
        <button
          onClick={applyConverted}
          style={{padding:'4px 14px',background:'#2e7d32',color:'#fff',border:'none',borderRadius:5,fontSize:11,cursor:'pointer'}}
        >✓ Apply to field</button>
      </div>}
    </div>}
  </div>;
}


// ── ASSEMBLY CREATE MODAL ──────────────────────────────────────────────────────
function AssemblyCreateModal(p){
  // p.onSave(data), p.onClose, p.projects, p.initialSchedule
  // p.editAssembly — if set, modal is in edit mode (pre-filled, calls p.onSave(patch))
  var isEdit=!!p.editAssembly;
  var init=isEdit?{name:p.editAssembly.name||'',schedule:p.editAssembly.schedule||'',project_label:p.editAssembly.project_label||'',room_name:p.editAssembly.room_name||'',room_number:p.editAssembly.room_number||'',tag:p.editAssembly.tag||'',sidemark:p.editAssembly.sidemark||'',notes:p.editAssembly.notes||''}:{name:'',schedule:p.initialSchedule||'',project_label:p.initialProject||'',room_name:'',room_number:'',tag:'',sidemark:'',notes:''};
  var fs=useState(init);
  var f=fs[0];var setF=fs[1];
  var sav=useState(false);var saving=sav[0];var setSaving=sav[1];
  var err=useState('');var errMsg=err[0];var setErr=err[1];
  function s(k){return function(v){setF(function(prev){var n=Object.assign({},prev);n[k]=v;return n;});};};
  async function handleSave(){
    if(!f.name.trim()){setErr('Assembly name is required.');return;}
    if(!f.schedule){setErr('Schedule is required.');return;}
    setSaving(true);setErr('');
    var result=await p.onSave(f);
    setSaving(false);
    if(result)p.onClose();else setErr('Could not save assembly. Try again.');
  }
  var ovl={position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:500,display:'flex',alignItems:'center',justifyContent:'center'};
  var box={background:'#fff',borderRadius:12,padding:28,width:480,maxWidth:'95vw',boxShadow:'0 8px 32px rgba(0,0,0,.2)'};
  return <div style={ovl} onClick={function(e){if(e.target===e.currentTarget)p.onClose();}}>
    <div style={box}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
        <div style={{fontWeight:700,fontSize:16}}>{isEdit?'Edit Assembly':'New Assembly'}</div>
        <button onClick={p.onClose} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'#999',lineHeight:1}}>×</button>
      </div>
      <Inp label="Assembly Name" value={f.name} onChange={s('name')} placeholder="e.g. Master Bedroom Drapery, LR Sofa" required={true} />
      <Sel label="Schedule" value={f.schedule} onChange={s('schedule')} required={true}>
        <option value=''>— Select schedule —</option>
        {SCHEDULE_NAMES.map(function(sn){return <option key={sn} value={sn}>{sn}</option>;})}
      </Sel>
      <Sel label="Project" value={f.project_label} onChange={function(v){setF(function(prev){return Object.assign({},prev,{project_label:v,room_name:'',room_number:''});});}}>
        <option value=''>— Select —</option>
        {activeProjects(p.projects, f.project_label).map(function(proj){return <option key={proj.id} value={proj.label}>{proj.label}</option>;})}
      </Sel>
      {(function(){var sp=(p.projects||[]).find(function(pr){return pr.label===f.project_label;})||null;
        return <RoomAC nameVal={f.room_name} numberVal={f.room_number} onName={s('room_name')} onNumber={s('room_number')} rooms={sp?sp.rooms:[]} />;})()}
      <Row>
        <div style={{flex:1}}><Inp label="Tag" value={f.tag} onChange={s('tag')} placeholder="e.g. L01-201" /></div>
        <div style={{flex:1}}><Inp label="Sidemark" value={f.sidemark} onChange={s('sidemark')} placeholder="CS/..." /></div>
      </Row>
      <Txta label="Notes" value={f.notes} onChange={s('notes')} placeholder="Optional assembly notes" rows={2} />
      {errMsg&&<div style={{fontSize:12,color:'#c62828',marginBottom:8,padding:'5px 8px',background:'#ffebee',borderRadius:5}}>{errMsg}</div>}
      <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16}}>
        <button onClick={p.onClose} style={{padding:'8px 18px',background:'#f5f5f5',border:'1px solid #ddd',borderRadius:6,fontSize:13,cursor:'pointer'}}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={{padding:'8px 18px',background:'#1a1a1a',color:'#fff',border:'none',borderRadius:6,fontSize:13,fontWeight:600,cursor:saving?'not-allowed':'pointer'}}>{saving?'Saving…':isEdit?'Save Changes':'Create Assembly'}</button>
      </div>
    </div>
  </div>;
}

function AssemblyDeleteModal(p){
  // p.assembly, p.memberCount, p.onDeleteAll, p.onDeleteAssemblyOnly, p.onClose
  var ovl={position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:500,display:'flex',alignItems:'center',justifyContent:'center'};
  var box={background:'#fff',borderRadius:12,padding:28,width:460,maxWidth:'95vw',boxShadow:'0 8px 32px rgba(0,0,0,.2)'};
  var n=p.memberCount||0;
  return <div style={ovl}>
    <div style={box}>
      <div style={{fontWeight:700,fontSize:16,marginBottom:8,color:'#c62828'}}>Delete Assembly</div>
      <div style={{fontSize:13,color:'#333',marginBottom:20,lineHeight:1.6}}>
        Delete "<strong>{p.assembly.name}</strong>"?<br />
        {n>0?'This assembly has '+n+' item'+(n!==1?'s':'')+'. Choose what to do with them:':'This assembly has no items.'}
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:20}}>
        {n>0&&<button onClick={p.onDeleteAll} style={{padding:'10px 18px',background:'#c62828',color:'#fff',border:'none',borderRadius:6,fontSize:13,fontWeight:600,cursor:'pointer',textAlign:'left'}}>
          {'Delete Assembly + All '+n+' Item'+(n!==1?'s':'')}
          <div style={{fontSize:11,fontWeight:400,opacity:0.85,marginTop:2}}>Removes the assembly and permanently deletes all component items</div>
        </button>}
        <button onClick={p.onDeleteAssemblyOnly} style={{padding:'10px 18px',background:'#e53935',color:'#fff',border:'none',borderRadius:6,fontSize:13,fontWeight:600,cursor:'pointer',textAlign:'left'}}>
          Delete Assembly Only
          <div style={{fontSize:11,fontWeight:400,opacity:0.85,marginTop:2}}>{n>0?'Keeps the '+n+' item'+(n!==1?'s':'')+' as standalone items':'Removes the assembly record'}</div>
        </button>
      </div>
      <button onClick={p.onClose} style={{padding:'8px 18px',background:'#f5f5f5',border:'1px solid #ddd',borderRadius:6,fontSize:13,cursor:'pointer',width:'100%'}}>Cancel</button>
    </div>
  </div>;
}

// ── ASSEMBLY PICKER ────────────────────────────────────────────────────────────
// Dropdown from real assemblies table; shows "Create new assembly" option
function AssemblyPicker(p){
  // p.value (assemblyId), p.onChange(id, assembly), p.assemblies, p.projects, p.project, p.onCreateNew
  var relevant=(p.assemblies||[]).filter(function(a){return !p.project||!a.project_label||a.project_label===p.project;});
  var selected=relevant.find(function(a){return a.id===p.value;})||null;
  return <div style={{marginBottom:12}}>
    <Lbl>Assembly (optional)</Lbl>
    <div style={{display:'flex',gap:6}}>
      <select value={p.value||''} onChange={function(e){
        var id=e.target.value;
        if(id==='__new__'){p.onCreateNew&&p.onCreateNew();return;}
        var asm=relevant.find(function(a){return a.id===id;})||null;
        p.onChange(id||null,asm);
      }} style={Object.assign({},IS,{flex:1,color:p.value?'#1a1a1a':'#999'})}>
        <option value=''>— None (standalone item) —</option>
        {relevant.map(function(a){return <option key={a.id} value={a.id}>{a.name+(a.room_name?' · '+a.room_name:'')}</option>;})}
        <option value='__new__'>+ Create new assembly…</option>
      </select>
      {p.value&&<button onClick={function(){p.onChange(null,null);}} style={{padding:'6px 10px',border:'1px solid #ddd',borderRadius:6,fontSize:11,cursor:'pointer',background:'#fff',color:'#888',whiteSpace:'nowrap'}}>Clear</button>}
    </div>
    {selected&&<div style={{fontSize:11,color:'#7b1fa2',marginTop:3,fontWeight:500}}>{'⬡ '+selected.name+(selected.room_name?' · '+selected.room_name:'')+(selected.schedule?' · '+selected.schedule:'')}</div>}
  </div>;
}

function TeamNotesPanel({ itemId, projectCode, userEmail, studioTeam }) {
  var [notes,     setNotes]     = useState([]);
  var [draft,     setDraft]     = useState('');
  var [saving,    setSaving]    = useState(false);
  var [editingId, setEditingId] = useState(null);
  var [editBody,  setEditBody]  = useState('');

  function fmtDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
      ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  useEffect(function() {
    if (!itemId) return;
    supabase.from('notes').select('*').eq('entity_type', 'item').eq('entity_id', itemId)
      .then(function(r) { if (!r.error) setNotes(sortNotesDesc(r.data || [])); });
  }, [itemId]);

  function handleSave() {
    if (!draft.trim() || !itemId || saving) return;
    setSaving(true);
    supabase.from('notes').insert(noteInsertRow(draft, projectCode || '', 'item', itemId, userEmail)).select()
      .then(function(r) {
        setSaving(false);
        if (!r.error && r.data && r.data[0]) {
          setNotes(function(prev) { return [r.data[0]].concat(prev); });
          setDraft('');
        }
      });
  }

  function handleUpdate(id) {
    if (!editBody.trim()) return;
    supabase.from('notes').update({ body: editBody.trim(), updated_at: new Date().toISOString() }).eq('id', id).select()
      .then(function(r) {
        if (!r.error && r.data && r.data[0]) {
          setNotes(function(prev) { return prev.map(function(n) { return n.id === id ? r.data[0] : n; }); });
          setEditingId(null); setEditBody('');
        }
      });
  }

  return (
    <div style={{ marginTop: 16 }}>
      <SHead>Team Notes</SHead>
      {!itemId
        ? <div style={{ fontSize: 12, color: '#bbb', marginBottom: 12 }}>Save this item first to add team notes.</div>
        : <>
            <textarea
              value={draft}
              onChange={function(e) { setDraft(e.target.value); }}
              placeholder="Add an internal note visible to all team members…"
              rows={2}
              style={{
                width: '100%', boxSizing: 'border-box', border: '1px solid #ddd',
                borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit',
                resize: 'vertical', outline: 'none', marginBottom: 7,
              }}
            />
            <button
              onClick={handleSave}
              disabled={saving || !draft.trim()}
              style={{
                height: 28, padding: '0 14px',
                background: draft.trim() ? '#111' : '#eee',
                color:      draft.trim() ? '#fff' : '#aaa',
                border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 500,
                cursor: draft.trim() ? 'pointer' : 'default',
                fontFamily: 'inherit', marginBottom: 14,
              }}
            >{saving ? 'Saving\u2026' : 'Add Note'}</button>
            {notes.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {notes.map(function(note) {
                  var isEditing  = editingId === note.id;
                  var authorName = resolveAuthorName(note.author_email, studioTeam);
                  var wasEdited  = note.updated_at && note.updated_at !== note.created_at;
                  return (
                    <div key={note.id} style={{ border: '1px solid #eee', borderRadius: 7, padding: '10px 12px', background: '#fafafa' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>{authorName}</span>
                        <span style={{ fontSize: 10, color: '#bbb' }}>{fmtDate(note.created_at)}{wasEdited ? ' (edited)' : ''}</span>
                      </div>
                      {isEditing
                        ? <div>
                            <textarea value={editBody} onChange={function(e){setEditBody(e.target.value);}} rows={2} autoFocus
                              style={{ width:'100%', boxSizing:'border-box', border:'1px solid #a5b4fc', borderRadius:5, padding:'6px 8px', fontSize:12, fontFamily:'inherit', resize:'vertical', outline:'none', marginBottom:6 }} />
                            <div style={{ display:'flex', gap:6 }}>
                              <button onClick={function(){handleUpdate(note.id);}} style={{ padding:'3px 10px', background:'#111', color:'#fff', border:'none', borderRadius:4, fontSize:11, cursor:'pointer', fontFamily:'inherit' }}>Save</button>
                              <button onClick={function(){setEditingId(null);setEditBody('');}} style={{ padding:'3px 10px', background:'none', color:'#888', border:'1px solid #ddd', borderRadius:4, fontSize:11, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
                            </div>
                          </div>
                        : <div>
                            <div style={{ fontSize:12, color:'#222', lineHeight:1.5, whiteSpace:'pre-wrap' }}>{note.body}</div>
                            <button onClick={function(){setEditingId(note.id);setEditBody(note.body);}} style={{ marginTop:5, fontSize:10, color:'#bbb', background:'none', border:'none', cursor:'pointer', padding:0, fontFamily:'inherit' }}>Edit</button>
                          </div>
                      }
                    </div>
                  );
                })}
              </div>
            )}
          </>
      }
    </div>
  );
}

// Procurement class selector — Phase 2 of the OF/BO feature
// (2026-05-28). Shipped behind the new procurement_class column.
// Standard items get the full pipeline; OF (Owner Furnished) items are
// supplied by the client; BO (By Others) items are spec'd by the firm
// but purchased + installed by a different trade. Both OF and BO are
// financially inert — see isNonProcured / shouldBillItem in utils.js.
//
// Switching class on an existing item: standard → OF/BO is gated by a
// confirmation dialog and BLOCKED outright when the item has an active
// PO, payment, or invoice (financial commitments can't be retroactively
// reclassified as non-procured). OF/BO → standard is allowed freely;
// OF ↔ BO is allowed freely.
//
// Hidden on assembly child items (parentId) per the spec; children
// inherit their parent's class via the shouldBillItem chain.
export function isItemFinanciallyCommitted(item, purchaseOrders, invoices, paymentAllocations) {
  if (!item || !item.id) return false;
  var iid = String(item.id);
  // Active PO referencing this item id in its items[] array.
  var hasPo = (purchaseOrders || []).some(function(po) {
    if (!po) return false;
    var ids = po.items || po.itemIds || [];
    return Array.isArray(ids) && ids.some(function(x) { return String(x) === iid; });
  });
  if (hasPo) return true;
  // Invoice (deposit or final) referencing this item via itemIds, itemId,
  // or the item's own finalInvoiceId / clientDepositInvoiceId pointers.
  if (item.finalInvoiceId || item.clientDepositInvoiceId) return true;
  var hasInv = (invoices || []).some(function(inv) {
    if (!inv) return false;
    if (inv.itemId && String(inv.itemId) === iid) return true;
    var ids = inv.itemIds || [];
    return Array.isArray(ids) && ids.some(function(x) { return String(x) === iid; });
  });
  if (hasInv) return true;
  // Payment allocations against any invoice that touches this item
  // would already surface via the invoice match above, so no separate
  // check needed here.
  return false;
}

function ProcurementClassPicker(p) {
  var value = p.value || 'standard';
  var locked = !!p.locked;

  function handleChange(next) {
    if (next === value) return;
    if (locked) return;
    var goingNonProcured = (next === 'owner_furnished' || next === 'by_others');
    var leavingStandard = value === 'standard';
    if (goingNonProcured && leavingStandard) {
      // Block when item already has a financial commitment — retro-
      // reclassifying as non-procured would orphan POs/invoices that
      // already exist against the item.
      if (isItemFinanciallyCommitted(p.item, p.purchaseOrders, p.invoices, p.paymentAllocations)) {
        window.alert(
          'Cannot change classification: this item has an active PO, invoice, or payment. ' +
          'Owner Furnished and By Others items are excluded from billing — switching now would ' +
          'orphan the existing financial commitments. Cancel the PO / void the invoice first.'
        );
        return;
      }
      var label = next === 'owner_furnished' ? 'Owner Furnished' : 'By Others';
      var msg = 'Change classification to ' + label + '?\n\n' +
        'This will clear the item\u2019s cost, markup, and client-price fields ' +
        'and exclude it from all financial totals, SKU assignment, and ' +
        'push. The vendor + product spec fields stay intact.';
      if (!window.confirm(msg)) return;
    }
    p.onChange(next);
  }

  var noteText = '';
  if (value === 'owner_furnished') {
    noteText = 'Owner-furnished items appear on schedules and proposals but are excluded from billing.';
  } else if (value === 'by_others') {
    noteText = 'By-others items are installed by another trade and excluded from billing.';
  }

  return (
    <div data-testid="procurement-class-picker" data-value={value} style={{ marginBottom: 14 }}>
      <Lbl>Classification</Lbl>
      <div style={{ display: 'flex', gap: 6 }}>
        {[
          { v: 'standard',        label: 'Standard' },
          { v: 'owner_furnished', label: 'Owner Furnished' },
          { v: 'by_others',       label: 'By Others' },
        ].map(function(opt) {
          var active = value === opt.v;
          return (
            <button
              key={opt.v}
              type="button"
              data-testid={'procurement-class-' + opt.v}
              data-active={active ? 'true' : 'false'}
              onClick={function() { handleChange(opt.v); }}
              disabled={locked}
              style={{
                height: 28, padding: '0 12px', borderRadius: 6,
                border: active ? '1.5px solid #111111' : '0.5px solid #D8D7D2',
                background: active ? '#111111' : '#FFFFFF',
                color: active ? '#FFFFFF' : '#555552',
                fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
                cursor: locked ? 'not-allowed' : 'pointer',
                opacity: locked ? 0.6 : 1,
              }}>
              {opt.label}
            </button>
          );
        })}
      </div>
      {noteText && (
        <div data-testid="procurement-class-note" style={{
          marginTop: 8, padding: '8px 12px',
          background: '#FAFAF8', border: '0.5px solid #E0E0DC', borderRadius: 6,
          fontSize: 11, color: '#555552', lineHeight: 1.5,
        }}>
          {noteText}
        </div>
      )}
    </div>
  );
}

function ItemForm(p){var BLANK={id:null,vendor:"",item:"",productName:"",qty:"1",qtyType:"Each",schedule:"",specs:{},project:"",roomName:"",roomNumber:"",tag:"",dimensions:"",manufacturer:"",model:"",finish:"",specNotes:"",internalNotes:"",sourceUrl:"",notes:"",imageData:"",imageUrl:"",status:"Specifying",netPrice:"",total:"",leadTime:"",leadWeeks:null,poNumber:"",installer:"",statusNotes:"",children:[],date:dateStr(),parentId:null,fees:{freight:"0",crating:"0",installation:"0",salesTax:"0",tariff:"0",custom:[]},images:[],heroIndex:0,sentToAustinDate:"",clientApprovedDate:"",paymentReceivedDate:"",proposalRef:"",poRef:"",isAssembly:false,specSummary:"",markupPct:30,feeMarkupPct:null,ccFeePct:0,depositPct:"50",assemblyName:"",assemblyId:null,childType:"",shipToVendorId:null,vendorContactName:"",vendorContactEmail:"",wrEntries:[],importSource:"",itemNumber:null,procurementClass:"standard",shippedDate:"",deliveredDate:"",installPlannedDate:"",installDays:"",installEventId:"",installDate:"",trackingNumber:"",carrier:"",installScheduleNotes:"",deliveredToStateOn:""};var init=p.initial?Object.assign({},BLANK,p.initial,{children:p.initial.children||[]}):Object.assign({},BLANK);var fs=useState(function(){var draft=loadDraft("item");if(draft&&!p.initial){var cleaned=Object.assign({},draft);if(!cleaned.id)cleaned.importSource="";return Object.assign({},BLANK,cleaned,{children:cleaned.children||[]});}return init;}());var f=fs[0];var setF=fs[1];var fRef=useRef(f);useEffect(function(){fRef.current=f;},[f]);
  var draftLoadedS=useState(!!(loadDraft("item")&&!p.initial));var draftLoaded=draftLoadedS[0];var setDraftLoaded=draftLoadedS[1];var sts=useState(false);var showSavedToast=sts[0];var setShowSavedToast=sts[1];
  useEffect(function(){if(f.item||f.vendor||f.schedule){saveDraft("item",f);window._hasUnsavedChanges=true;}return function(){};}, [f]);
  // Safety net: if initial had assembly room info but useState didn't pick it up, apply it on mount
  useEffect(function(){
    if(p.initial&&p.initial.assemblyId){
      var needsRoom=!f.roomName&&p.initial.roomName;
      var needsNum=!f.roomNumber&&p.initial.roomNumber;
      if(needsRoom||needsNum){
        setF(function(prev){return Object.assign({},prev,{roomName:p.initial.roomName||prev.roomName,roomNumber:p.initial.roomNumber||prev.roomNumber});});
      }
    }
  },[]);// eslint-disable-line react-hooks/exhaustive-deps
  var dws=useState(null);var dupWarn=dws[0];var setDupWarn=dws[1];var ats=useState("details");var activeTab=ats[0];var setActiveTab=ats[1];var qds=useState([]);var quoteDocs=qds[0];var setQuoteDocs=qds[1];var cms=useState(null);var comEmailChild=cms[0];var setComEmailChild=cms[1];function set(k){return function(v){setF(function(prev){var n=Object.assign({},prev);n[k]=v;return n;});};}
  var sue=useState(null);var scanUploadError=sue[0];var setScanUploadError=sue[1];var ms=useState("client");var tsMode=ms[0];var setTsMode=ms[1];var sca=useState(false);var showCreateAsm=sca[0];var setShowCreateAsm=sca[1];
  function setFees(fees){setF(function(prev){return Object.assign({},prev,{fees:fees});});}
  function setImages(images){setF(function(prev){return Object.assign({},prev,{images:images});});}
  function setHeroIndex(idx){setF(function(prev){return Object.assign({},prev,{heroIndex:idx});});}function setSpecs(specs){setF(function(prev){var n=Object.assign({},prev);n.specs=typeof specs==='function'?specs(prev.specs||{}):specs;return n;});}
  var bps=useState(false);var showBulbPicker=bps[0];var setShowBulbPicker=bps[1];var sfn=useState(false);var showFinal=sfn[0];var setShowFinal=sfn[1];
  var cbes=useState(null);var ctxEditing=cbes[0];var setCtxEditing=cbes[1];
  var ask=useState(false);var assigningSKU=ask[0];var setAssigningSKU=ask[1];
  var skev=useState('');var skuEditVal=skev[0];var setSkuEditVal=skev[1];
  var skdw=useState(false);var skuDupWarning=skdw[0];var setSkuDupWarning=skdw[1];
  var appCtx=useAppContext();
  // New item with a pre-filled project (opened from a room, a schedule, a
  // duplicate): stamp the project/client markup defaults once on mount.
  // The project <Sel> stamps on change; this covers the path that never
  // touches it. Only blanks are filled — a draft's typed rate survives.
  useEffect(function(){
    if(f.id||!f.project) return;
    var _pr=(p.projects||appCtx.projects||[]).find(function(x){return x.label===f.project;})||null;
    var _d=resolveMarkupDefaults(_pr,appCtx.clients);
    if(f.feeMarkupPct==null) set("feeMarkupPct")(_d.feeMarkupPct);
    if(f.markupPct==null||String(f.markupPct)===String(BLANK.markupPct)) set("markupPct")(_d.markupPct);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // No-op pass-through. Tax derives from project-level config at
  // document-generation time via `computeItemTax(item, project,
  // allItems)` in utils.js. The helper survives because the three save
  // call sites (handleSave, "Save Anyway", debounced auto-save) each
  // wrap their item in it; inline-deleting the calls is a Phase 5
  // cleanup that also rips the rest of the legacy tax code
  // (the old per-item auto-calc and rate cache, both removed 2026-10-17).
  function applyTaxAutoCalc(item) {
    return item;
  }

  // Lock mode: once an item passes the LOCK_INDEX (Client Approved), every
  // material field flips from editable input to read-only text. Edits to
  // material fields require a Change Order (audit trail + owner approval).
  // See CLAUDE.md "Status System" + "Component Independence" + the parameterized
  // test at __tests__/itemFormLockMode.test.jsx. Cancelled items also render
  // read-only (terminal status) but show no Request Change Order button.
  var isLocked = isFinanciallyCommitted(f.status) && !isCancelled(f) && !isNonProcured(f.procurementClass);
  var isReadOnly = isLocked || isCancelled(f);
  var cms=useState(false);var cancelModalOpen=cms[0];var setCancelModalOpen=cms[1];
  var coms=useState(false);var coModalOpen=coms[0];var setCoModalOpen=coms[1];
  var canManageCancel=appCtx.isOwner||hasAdminAccess(appCtx.userEmail,appCtx.firmSettings);
  var dts=useState('');var dupToast=dts[0];var setDupToast=dts[1];
  // Once user clicks "Save Anyway" on the dup warning, we remember that
  // they've acknowledged this combination and don't keep asking on every
  // subsequent save. Resets if vendor/item-name/roomNumber changes.
  var daks=useState(false);var dupAcknowledged=daks[0];var setDupAcknowledged=daks[1];
  useEffect(function(){
    setDupAcknowledged(false);
  },[f.vendor, f.item, f.roomNumber]);
  // Auto-save indicator — shows when last auto-save succeeded
  var asas=useState(null);var autoSavedAt=asas[0];var setAutoSavedAt=asas[1];
  // Save state — reflects DB-confirmed state, not optimistic local update.
  // 'idle'    — nothing in flight, last known good
  // 'saving'  — request out, waiting for DB confirmation (can take 1–6s in
  //             Safari due to retries on its CORS preflight quirks)
  // 'saved'   — DB confirmed within last 4s
  // 'failed'  — all retries exhausted
  var sst=useState('idle');var saveState=sst[0];var setSaveState=sst[1];

  // Duplicate this item to a new draft. Strips IDs, financial-commitment fields,
  // and status history so the duplicate is a fresh Specifying-stage item ready
  // to edit (e.g., change the room when it's the same lamp in a different room,
  // or rebuild after a cancellation).
  function duplicateItem(){
    if(!f.id){return;} // can't duplicate an unsaved draft
    var clone=Object.assign({},f);
    // Reset identity + lifecycle fields
    delete clone.id;
    clone.status='Specifying';
    clone.itemNumber=null;
    clone.proposalId=undefined;
    clone.poNumber='';
    clone.poRef='';
    clone.sentToAustinDate='';
    clone.clientApprovedDate='';
    clone.paymentReceivedDate='';
    clone.orderPlacedDate='';
    clone.receivedDate='';
    clone.finalInvoiceId='';
    clone.finalInvoiceSentAt='';
    clone.wrEntries=[];
    clone.statusNotes='';
    clone.importSource='';
    clone.date=new Date().toISOString().slice(0,10);
    // A duplicate is a NEW item, so it takes today's fee rate from
    // project → client → firm default — not the source's stamp. Before
    // 2026-10-11 the copy carried the source's rate, so every item
    // duplicated from a pre-2026-10-06 row came out at the legacy 10%
    // (the 26-Hoback bedding in rooms 124/127). Merchandise markup is
    // kept: that is a per-product decision, and the source's was made
    // on purpose (e.g. 5% on install-only rows).
    var _dupProj=(p.projects||appCtx.projects||[]).find(function(x){return x.label===clone.project;})||null;
    clone.feeMarkupPct=resolveMarkupDefaults(_dupProj,appCtx.clients).feeMarkupPct;
    // Regenerate any embedded children with fresh ids and a Specifying status
    if(Array.isArray(clone.children)){
      clone.children=clone.children.map(function(c){
        return Object.assign({},c,{
          id:crypto.randomUUID?crypto.randomUUID():(Date.now()+'-'+Math.random()).toString(),
          status:'Specifying',
          statusNotes:'',
        });
      });
    }
    appCtx.addItem(clone,[]);
    setDupToast('Duplicated — open Items to edit the new copy');
    setTimeout(function(){setDupToast('');},3500);
  }
  async function handleAssignSKU(){
    if(!f.id){alert('Save the item first before assigning a SKU.');return;}
    setAssigningSKU(true);
    try{
      var num=await appCtx.autoAssignItemNumber(f);
      if(num){set('itemNumber')(num);}
      else{alert('Could not assign SKU — make sure the item has a project with a project code and a room number set.');}
    }catch(e){
      alert('SKU assignment failed: '+e.message);
    }finally{
      setAssigningSKU(false);
    }
  }
  function addBulb(bulb){setF(function(prev){var n=Object.assign({},prev);n.children=(prev.children||[]).concat([Object.assign({},bulb,{id:Date.now(),parentId:null})]);return n;});}
  function addChild(type){setF(function(prev){var n=Object.assign({},prev);var qtyType=(type==="Fabric"||type==="Trim"||type==="COM/COL Fabric")?"Yard":"Each";n.children=(prev.children||[]).concat([{id:Date.now(),childType:type||"Fabric",parentId:null,vendor:"",item:"",qty:"1",qtyType:qtyType,specs:{},status:"In Database",notes:"",netPrice:"",depositPct:"50",fees:{freight:"0",crating:"0",installation:"0",salesTax:"0",tariff:"0",custom:[]},imageData:"",imageUrl:"",date:dateStr()}]);return n;});}function updateChild(id,patch){setF(function(prev){var n=Object.assign({},prev);n.children=(prev.children||[]).map(function(c){return c.id===id?Object.assign({},c,patch):c;});return n;});}function removeChild(id){setF(function(prev){var n=Object.assign({},prev);n.children=(prev.children||[]).filter(function(c){return c.id!==id;});return n;});}function handleStatusUpdate(newStatus,date){setF(function(prev){var n=Object.assign({},prev);n.status=newStatus;if(newStatus==="Sent to Austin")n.sentToAustinDate=date;if(newStatus==="Client Approved")n.clientApprovedDate=date;if(newStatus==="Payment Received")n.paymentReceivedDate=date;return n;});}var sv=null;for(var i=0;i<p.vendors.length;i++){if(p.vendors[i].name===f.vendor){sv=p.vendors[i];break;}}var sp=null;for(var i=0;i<p.projects.length;i++){if(p.projects[i].label===f.project){sp=p.projects[i];break;}}var sidemark=computeSidemark(f.project,f.roomName,f.roomNumber);var comChildren=(f.children||[]).filter(function(c){return c.childType==="Fabric";});// Strict duplicate definition: vendor + item name + room number must ALL
// match. A real duplicate is an exact triplicate. Same item different room
// (lamp in two rooms) or same vendor different product is a legitimate
// variant and shouldn't trigger the warning.
function checkDups(){return(p.allItems||[]).filter(function(it){
  if(it.id===f.id)return false;
  if(!it.vendor||!f.vendor||it.vendor!==f.vendor)return false;
  if(!it.item||!f.item||it.item.toLowerCase()!==f.item.toLowerCase())return false;
  if(!it.roomNumber||!f.roomNumber||String(it.roomNumber)!==String(f.roomNumber))return false;
  return true;
});}var proposalLocked=!!(f.proposalId&&!p.isOwner&&(p.proposals||[]).some(function(prop){return prop.id===f.proposalId&&prop.status==='Sent';}));function handleSave(){if(proposalLocked){alert('This item is locked — the proposal has been sent. Only the owner can edit it.');return;}clearDraft("item");window._hasUnsavedChanges=false;if(!f.item||!f.schedule){alert("Item description and schedule are required.");return;}if(!f.project){if(!window.confirm("No project selected. This item won't be tied to a project — it may appear across all project views. Continue?"))return;}var dups=checkDups();if(dups.length>0&&!dupWarn&&!dupAcknowledged){setDupWarn(dups);return;}var newId=f.id||(crypto.randomUUID?crypto.randomUUID():String(Date.now()));var savedItem=applyTaxAutoCalc(Object.assign({},f,{id:newId,sidemark:sidemark}));if(!f.id){setF(function(prev){return Object.assign({},prev,{id:newId});});}p.onSave(savedItem);setShowSavedToast(true);setTimeout(function(){setShowSavedToast(false);},2000);}

  // ── AUTO-SAVE ────────────────────────────────────────────────────────────
  // Debounced auto-save 600ms after the user stops typing. Only fires for
  // existing items (id present), only when required fields are filled, and
  // never when the proposal is locked. Skips dup-check — manual Save still
  // does the dup gate. Updates appCtx.updateItem directly which both
  // refreshes local state and queues the Supabase save.
  useEffect(function(){
    if(!f.id) return;
    if(proposalLocked) return;
    if(!f.item || !f.schedule) return;
    var t=setTimeout(function(){
      var savedItem=applyTaxAutoCalc(Object.assign({},f,{sidemark:sidemark}));
      if(typeof appCtx.updateItem==='function'){
        setSaveState('saving');
        var result=appCtx.updateItem(f.id, savedItem);
        if(result && typeof result.then==='function'){
          result.then(function(ok){
            setSaveState(ok ? 'saved' : 'failed');
            if(ok) setAutoSavedAt(Date.now());
            // Clear the 'saved' visual after 4s so it doesn't linger
            if(ok) setTimeout(function(){
              setSaveState(function(s){ return s === 'saved' ? 'idle' : s; });
            }, 4000);
          });
        } else {
          // Legacy non-promise return — assume optimistic success
          setSaveState('saved');
          setAutoSavedAt(Date.now());
        }
      }
    },600);
    return function(){clearTimeout(t);};
  },[JSON.stringify(f), proposalLocked, sidemark]);
  var tabBarItems=[
    <TabBtn key="d" label="Details &amp; Specs" active={activeTab==="details"} onClick={function(){setActiveTab("details");}} />,
    <TabBtn key="p" label="Pricing" active={activeTab==="pricing"} onClick={function(){setActiveTab("pricing");}} />,
    <TabBtn key="t" label="Status &amp; Tracking" active={activeTab==="tracking"} onClick={function(){setActiveTab("tracking");}} />,
    <TabBtn key="e" label="Quote Email" active={activeTab==="email"} onClick={function(){setActiveTab("email");}} />,
    <TabBtn key="qs" label="Quote Summary" active={activeTab==="quotesummary"} onClick={function(){setActiveTab("quotesummary");}} />
  ];
  tabBarItems.push(<TabBtn key="img" label="Images" active={activeTab==="images"} onClick={function(){setActiveTab("images");}} />);
  tabBarItems.push(<TabBtn key="hist" label="History" active={activeTab==="history"} onClick={function(){setActiveTab("history");}} />);
  tabBarItems.push(<TabBtn key="msg" label="Messages" active={activeTab==="messages"} onClick={function(){setActiveTab("messages");}} />);
  if((f.children||[]).filter(function(c){return c.childType!=="Bulb";}).length>0){tabBarItems.push(<TabBtn key="cq" label="Component Quotes" active={activeTab==="compquotes"} onClick={function(){setActiveTab("compquotes");}} />);}
  var draftBanner=draftLoaded?<div style={{background:"#F7F7F5",border:"0.5px solid #D8D7D2",borderRadius:6,padding:"10px 14px",marginBottom:16,fontSize:11,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
    <span style={{color:"#555552",fontWeight:500}}>Draft restored — you have unsaved changes from a previous session</span>
    <button onClick={function(){clearDraft("item");window._hasUnsavedChanges=false;setF(Object.assign({},BLANK));setDraftLoaded(false);}} style={{padding:"4px 12px",background:"none",border:"0.5px solid #D8D7D2",borderRadius:6,fontSize:11,cursor:"pointer",color:"#888880"}}>Discard Draft</button>
  </div>:null;
  var dupWarningEl=null;if(dupWarn){dupWarningEl=<div style={{background:"#FDECEA",border:"0.5px solid #F5C0B8",borderRadius:6,padding:"12px 16px",marginBottom:16,fontSize:11}}>
    <div style={{fontWeight:500,color:"#C8322A",marginBottom:8}}>⚠ Possible duplicate detected</div>
    {(dupWarn||[]).map(function(d){return <div key={d.id} style={{fontSize:12,color:"#555",marginBottom:4}}>{"• "+d.item+" — "+d.project+" / "+d.roomName+" ("+d.status+")"}</div>;})}
    <div style={{display:"flex",gap:8,marginTop:10}}>
      <Btn variant="warning" onClick={function(){setDupWarn(null);setDupAcknowledged(true);p.onSave(applyTaxAutoCalc(Object.assign({},f,{id:f.id||(crypto.randomUUID?crypto.randomUUID():String(Date.now())),sidemark:sidemark})));}}>Save Anyway</Btn>
      <Btn variant="secondary" onClick={function(){setDupWarn(null);}}>Review First</Btn>
    </div>
  </div>;}
  var _fsp=null;(p.projects||[]).forEach(function(pr){if((pr.label||pr.id)===f.project)_fsp=pr;});
  var noAddrNote=(_fsp&&!_fsp.receiverId&&!f.shipToVendorId&&!_fsp.address)?<div style={{fontSize:12,color:"#80cbc4",marginBottom:8}}>No shipping address on file — add in Projects tab</div>:null;
  var sidemarkEl=null;var childrenSection=null;
  var bulbChildren=(f.children||[]).filter(function(c){return c.childType==="Bulb";});
  var otherChildren=(f.children||[]).filter(function(c){return c.childType!=="Bulb";});
  var isLighting=f.schedule==="Lighting";
  var isComponent=!!(f.childType&&f.assemblyId);
  var isWorkroom=f.childType==="Workroom";
  var isFabricLike=f.childType==="Fabric"||f.childType==="Trim";
  var isHardwareLike=f.childType==="Hardware"||f.childType==="Roller Shades";
  if(bulbChildren.length>0){var bulbSection=<div style={{marginBottom:12}}>
    <SHead>Bulbs</SHead>
    <div style={{border:"1px solid #e0e0e0",borderRadius:8,overflow:"hidden"}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
        <thead><tr style={{background:"#fff8e1"}}>
          {["Shape/SKU","W","Lm","K","Base","Voltage","Qty","Vendor",""].map(function(hd){return <th key={hd} style={{padding:"6px 10px",textAlign:"left",fontWeight:600,color:"#555",borderBottom:"1px solid #ffe082",fontSize:11}}>{hd}</th>;})}
        </tr></thead>
        <tbody>{bulbChildren.map(function(c){return <tr key={c.id} style={{borderBottom:"1px solid #f5f5f5"}}>
          <td style={{padding:"6px 10px",fontWeight:600,fontSize:11}}>{c.item||c.bulbShape||"—"}</td>
          <td style={{padding:"6px 10px"}}>{c.watts||"—"}</td>
          <td style={{padding:"6px 10px",color:"#888"}}>{c.lumens||"—"}</td>
          <td style={{padding:"6px 10px",color:c.kelvin<=2400?"#c0392b":c.kelvin<=2700?"#e65100":"#f39c12",fontWeight:600}}>{c.kelvin?c.kelvin+"K":"—"}</td>
          <td style={{padding:"6px 10px"}}>{c.base||"—"}</td>
          <td style={{padding:"6px 10px",color:"#888"}}>{c.voltage||"—"}</td>
          <td style={{padding:"6px 10px"}}>{c.qty||"1"}</td>
          <td style={{padding:"6px 10px",color:"#888"}}>{c.vendor||"—"}</td>
          <td style={{padding:"6px 10px"}}><button onClick={function(){removeChild(c.id);}} style={{background:"none",border:"none",color:"#aaa",cursor:"pointer",fontSize:13}}>✕</button></td>
        </tr>;})}</tbody>
      </table>
    </div>
  </div>;
  childrenSection=<div>{bulbSection}{otherChildren.length>0?<div style={{marginBottom:12}}>
    <SHead>Linked Items</SHead>
    {otherChildren.map(function(c){return <ChildItemForm key={c.id} child={c} onChange={function(patch){updateChild(c.id,patch);}} vendors={p.vendors} onAddVendor={p.onAddVendor} parentItem={f.item} siblings={otherChildren.filter(function(s){return s.id!==c.id;})} onRemove={function(){removeChild(c.id);}} />;})}</div>:null}</div>;}
  else if(otherChildren.length>0){childrenSection=<div style={{marginBottom:12}}>
    <SHead>Linked Items</SHead>
    {otherChildren.map(function(c){return <ChildItemForm key={c.id} child={c} onChange={function(patch){updateChild(c.id,patch);}} vendors={p.vendors} onAddVendor={p.onAddVendor} parentItem={f.item} siblings={otherChildren.filter(function(s){return s.id!==c.id;})} onRemove={function(){removeChild(c.id);}} />;})}
  </div>;}
  var comEmailBtns=null;if(comChildren.length>0){comEmailBtns=<div style={{marginTop:8,display:"flex",gap:8,flexWrap:"wrap"}}>{comChildren.map(function(c){return <Btn key={c.id} variant="secondary" small={true} onClick={function(){setComEmailChild(c);}}>{"COM Email → "+(c.vendor||c.vendorCustom||"fabric vendor")}</Btn>;})}</div>;}
  var sentNote=f.sentToAustinDate?<div style={{fontSize:12,color:"#0277bd",marginTop:4}}>{"Sent to Austin: "+f.sentToAustinDate}</div>:null;
  var clientNote=f.clientApprovedDate?<div style={{fontSize:12,color:"#1A5C38",marginTop:4}}>{"Client Approved: "+f.clientApprovedDate}</div>:null;
  var payNote=f.paymentReceivedDate?<div style={{fontSize:12,color:"#00695c",marginTop:4}}>{"Payment Received: "+f.paymentReceivedDate}</div>:null;
  var tabContent=null;
  if(activeTab==="details"){tabContent=<div style={{display:"grid",gridTemplateColumns:"3fr 2fr",gap:28}}>

  {/* ── LEFT COLUMN: Item identity ─────────────────────────────────────────── */}
  <div>

    {/* Procurement class selector — Phase 2 of OF/BO feature (2026-05-28).
        Placed at the top of the Details tab because the choice changes
        which fields are relevant. Standard items get the normal 14-status
        pipeline + financials + SKU + QB push. OF/BO items skip cost/markup,
        skip SKU, skip QB, and ride a truncated status pipeline.
        Hidden on assembly child items (parentId set) — children inherit
        their parent assembly's class implicitly via shouldBillItem. */}
    {!isComponent && <ProcurementClassPicker
      value={f.procurementClass || 'standard'}
      onChange={function(next) { set('procurementClass')(next); }}
      item={f}
      purchaseOrders={appCtx.purchaseOrders || []}
      invoices={appCtx.invoices || []}
      paymentAllocations={appCtx.paymentAllocations || []}
      locked={isReadOnly}
    />}

    {/* Project + Room compact — hidden; editing handled in context bar */}
    <div style={{display:'none'}}>
    {!isComponent&&<Row>
      <div style={{flex:"0 0 160px"}}><Sel label="Project" value={f.project} onChange={function(v){set("project")(v);set("roomName")("");set("roomNumber")("");if(!f.id){var _pr=(p.projects||appCtx.projects||[]).find(function(x){return x.label===v;})||null;var _d=resolveMarkupDefaults(_pr,appCtx.clients);set("markupPct")(_d.markupPct);set("feeMarkupPct")(_d.feeMarkupPct);}}}>
        <option value="">— Select —</option>
        {activeProjects(p.projects, f.project).map(function(proj){return <option key={proj.id} value={proj.label}>{proj.label}</option>;})}
      </Sel></div>
      <div style={{flex:1}}><RoomAC nameVal={f.roomName} numberVal={f.roomNumber} onName={set("roomName")} onNumber={set("roomNumber")} rooms={sp?sp.rooms:[]} /></div>
    </Row>}
    </div>
    {noAddrNote}

    {/* Assembly picker — formal assembly grouping */}
    {(function(){
      var asmObj=(p.assemblies||[]).find(function(a){return a.id===f.assemblyId;})||null;
      return <div>
        {asmObj&&<div style={{background:'#fafafa',border:'1px solid #E0E0DC',borderRadius:8,padding:'10px 14px',marginBottom:12}}>
          <div style={{fontSize:11,fontWeight:700,color:'#999',letterSpacing:0.3,textTransform:'uppercase',marginBottom:4}}>Assembly</div>
          <div style={{fontSize:13,fontWeight:700,color:'#111'}}>{asmObj.name}</div>
          <div style={{fontSize:11,color:'#888',marginTop:2}}>{[asmObj.schedule,asmObj.room_name,asmObj.project_label].filter(Boolean).join(' · ')}</div>
        </div>}
        {!isComponent&&<AssemblyPicker
          value={f.assemblyId||''}
          assemblies={p.assemblies||[]}
          projects={p.projects}
          project={f.project}
          onChange={function(id,asm){
            setF(function(prev){
              var patch={assemblyId:id||null};
              if(asm){
                if(asm.schedule)patch.schedule=asm.schedule;
                if(asm.project_label)patch.project=asm.project_label;
                if(asm.room_name)patch.roomName=asm.room_name;
                if(asm.room_number)patch.roomNumber=asm.room_number;
                if(asm.sidemark)patch.sidemark=asm.sidemark;
              }
              return Object.assign({},prev,patch);
            });
          }}
          onCreateNew={function(){setShowCreateAsm(true);}}
        />}
        {!isComponent&&showCreateAsm&&<AssemblyCreateModal
          onClose={function(){setShowCreateAsm(false);}}
          projects={p.projects}
          initialProject={f.project}
          initialSchedule={f.schedule}
          onSave={async function(data){
            if(!p.onAddAssembly)return null;
            var result=await p.onAddAssembly(data);
            if(result){
              setF(function(prev){return Object.assign({},prev,{assemblyId:result.id,schedule:result.schedule||prev.schedule,project:result.project_label||prev.project,roomName:result.room_name||prev.roomName,roomNumber:result.room_number||prev.roomNumber,sidemark:result.sidemark||prev.sidemark});});
            }
            return result;
          }}
        />}
      </div>;
    })()}

    {/* Schedule — hidden from form body; context bar pill handles display and editing */}
    <div style={{display:'none'}}><div style={{maxWidth:240}}>
      {(function(){
        var locked=!!(f.assemblyId&&(p.assemblies||[]).find(function(a){return a.id===f.assemblyId;}));
        var lockIcon=locked?<span style={{fontSize:11,marginLeft:4,textTransform:'none',letterSpacing:0}}>🔒</span>:null;
        return <div style={{marginBottom:12}}>
          <Lbl required={!locked}>Schedule{lockIcon}</Lbl>
          <select
            value={f.schedule||''}
            onChange={function(e){if(!locked){set("schedule")(e.target.value);set("childType")("");setSpecs(Object.assign({},f.specs,{treatmentType:""}));}}}
            disabled={locked}
            style={Object.assign({},IS,locked
              ?{background:'#F8F8F6',color:'#999',border:'0.5px solid #E0E0DC',cursor:'not-allowed',opacity:1}
              :{background:'#fff'})}
          >
            <option value="">— Select schedule —</option>
            {SCHEDULE_NAMES.map(function(s){return <option key={s} value={s}>{s}</option>;})}
          </select>
        </div>;
      })()}
    </div></div>

    {/* Component type buttons — locked badge when item is an assembly component */}
    {(function(){
      var comps=ASSEMBLY_COMPONENTS[f.schedule]||[];
      if(!comps.length)return null;
      if(isComponent){
        return <div style={{marginBottom:12}}>
          <Lbl>Component Type</Lbl>
          <span style={{display:'inline-flex',alignItems:'center',gap:6,padding:'5px 14px',background:'#F4F4F0',color:'#555',borderRadius:20,fontSize:11,fontWeight:700,letterSpacing:0.3}}>
            {f.childType}
            <span title="Type is locked. Delete and recreate to change." style={{fontSize:10,opacity:0.75,cursor:'help'}}>🔒</span>
          </span>
        </div>;
      }
      return <div style={{marginBottom:12}}>
        <Lbl>Component Type</Lbl>
        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
          {comps.map(function(ct){
            var active=f.childType===ct;
            return <button key={ct} onClick={function(){set('childType')(active?'':ct);}}
              style={{padding:'5px 12px',border:'1.5px solid '+(active?'#111':'#D0D0CC'),borderRadius:20,fontSize:11,fontWeight:600,cursor:'pointer',
                background:active?'#F4F4F0':'#fff',color:active?'#111':'#555',transition:'all 0.12s'}}>{ct}</button>;
          })}
        </div>
      </div>;
    })()}

    {/* + Add another component to this assembly — only when this item is
        already a saved component (assemblyId set, item id present, and
        the parent wired the onAddSiblingComponent prop). Hands off to the
        same flow as the items-list "+ Add component" button so the new
        component gets the assembly's schedule/room/sidemark pre-filled. */}
    {f.assemblyId && f.id && p.onAddSiblingComponent && (
      <div style={{marginBottom:12}}>
        <button
          onClick={function(){p.onAddSiblingComponent(f.assemblyId);}}
          style={{padding:'5px 12px',border:'0.5px solid #D4007A',borderRadius:20,fontSize:11,fontWeight:600,cursor:'pointer',background:'#fff',color:'#D4007A',fontFamily:'inherit'}}>
          + Add another component to this assembly
        </button>
      </div>
    )}

    {/* Product Name + Item Description — Workroom shows only service description */}
    {isWorkroom
      ?<div style={{marginBottom:12}}><Inp label="Workroom / Service" value={f.item} onChange={set("item")} placeholder="e.g. Drapery fabrication" required={true} locked={isReadOnly} dataField="item" /></div>
      :<Row>
          <div style={{flex:1}}><Inp label="Product Name" value={f.productName||""} onChange={set("productName")} placeholder="e.g. Myrna Table" locked={isReadOnly} dataField="productName" /></div>
          <div style={{flex:1}}><Inp label="Item Description" value={f.item} onChange={set("item")} placeholder="e.g. DINING CHAIR" required={true} locked={isReadOnly} dataField="item" /></div>
        </Row>}

    {/* Qty + Qty Type compact (Tag only shown on standalone items — assemblies own the tag) */}
    <Row>
      <div style={{flex:"0 0 80px"}}><Inp label="Qty" value={f.qty} onChange={set("qty")} placeholder="1" locked={isReadOnly} dataField="qty" /></div>
      <div style={{flex:1}}><QtyTypeField value={f.qtyType} onChange={set("qtyType")} locked={isReadOnly} dataField="qtyType" /></div>
      {!f.assemblyId&&<div style={{flex:"0 0 100px"}}><Inp label="Tag" value={f.tag||""} onChange={set("tag")} placeholder="e.g. F-3" /></div>}
    </Row>

    {/* Vendor */}
    {/* Item creation is one of the two well-known surfaces where new
        vendors get added (the other is VendorDirectory). allowAdd default
        true here — staff regularly encounter new vendors mid-spec. */}
    {isReadOnly
      ? <div><Lbl>Vendor</Lbl><div data-field="vendor" style={Object.assign({}, IS, { background: '#FAFAF8', color: '#444' })}>{f.vendor || '—'}</div></div>
      : <VendorPicker
          value={f.vendor}
          onChange={function(v){setF(function(prev){return Object.assign({},prev,{vendor:v,vendorContactName:'',vendorContactEmail:''});});}}
          vendors={p.vendors}
          items={p.items}
          purchaseOrders={p.purchaseOrders}
          onAddVendor={p.onAddVendor} />}
    <Row>
      {(function(){
        var _vo=(p.vendors||[]).find(function(v){return v.name===f.vendor;});
        var _vc=_vo&&(_vo.contacts||[]).filter(function(c){return c.name;});
        if(!_vc||!_vc.length)return null;
        return <div style={{flex:1}}>
          <div style={{fontSize:10,fontWeight:500,letterSpacing:'0.07em',textTransform:'uppercase',color:'#999',marginBottom:3}}>Vendor Contact</div>
          <div style={{marginBottom:12}}>
            <select value={f.vendorContactName||''} onChange={function(e){
              var name=e.target.value;
              var c=_vc.find(function(c){return c.name===name;})||{};
              setF(function(prev){return Object.assign({},prev,{vendorContactName:name,vendorContactEmail:c.email||''});});
            }} style={Object.assign({},IS,{background:'#fff'})}>
              <option value=''>— Select contact —</option>
              {_vc.map(function(c,i){return <option key={i} value={c.name}>{c.name+(c.phone?' - '+c.phone:'')}</option>;})}
            </select>
          </div>
        </div>;
      })()}
      <div style={{flex:1}}>
        <Sel label="Ships to" value={String(f.shipToVendorId||'')} onChange={function(v){set("shipToVendorId")(v?parseInt(v)||null:null);}}>
          <option value="">— Project receiver (default) —</option>
          {(p.vendors||[]).filter(function(v){return v.vendorType==="Receiver"||v.vendorType==="Workroom";}).map(function(v){return <option key={v.id} value={String(v.id)}>{v.name+(v.vendorType==="Workroom"?" (Workroom)":"")}</option>;})}
        </Sel>
      </div>
    </Row>

    {/* Manufacturer + Model — hidden for Workroom (service, not a product) */}
    {!isWorkroom&&<Row>
      <div style={{flex:1}}><Inp label="Manufacturer" value={f.manufacturer||""} onChange={set("manufacturer")} placeholder="e.g. Arteriors" locked={isReadOnly} dataField="manufacturer" /></div>
      <div style={{flex:1}}><Inp label="Model / SKU" value={f.model||""} onChange={set("model")} placeholder="e.g. 49983" locked={isReadOnly} dataField="model" /></div>
    </Row>}

    {/* Finish — only for Hardware/Roller Shades when component; always shown for standalone items */}
    {(!isComponent||isHardwareLike)&&<div style={{marginBottom:12}}>
      <Lbl>Finish</Lbl>
      {isReadOnly ? (
        <ReadValue dataField="finish">{f.finish}</ReadValue>
      ) : (
        <textarea
          data-field="finish"
          value={f.finish||""}
          onChange={function(e){set("finish")(e.target.value);}}
          placeholder="e.g. Antique Brass / Matte Black — list multiple if needed"
          rows={2}
          style={{width:"100%",padding:"7px 10px",border:"1px solid #ddd",borderRadius:6,fontSize:13,fontFamily:"inherit",resize:"vertical",boxSizing:"border-box"}}
        />
      )}
    </div>}

    {/* Dimensions */}
    {(!isComponent||isHardwareLike)&&<DimensionsField value={f.dimensions||""} onChange={set("dimensions")} locked={isReadOnly} />}

    {/* Product URL — compact */}
    {!isWorkroom&&<div style={{marginBottom:12}}>
      <Lbl>Product URL</Lbl>
      <div style={{display:"flex",gap:6,alignItems:"center"}}>
        <input
          type="url"
          value={f.sourceUrl||""}
          onChange={function(e){set("sourceUrl")(e.target.value);}}
          placeholder="https://..."
          style={Object.assign({},IS,{flex:1})}
        />
        {f.sourceUrl&&<a
          href={f.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          title="Open in new tab"
          style={{padding:"7px 10px",background:"#f5f5f5",border:"1px solid #ddd",borderRadius:6,fontSize:12,color:"#555",textDecoration:"none",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:4}}
        >Open ↗</a>}
      </div>
    </div>}

    {/* Notes — NOT a material field, stays editable even when item is locked.
        Bookkeepers add internal notes long after an item ships; that audit
        shouldn't require a Change Order. */}
    <Txta label="Notes" labelColor="#D4007A" hint="prints on client / GC / workroom schedules" value={f.notes} onChange={set("notes")} placeholder="Additional notes..." rows={2} dataField="notes" />

    <label style={{display:"flex",alignItems:"center",gap:8,marginTop:14,cursor:"pointer",userSelect:"none"}}>
      <input type="checkbox" checked={!!(f.importSource&&(f.importSource.indexOf("Pre-App")>=0||f.importSource.indexOf("ABD")>=0))} onChange={function(e){set("importSource")(e.target.checked?"ABD":"");}} style={{width:14,height:14,cursor:"pointer",flexShrink:0}} />
      <span style={{fontSize:12,color:"#666"}}>ABD item <span style={{color:"#bbb"}}>— excluded from financial totals</span></span>
    </label>

    <label style={{display:"flex",alignItems:"center",gap:8,marginTop:8,cursor:"pointer",userSelect:"none"}}>
      <input type="checkbox" checked={!!f.offScheduleDefault} onChange={function(e){set("offScheduleDefault")(e.target.checked);}} style={{width:14,height:14,cursor:"pointer",flexShrink:0}} />
      <span style={{fontSize:12,color:"#666"}}>Off schedule by default <span style={{color:"#bbb"}}>— appears in the schedule picker but starts unchecked</span></span>
    </label>

  </div>

  {/* ── RIGHT COLUMN: Scan/Upload + Schedule-specific specs ────────────────── */}
  <div>

    {/* Scan/Upload tool */}
    <ScanUploadTool
      itemId={f.id}
      schedule={f.schedule}
      fRef={fRef}
      setF={setF}
      setSpecs={setSpecs}
      set={set}
      supabase={supabase}
      saveItemDirect={p.onSaveDirect}
    />

    {/* Window Treatment component panels (replace generic spec fields) */}
    {(function(){
      if(f.schedule!=="Window Treatments")return null;
      var wtSiblings=f.assemblyId?(p.allItems||[]).filter(function(it){return it.assemblyId===f.assemblyId&&it.id!==f.id;}):f.assemblyName?(p.allItems||[]).filter(function(it){return it.assemblyName===f.assemblyName&&it.id!==f.id;}):[];
      if(f.childType==="Fabric")return <div style={{marginTop:16}}><FabricPanel specs={f.specs||{}} onChange={setSpecs} /></div>;
      if(f.childType==="Trim")return <div style={{marginTop:16}}><TrimPanel specs={f.specs||{}} onChange={setSpecs} /></div>;
      if(f.childType==="Workroom")return <div style={{marginTop:16}}><WorkroomPanel specs={f.specs||{}} onChange={setSpecs} siblings={wtSiblings} /></div>;
      if(f.childType==="Hardware")return <div style={{marginTop:16}}><HardwarePanel specs={f.specs||{}} onChange={setSpecs} /></div>;
      if(f.childType==="Roller Shades"||f.specs&&f.specs.treatmentType==="Roller Shades")return <div style={{marginTop:16}}><RollerShadePanel specs={f.specs||{}} onChange={setSpecs} /></div>;
      return null;
    })()}

    {/* Schedule-specific specs for all other schedules */}
    {f.schedule&&f.schedule!=="Window Treatments"&&(SCHEDULE_FIELDS[f.schedule]||[]).length>0&&<div style={{marginTop:16,background:"#fafafa",border:"1px solid #eee",borderRadius:8,padding:14}}>
      <div style={{fontSize:11,fontWeight:700,color:"#888",letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:10}}>
        {f.schedule+" Specs"}
      </div>
      <SpecFields schedule={f.schedule} specs={f.specs} onChange={setSpecs} />
      <Txta label="Spec Notes" labelColor="#D4007A" hint="prints on proposals, tear sheets, invoices & POs" value={f.specNotes||""} onChange={set("specNotes")} placeholder="Additional spec details for this schedule..." rows={2} />
      <Txta label="Internal Notes" value={f.internalNotes||""} onChange={set("internalNotes")} placeholder="Internal team notes — not shown on any client document..." rows={2} />
    </div>}

    {/* Documents & photos icon grid */}
    <div style={{marginTop:16}}>
      <div style={{fontSize:11,fontWeight:700,color:"#888",letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:8}}>Files &amp; Documents</div>
      <ItemDocuments
        itemId={f.id}
        images={f.images||[]}
        heroIndex={f.heroIndex||0}
        onImageClick={function(i){setActiveTab("images");}}
        compact={false}
        readOnly={false}
      />
    </div>

    <TeamNotesPanel
      itemId={f.id}
      projectCode={f.project}
      userEmail={appCtx.userEmail || ''}
      studioTeam={((appCtx.firmSettings || {}).studioTeam) || []}
    />
    {isLighting&&<div style={{marginTop:8}}>
      <Btn variant="warning" onClick={function(){setShowBulbPicker(true);}}>+ Add Bulb</Btn>
      {showBulbPicker&&<BulbPicker onAdd={function(bulb){addBulb(bulb);setShowBulbPicker(false);}} onClose={function(){setShowBulbPicker(false);}} />}
    </div>}
    {(function(){
      var hasWorkroom=otherChildren.some(function(c){return c.childType==="Workroom";});
      if(!hasWorkroom||!f.id)return null;
      return <div style={{marginBottom:8}}>
        <button
          onClick={function(){p.onGenerateWorkroomSheet&&p.onGenerateWorkroomSheet(Object.assign({},f),f.heroIndex||0,'',p.vendors,[],true).catch(function(e){alert("Workroom sheet error: "+e.message);});}}
          style={{padding:"5px 14px",background:"#e3f2fd",border:"1px solid #90caf9",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",color:"#1565c0"}}
        >⬇ Workroom Fabrication Order</button>
      </div>;
    })()}
    {!f.assemblyId&&(isLighting||otherChildren.length>0)&&childrenSection}
  </div>
</div>;}
  else if(activeTab==="pricing"){
  // Phase 2 of OF/BO feature: when the item is non-procured (Owner
  // Furnished or By Others), the entire Pricing tab is replaced with an
  // inert placeholder card. Cost / markup / client-price / fees fields
  // are not relevant — the item rides $0 through every financial
  // computation (see shouldBillItem in utils.js). Spec fields (vendor,
  // product, dimensions, finish) stay editable in the Details tab.
  {
  // Reference pricing for OF/BO (2026-07-29 — Cimon: "I'm not sure why we
  // need to remove the data points; we can just not have it move through
  // the purchasing system"). The full pricing fields render for every
  // classification; the note below states the rule. The money gate is
  // shouldBillItem in the MATH (proposal totals/lines, DEP payload, QB
  // estimate) — never field-hiding. Reference prices print on internal +
  // insurance schedules and tear sheets; never on client billing.
  var _n=parsePrice(f.netPrice);var _m=parseFloat(f.markupPct!=null?f.markupPct:30);var _q=parseFloat(f.qty||1);var _vdp=parseFloat(f.depositPct||50);var _cdp=_vdp>=100?100:80;var _fs=f.fees||{};var _hp=_n>0;
  function _feeN(s){var n=parseFloat(String(s==null?"":s).replace(/[$,]/g,""));return isNaN(n)?0:n;}
  var _netTot=calcNetTotal(_n,_q);var _cpUnit=calcClientPrice(_n,_m);var _cpTot=calcClientTotal(_n,_m,_q);
  var _cdep=calcClientDeposit(_n,_m,_q,_cdp);var _depTot=calcDepositInvoiceTotal(_n,_m,_q,_fs,_vdp,f);
  var _vdepBase=calcVendorDeposit(_n,_q,_vdp);
  var _finalTot=calcFinalInvoiceTotal(_n,_m,_q,_fs,_vdp,f);
  var CARD={border:"1px solid #D0D0CC",borderRadius:6,background:"#fff",padding:16,marginBottom:12};
  var CTITLE={fontSize:11,fontWeight:600,color:"#999",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:12};
  var CROW={display:"flex",justifyContent:"space-between",fontSize:12,color:"#555",marginBottom:6};
  var CDIV={borderTop:"0.5px solid #E0E0DC",marginTop:8,paddingTop:8,display:"flex",justifyContent:"space-between",fontSize:13};
  var CLBL={fontSize:10,fontWeight:600,color:"#999",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:4};
  tabContent=<div style={{maxWidth:560}}>
    <SHead>Item Pricing</SHead>
    {isNonProcured(f.procurementClass) && (
      <div data-testid="reference-pricing-note" style={{ border: '0.5px solid #C99700', borderRadius: 5, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: '#8A6D00', lineHeight: 1.5 }}>
        Reference pricing — {f.procurementClass === 'owner_furnished' ? 'Owner Furnished' : 'By Others'} items are never billed to the client or purchased by the firm. Values here are for records, internal schedules, insurance, and spec books.
      </div>
    )}
    <table style={{borderCollapse:"collapse",marginBottom:16}}><tbody>
      <tr>
        <td style={{fontSize:10,textTransform:"uppercase",color:"#999",paddingRight:16,whiteSpace:"nowrap",verticalAlign:"middle",paddingBottom:8}}>Net Cost</td>
        <td style={{verticalAlign:"middle",paddingRight:4,paddingBottom:8}}>
          {isReadOnly ? (
            // minWidth matches the edit-mode input's 140px so table-
            // layout-auto can't squeeze this cell below the value's
            // natural length. Without this, long netPrice values (e.g.
            // $115,214.68) would wrap at character level via ReadValue's
            // wordBreak:'break-word' style when the adjacent Net Total
            // cell wins more of the 560px container's width.
            <div style={{display:"inline-block",minWidth:140}}>
              <ReadValue dataField="netPrice">{f.netPrice ? '$' + f.netPrice : ''}</ReadValue>
            </div>
          ) : (
            <div style={{position:"relative",display:"inline-block"}}>
              <span style={{position:"absolute",top:"50%",transform:"translateY(-50%)",fontSize:12,color:"#999",left:8,pointerEvents:"none"}}>$</span>
              <input data-field="netPrice" value={f.netPrice||""} onChange={function(e){set("netPrice")(e.target.value);}} placeholder="0.00" style={Object.assign({},IS,{width:140,paddingLeft:22,marginBottom:0})} />
            </div>
          )}
        </td>
        <td style={{color:"#bbb",fontSize:12,paddingRight:8,verticalAlign:"middle",paddingBottom:8}}>×</td>
        <td style={{fontSize:10,textTransform:"uppercase",color:"#999",paddingRight:16,whiteSpace:"nowrap",verticalAlign:"middle",paddingBottom:8}}>Qty</td>
        <td style={{verticalAlign:"middle",paddingRight:24,paddingBottom:8}}><span style={{fontSize:14,fontWeight:600,color:"#111"}}>{_q}</span></td>
        <td style={{color:"#bbb",fontSize:12,paddingRight:8,verticalAlign:"middle",paddingBottom:8}}>=</td>
        <td style={{fontSize:10,textTransform:"uppercase",color:"#999",paddingRight:16,whiteSpace:"nowrap",verticalAlign:"middle",paddingBottom:8}}>Net Total</td>
        <td style={{verticalAlign:"middle",paddingBottom:8}}><span style={{fontSize:14,fontWeight:600,color:"#111"}}>{_hp?fmtInput(_netTot):"—"}</span></td>
      </tr>
      <tr>
        <td style={{fontSize:10,textTransform:"uppercase",color:"#999",paddingRight:16,whiteSpace:"nowrap",verticalAlign:"middle",paddingBottom:8}}>Markup</td>
        <td style={{verticalAlign:"middle",paddingRight:4,paddingBottom:8}}>
          {isReadOnly ? (
            <ReadValue dataField="markupPct">{f.markupPct != null ? String(f.markupPct) : ''}</ReadValue>
          ) : (
            <input data-field="markupPct" value={f.markupPct!=null?f.markupPct:""} onChange={function(e){set("markupPct")(e.target.value);}} onFocus={function(e){e.target.select();}} placeholder="30" style={Object.assign({},IS,{width:80,marginBottom:0})} />
          )}
        </td>
        <td style={{color:"#bbb",fontSize:12,paddingRight:8,verticalAlign:"middle",paddingBottom:8}}>%</td>
        <td style={{color:"#bbb",fontSize:12,paddingRight:8,verticalAlign:"middle",paddingBottom:8}}>=</td>
        <td style={{fontSize:10,textTransform:"uppercase",color:"#999",paddingRight:16,whiteSpace:"nowrap",verticalAlign:"middle",paddingBottom:8}}>Client Price / Item</td>
        <td style={{verticalAlign:"middle",paddingRight:24,paddingBottom:8}}><span style={{fontSize:14,fontWeight:600,color:"#111"}}>{_hp?fmtInput(_cpUnit):"—"}</span></td>
        <td style={{fontSize:10,textTransform:"uppercase",color:"#999",paddingRight:16,whiteSpace:"nowrap",verticalAlign:"middle",paddingBottom:8}}>Client Total</td>
        <td style={{verticalAlign:"middle",paddingBottom:8}}><span style={{fontSize:14,fontWeight:600,color:"#111"}}>{_hp?fmtInput(_cpTot):"—"}</span></td>
      </tr>
      <tr>
        <td style={{fontSize:10,textTransform:"uppercase",color:"#999",paddingRight:16,whiteSpace:"nowrap",verticalAlign:"middle"}}>Vendor Deposit %</td>
        <td style={{verticalAlign:"middle",paddingRight:4}}>
          {isReadOnly ? (
            <ReadValue dataField="depositPct">{f.depositPct != null ? String(f.depositPct) : ''}</ReadValue>
          ) : (
            <input data-field="depositPct" value={f.depositPct!=null?f.depositPct:""} onChange={function(e){set("depositPct")(e.target.value);}} onFocus={function(e){e.target.select();}} placeholder="50" style={Object.assign({},IS,{width:80,marginBottom:0})} />
          )}
        </td>
        <td style={{color:"#bbb",fontSize:12,paddingRight:8,verticalAlign:"middle"}}>%</td>
        <td colSpan={5}></td>
      </tr>
      {/* CC Fee % gets its own row so users don't miss the step. A 549%
          data-entry error on 26HOB-P-039 (2026-06-28) showed that
          tucking this into a shared row with Vendor Deposit % made it
          easy to skip — or worse, to set without understanding what
          it does. Row gives it a label, a helper line explaining what
          the multiplier does + the "leave at 0" default, and a live
          "$X recovered" readout once a non-zero value is entered so
          the user can sanity-check the dollar impact in real time. */}
      <tr>
        <td style={{fontSize:10,textTransform:"uppercase",color:"#999",paddingRight:16,whiteSpace:"nowrap",verticalAlign:"middle",paddingTop:10}}>
          CC Fee %
          <div style={{fontSize:9,fontWeight:400,color:"#888",marginTop:2,textTransform:"none",letterSpacing:0}}>vendor surcharge %</div>
        </td>
        <td style={{verticalAlign:"middle",paddingRight:4,paddingTop:10}}>
          {/* RETIRED 2026-09-07 as the recovery mechanism. Card fees now
              recover at ACTUAL cost from the receipt, inside the
              Shipping & Handling line. No NEW item may take a value:
              the input renders only for items that already carry one,
              so the 12 legacy items stay visible + editable (and their
              multiplier keeps working) while the field is closed to
              everything else. Do not re-open it — a percentage of the
              client price over-recovers against a fee charged on the
              vendor's net invoice. */}
          {(isReadOnly || !(parseFloat(f.ccFeePct) > 0)) ? (
            <ReadValue dataField="ccFeePct">{parseFloat(f.ccFeePct) > 0 ? String(f.ccFeePct) : '— retired —'}</ReadValue>
          ) : (
            <input
              data-field="ccFeePct"
              type="number"
              min="0"
              max="50"
              step="0.01"
              value={f.ccFeePct!=null?f.ccFeePct:""}
              onChange={function(e){
                // Defensive clamp at the input boundary. ccFeeMul in utils.js
                // also clamps values >50 to no-op (see CC_FEE_PCT_MAX), but
                // we refuse the bad input here too so the form never holds
                // a value that the PDF would later silently zero out.
                var v = e.target.value;
                var n = parseFloat(v);
                if (isFinite(n) && n > 50) { v = '50'; }
                set("ccFeePct")(v);
              }}
              onFocus={function(e){e.target.select();}}
              placeholder="0"
              style={Object.assign({},IS,{width:80,marginBottom:0},(parseFloat(f.ccFeePct)>0?{border:"0.5px solid #D4007A"}:{}))} />
          )}
        </td>
        <td style={{color:"#bbb",fontSize:12,paddingRight:8,verticalAlign:"middle",paddingTop:10}}>%</td>
        <td colSpan={5} style={{verticalAlign:"middle",paddingTop:10,paddingLeft:8}}>
          {(function(){
            var pct = parseFloat(f.ccFeePct) || 0;
            var hintStyle = {fontSize:10,color:"#888",lineHeight:1.4};
            // Empty state intentionally renders nothing — the label +
            // subtitle ("vendor surcharge %") already explain the field;
            // a helper line on the default-zero state was noise.
            if (pct <= 0) {
              return null;
            }
            if (pct > 50) {
              return <div style={{fontSize:10,color:"#C8322A",fontWeight:500,lineHeight:1.4}}>Capped at 50%. Did you mean {(pct/100).toFixed(2)}%? Update the field to apply.</div>;
            }
            // Live preview of how much this surcharge adds to client
            // billing for this item. _cpTot = client total (price × qty)
            // computed above. Surcharge in dollars = item total × pct / 100.
            var surcharge = _hp && _cpTot ? (parseFloat(_cpTot) * pct / 100) : 0;
            return (
              <div style={hintStyle}>
                <span style={{display:"inline-block",width:6,height:6,borderRadius:"50%",background:"#D4007A",marginRight:6,verticalAlign:"middle"}} />
                {surcharge > 0
                  ? <span style={{fontFeatureSettings:'"tnum","lnum"'}}><span style={{color:"#111",fontWeight:500}}>+{fmtInput(surcharge)}</span> added to this item&apos;s client billing.</span>
                  : <span style={{color:"#111",fontWeight:500}}>Client billed for vendor&apos;s CC surcharge on this item.</span>}
              </div>
            );
          })()}
        </td>
      </tr>
    </tbody></table>
    {_hp?(function(){
      // When a DEP invoice has been issued for this item, display actual
      // paid (itemPaidShare) instead of the recomputed expected deposit.
      // The recomputed value drifts past the actual paid amount whenever
      // the item is re-priced post-DEP (e.g. an FX-delta CO on Kumiko
      // shifted client_price upward, which inflated the predicted
      // "Client total × 80%" display to a number that didn't match either
      // the original DEP amountDue OR the actual cash wire). Reading from
      // payment_allocations matches what the FIN math bills (CLAUDE.md
      // Rule 10).
      var _depInvoiceId = f.clientDepositInvoiceId || '';
      var _allInvoices = appCtx.invoices || [];
      var _allAllocs   = appCtx.paymentAllocations || [];
      var _depInv = _depInvoiceId
        ? _allInvoices.find(function(i){ return i && i.id === _depInvoiceId && (i.invoiceType === 'deposit' || i.invoice_type === 'deposit'); })
        : null;
      if (_depInv) {
        var _paid = itemPaidShare(f, _allInvoices, _allAllocs, appCtx.items || [f]);
        var _depRef = _depInv.invoiceRef || _depInv.invoice_ref || '';
        var _depAmtDue = parseFloat(_depInv.amountDue != null ? _depInv.amountDue : _depInv.amount_due) || 0;
        return <div style={CARD}>
          <div style={CTITLE}>Client Deposit Paid</div>
          <div style={CROW}><span>{"Against DEP " + (_depRef || _depInvoiceId.slice(0,8))}</span><span>{fmtInput(_paid)}</span></div>
          {_depAmtDue > 0 && Math.abs(_paid - _depAmtDue) > 0.01 ? (
            <div style={CROW}>
              <span style={{color:"#888"}}>{"DEP billed " + fmtInput(_depAmtDue)}</span>
              <span style={{color:"#888"}}>{_paid < _depAmtDue ? ("short " + fmtInput(_depAmtDue - _paid)) : ("over " + fmtInput(_paid - _depAmtDue))}</span>
            </div>
          ) : null}
          <div style={CDIV}><span style={{color:"#555"}}>Actual cash recorded</span><span style={{fontWeight:600,color:"#111"}}>{fmtInput(_paid)}</span></div>
        </div>;
      }
      // No DEP yet — show the recomputed prediction (existing behavior).
      return <div style={CARD}>
        <div style={CTITLE}>Client Deposit</div>
        <div style={CROW}><span>{"Client total × "+_cdp+"%"}</span><span>{fmtInput(_cdep)}</span></div>
        {_cdp>=100?<>
          {_feeN(_fs.freight)>0?<div style={CROW}><span>{"+ Freight × "+(1+itemFeeMarkupPct(f)/100).toFixed(2)}</span><span>{fmtInput(applyFreightMarkup(_fs.freight,f))}</span></div>:null}
          {_feeN(_fs.crating)>0?<div style={CROW}><span>{"+ Crating × "+(1+itemFeeMarkupPct(f)/100).toFixed(2)}</span><span>{fmtInput(applyFreightMarkup(_fs.crating,f))}</span></div>:null}
          {_feeN(_fs.salesTax)>0?<div style={CROW}><span>+ Sales Tax</span><span>{fmtInput(_feeN(_fs.salesTax))}</span></div>:null}
          {_feeN(_fs.tariff)>0?<div style={CROW}><span>+ Tariff</span><span>{fmtInput(_feeN(_fs.tariff))}</span></div>:null}
          {(_fs.custom||[]).filter(function(c){return _feeN(c.amount)>0;}).map(function(c,i){var _cp=parseFloat(c.markupPct);if(isNaN(_cp))_cp=itemFeeMarkupPct(f);return <div key={i} style={CROW}><span>{"+ "+(c.label||"Custom fee")+" × "+(1+_cp/100).toFixed(2)}</span><span>{fmtInput(customFeeClient(c,f))}</span></div>;})}
        </>:<div style={CROW}><span style={{color:"#888"}}>Fees collected at final invoice</span><span style={{color:"#aaa"}}>—</span></div>}
        <div style={CDIV}><span style={{color:"#555"}}>Total client deposit</span><span style={{fontWeight:600,color:"#111"}}>{fmtInput(_depTot)}</span></div>
      </div>;
    })():null}
    {_hp?<div style={CARD}>
      <div style={CTITLE}>Vendor Deposit</div>
      <div style={CROW}><span>{"Net total × "+_vdp+"%"}</span><span>{fmtInput(_vdepBase)}</span></div>
      {_vdp>=100?<>
        {_feeN(_fs.freight)>0?<div style={CROW}><span>+ Freight (net)</span><span>{fmtInput(_feeN(_fs.freight))}</span></div>:null}
        {_feeN(_fs.crating)>0?<div style={CROW}><span>+ Crating (net)</span><span>{fmtInput(_feeN(_fs.crating))}</span></div>:null}
        {_feeN(_fs.salesTax)>0?<div style={CROW}><span>+ Sales Tax</span><span>{fmtInput(_feeN(_fs.salesTax))}</span></div>:null}
        {_feeN(_fs.tariff)>0?<div style={CROW}><span>+ Tariff</span><span>{fmtInput(_feeN(_fs.tariff))}</span></div>:null}
        {(_fs.custom||[]).filter(function(c){return _feeN(c.amount)>0;}).map(function(c,i){return <div key={i} style={CROW}><span>{"+ "+(c.label||"Custom fee")}</span><span>{fmtInput(_feeN(c.amount))}</span></div>;})}
      </>:null}
      <div style={CDIV}><span style={{color:"#555"}}>Total vendor deposit</span><span style={{fontWeight:600,color:"#111"}}>{fmtInput(calcVendorDepositWithFees(_n,_q,_vdp,_fs))}</span></div>
    </div>:null}
    <FeesPanel
      fees={f.fees||{}}
      onChange={setFees}
      // Phase 3: pass the resolved project + allItems so the read-only
      // Sales Tax display calls computeItemTax — same helper every
      // payload builder calls. FeesPanel's display matches what lands
      // on the proposal / deposit / final / billing-summary PDF.
      project={(appCtx.projects||[]).find(function(pr){return pr.label===f.project;})||null}
      allItems={appCtx.items||[]}
      clientTotal={parsePrice(f.netPrice)*parseFloat(f.qty||1)*(1+(parseFloat(f.markupPct||30)/100))}
      // Raw price inputs for the per-unit assembly merchandise base —
      // computeItemTax reads them through the synthetic item arg.
      itemNetPrice={f.netPrice}
      itemMarkupPct={f.markupPct}
      itemQty={f.qty}
      feeMarkupPct={f.feeMarkupPct}
      onFeeMarkupPctChange={set("feeMarkupPct")}
      feeRateLocked={!!f.finalInvoiceId || isCancelled(f)}
      assemblyId={f.assemblyId}
      assemblyPeers={f.assemblyId ? (appCtx.items||[]).filter(function(x){return x.assemblyId===f.assemblyId && x.id!==f.id;}) : []}
    />
    <SalesTaxBlock item={f} project={sp} onDeliveredChange={set("deliveredToStateOn")} />
    {otherChildren.length>0?(function(){
      var _childNet=otherChildren.reduce(function(sum,c){return sum+(parsePrice(c.netPrice)*parseFloat(c.qty||1));},0);
      var _mainNet=parsePrice(f.netPrice)*parseFloat(f.qty||1);
      var _asmNet=_mainNet+_childNet;
      var _markup=parseFloat(f.markupPct||30)/100;
      var _asmClient=_asmNet*(1+_markup);
      var _anyPrice=f.netPrice||otherChildren.some(function(c){return c.netPrice;});
      if(!_anyPrice)return null;
      return <div style={{background:"#fafafa",border:"1px solid #E0E0DC",borderRadius:8,padding:"12px 16px",marginTop:12,marginBottom:4}}>
        <div style={{fontSize:11,color:"#888",fontWeight:700,letterSpacing:0.3,marginBottom:8}}>COMPONENT COSTS</div>
        {f.netPrice&&<div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#555",marginBottom:4}}>
          <span>{"Main item"+(parseFloat(f.qty||1)>1?" (\xd7"+parseFloat(f.qty||1)+")":"")}</span>
          <span>{fmtInput(_mainNet)}</span>
        </div>}
        {otherChildren.map(function(c){
          if(!c.netPrice)return null;
          return <div key={c.id} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#555",marginBottom:4}}>
            <span style={{color:"#888"}}>{(c.childType||"Component")+(c.item?" — "+c.item:"")+(c.vendor?" ("+c.vendor+")":"")+(parseFloat(c.qty||1)>1?" \xd7"+parseFloat(c.qty||1):"")}</span>
            <span>{fmtInput(parseFloat(c.netPrice||0)*parseFloat(c.qty||1))}</span>
          </div>;
        })}
        <div style={{display:"flex",justifyContent:"space-between",fontSize:13,fontWeight:700,color:"#111",borderTop:"1px solid #E0E0DC",paddingTop:8,marginTop:4}}>
          <span>Assembly Net Total</span><span>{fmtInput(_asmNet)}</span>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#888",marginTop:4}}>
          <span>{"Assembly Client Total ("+Math.round(parseFloat(f.markupPct||30))+"% markup)"}</span>
          <span style={{fontWeight:600}}>{fmtInput(_asmClient)}</span>
        </div>
      </div>;
    })():null}
    {_hp?<div style={Object.assign({},CARD,{marginBottom:20})}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",marginBottom:showFinal?12:0}} onClick={function(){setShowFinal(function(v){return !v;});}}>
        <div style={{fontSize:11,fontWeight:600,color:"#999",textTransform:"uppercase",letterSpacing:"0.07em"}}>Final Invoice Preview</div>
        <span style={{fontSize:11,color:"#bbb"}}>{showFinal?"▲ Hide":"▼ Show"}</span>
      </div>
      {showFinal?<div>
        {_cdp>=100
          ?<div style={CROW}><span style={{color:"#888"}}>All collected at deposit</span><span style={{color:"#888"}}>$0.00</span></div>
          :<><div style={CROW}><span>20% balance (client total × 20%)</span><span>{fmtInput(_cpTot*0.20)}</span></div>
            {_feeN(_fs.freight)>0?<div style={CROW}><span>{"+ Freight × "+(1+itemFeeMarkupPct(f)/100).toFixed(2)}</span><span>{fmtInput(applyFreightMarkup(_fs.freight,f))}</span></div>:null}
            {_feeN(_fs.crating)>0?<div style={CROW}><span>{"+ Crating × "+(1+itemFeeMarkupPct(f)/100).toFixed(2)}</span><span>{fmtInput(applyFreightMarkup(_fs.crating,f))}</span></div>:null}
            {_feeN(_fs.salesTax)>0?<div style={CROW}><span>+ Sales Tax</span><span>{fmtInput(_feeN(_fs.salesTax))}</span></div>:null}
            {_feeN(_fs.tariff)>0?<div style={CROW}><span>+ Tariff</span><span>{fmtInput(_feeN(_fs.tariff))}</span></div>:null}
            {(_fs.custom||[]).filter(function(c){return _feeN(c.amount)>0;}).map(function(c,i){
              var _cp2=parseFloat(c.markupPct);if(isNaN(_cp2))_cp2=itemFeeMarkupPct(f);return <div key={i} style={CROW}><span>{"+ "+(c.label||"Custom fee")+" × "+(1+_cp2/100).toFixed(2)}</span><span>{fmtInput(customFeeClient(c,f))}</span></div>;
            })}</>}
        <div style={CDIV}><span style={{color:"#555"}}>Total final invoice</span><span style={{fontWeight:600,color:"#111"}}>{fmtInput(_finalTot)}</span></div>
      </div>:null}
    </div>:null}
  </div>;
  }
  }
  // Status & Tracking — split out of the Pricing tab 2026-07-28 (Cimon's
  // tracker-rebuild spec: all pricing on one tab, all status on another).
  // Renders for EVERY procurement class: Owner Furnished / By Others items
  // have no pricing but their physical whereabouts are still tracked
  // (pre-split, the non-procured placeholder replaced the whole tab and
  // OF/BO items had no tracking surface here at all).
  else if(activeTab==="tracking"){
  tabContent=<div style={{maxWidth:560}}>
    <SHead>Status &amp; Tracking</SHead>
    {(function(){
      // Three regimes (tracker rebuild item 5, 2026-07-28):
      //   • Pre-lock (≤ On Proposal): the manually-selectable Dropdown.
      //   • Client Approved / Deposit Paid: read-only — these two are
      //     owned by the MONEY cascades (proposal approval, deposit
      //     payment); a manual write orphans the item per LIFECYCLES.md
      //     Appendix B #1.
      //   • PO Issued and beyond: the TrackingTimeline below owns the
      //     stage control — physical stages are human-marked by design.
      // OF/BO (2026-07-29, Cimon: "seems like we need a cruise through
      // status that says what it is"): no workflow dropdown — the status
      // line names the classification, and the timeline's Location
      // dropdown below is the only control. Proposal inclusion no longer
      // needs a status walk (eligibleForProject admits OF/BO directly).
      if(isNonProcured(f.procurementClass)){
        var npLabel=f.procurementClass==='owner_furnished'?'Owner Furnished':'By Others';
        return <div style={{marginBottom:12}}>
          <Lbl>Status</Lbl>
          <ReadValue dataField="status">{npLabel+' — tracked by location below'}</ReadValue>
        </div>;
      }
      var pickable=manuallySelectableStatusesForProcurementClass(f.procurementClass);
      var statusLocked=pickable.indexOf(f.status)<0;
      var physIdx=STATUSES.indexOf('PO Issued');
      var curIdx=STATUSES.indexOf(f.status);
      var timelineOwns=curIdx>=physIdx&&f.status!=='Cancelled';
      if(timelineOwns)return null;
      return <Dropdown label="Status" value={f.status} onChange={set("status")} dataField="status" locked={statusLocked}
        options={pickable.map(function(s){return {value:s,dot:true};})} />;
    })()}
    <TrackingTimeline item={f} locked={isCancelled(f)} vendors={p.vendors||[]} project={sp}
      onPatch={function(patch){
        setF(function(prev){return Object.assign({},prev,patch);});
        // Saved items COMMIT IMMEDIATELY (2026-07-28 fix — "none of the
        // status information is updating on the tracker"): timeline
        // edits used to sit in form state until Save changes, so stage
        // marks made here never reached the Tracker unless the user
        // remembered to save. The timeline is a record-keeping surface
        // like the Tracker's stage confirm, not a draft field. New
        // (unsaved) items keep form-state-only until first save.
        if(f.id&&p.onUpdate)p.onUpdate(f.id,patch);
      }} />
    <Inp label="Lead Time" value={f.leadTime||""} onChange={set("leadTime")} placeholder="e.g. 14-16 weeks from payment" />
    <div style={{marginBottom:12}}>
      <Inp label="Lead Time (weeks)" value={f.leadWeeks||''} onChange={function(v){var n=parseInt(v,10);set('leadWeeks')(isNaN(n)?null:n);}} placeholder="e.g. 14" type="number" />
      <div style={{fontSize:10,color:"#888",marginTop:-8}}>Required before issuing a PO · used for delivery timeline</div>
    </div>
    {(function(){
      // Below PO Issued the timeline doesn't render — keep the bare
      // order-date field + est-ship hint for early manual entry.
      var physIdx=STATUSES.indexOf('PO Issued');
      if(STATUSES.indexOf(f.status)>=physIdx&&f.status!=='Cancelled')return null;
      return <div style={{marginBottom:12}}>
        <Inp label="Date Ordered" value={f.orderPlacedDate||''} onChange={set('orderPlacedDate')} type="date" dataField="orderPlacedDate" />
        {(function(){
          var iso = estimatedShipDate(f);
          if (!iso) return <div style={{fontSize:10,color:"#888",marginTop:-8}}>Auto-stamped when the vendor deposit receipt is approved · enter manually to override</div>;
          try {
            var d = new Date(iso + "T00:00:00");
            if (isNaN(d.getTime())) return null;
            var pretty = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
            return <div style={{fontSize:10,color:"#0277bd",marginTop:-8,fontWeight:500}}>Est. ship: {pretty}</div>;
          } catch (e) { return null; }
        })()}
      </div>;
    })()}
    <Inp label="Installer / Trade" value={f.installer||""} onChange={set("installer")} placeholder="e.g. ABC Install" />
    {/* Two notes rails (Cimon 2026-07-28): internal tracking chatter vs
        the notes that PRINT on external documents. statusNotes/
        internalNotes are internalOnly in SCHEDULE_FIELDS (hard-refused
        on client/gc/insurance templates); item.notes feeds the NOTES
        column on schedules incl. install. */}
    <Txta label="Internal Status Notes" hint="team only — never on documents" value={f.statusNotes||""} onChange={set("statusNotes")} placeholder="Vendor delays, chase notes, updates..." rows={3} dataField="statusNotes" />
    <Txta label="Install Schedule Notes" labelColor="#D4007A" hint="prints on the install schedule only" value={f.installScheduleNotes||""} onChange={set("installScheduleNotes")} placeholder="e.g. Deliver to garage. Install after flooring." rows={2} dataField="installScheduleNotes" />
    {sentNote}{clientNote}{payNote}
    <SHead>Warehouse Receipts</SHead>
    {(f.wrEntries||[]).map(function(wr,i){return <Row key={wr.id||i}>
      <div style={{flex:2}}><Inp label={i===0?"Description":""} value={wr.description||""} onChange={function(v){setF(function(prev){var n=Object.assign({},prev);n.wrEntries=prev.wrEntries.map(function(e,j){return j===i?Object.assign({},e,{description:v}):e;});return n;});}} placeholder="e.g. Table Top" /></div>
      <div style={{flex:1}}><Inp label={i===0?"WR Number":""} value={wr.wrNumber||""} onChange={function(v){setF(function(prev){var n=Object.assign({},prev);n.wrEntries=prev.wrEntries.map(function(e,j){return j===i?Object.assign({},e,{wrNumber:v}):e;});return n;});}} placeholder="e.g. WR-2026-041" /></div>
      <div style={{flex:1}}><Inp label={i===0?"Date Received":""} value={wr.dateReceived||""} onChange={function(v){setF(function(prev){var n=Object.assign({},prev);n.wrEntries=prev.wrEntries.map(function(e,j){return j===i?Object.assign({},e,{dateReceived:v}):e;});return n;});}} placeholder="e.g. 4/10/2026" /></div>
      <div style={{flex:1}}><Inp label={i===0?"Package #":""} value={wr.packageNumber||""} onChange={function(v){setF(function(prev){var n=Object.assign({},prev);n.wrEntries=prev.wrEntries.map(function(e,j){return j===i?Object.assign({},e,{packageNumber:v}):e;});return n;});}} placeholder="e.g. 1 of 2" /></div>
      <div style={{display:"flex",alignItems:"flex-end",paddingBottom:4}}><button onClick={function(){setF(function(prev){var n=Object.assign({},prev);n.wrEntries=prev.wrEntries.filter(function(_,j){return j!==i;});return n;});}} style={{background:"none",border:"none",color:"#bbb",cursor:"pointer",fontSize:15,padding:"4px 6px"}}>×</button></div>
    </Row>;})}
    <Btn variant="secondary" small={true} onClick={function(){setF(function(prev){var n=Object.assign({},prev);n.wrEntries=(prev.wrEntries||[]).concat([{id:Date.now(),description:"",wrNumber:"",dateReceived:"",packageNumber:""}]);return n;});}}>+ Add WR Entry</Btn>
    <Inp label="PO Number" value={f.poNumber||""} onChange={set("poNumber")} placeholder="PO-2026-001" locked={isReadOnly} dataField="poNumber" />
    {f.proposalRef
      ?<div style={{marginBottom:8}}>
          <div style={{fontSize:10,fontWeight:500,letterSpacing:'0.07em',textTransform:'uppercase',color:'#888880',marginBottom:3}}>Proposal Reference</div>
          <div style={{fontSize:12,color:'#111111'}}>{f.proposalRef}</div>
        </div>
      :<Inp label="Proposal Reference" value={f.proposalRef||""} onChange={set("proposalRef")} placeholder="e.g. Proposal #2026-001" locked={isReadOnly} dataField="proposalRef" />}
  </div>;
  }
  else if(activeTab==="email"){tabContent=<EmailPanel item={f} vendors={p.vendors} projects={p.projects} />;}
  else if(activeTab==="compquotes"){tabContent=<ComponentQuotesPanel item={f} children={otherChildren} vendors={p.vendors} projects={p.projects} />;}
  else if(activeTab==="quotesummary"){tabContent=<QuoteSummaryPanel item={f} docs={quoteDocs} onDocUploaded={function(doc){setQuoteDocs(function(prev){return prev.concat([doc]);});}} />;}
  if(activeTab==="images"){tabContent=<ItemImageGallery itemId={f.id} images={f.images||[]} heroIndex={f.heroIndex||0}
    onChange={function(newImages){
      var currentId=fRef.current.id;
      setF(function(prev){return Object.assign({},prev,{images:newImages,imageUrl:newImages[0]?newImages[0].url:prev.imageUrl});});
      // Save immediately to Supabase
      if(currentId){
        console.log('[Images] Saving',newImages.length,'images to item',currentId);
        supabase.from('items').update({images:JSON.stringify(newImages),image_url:newImages[0]?newImages[0].url:''}).eq('id',currentId).select('id,updated_at')
          .then(function(r){
            if(r.error){console.warn('[Images] Save failed:',r.error.message);}
            else{
              console.log('[Images] Saved OK');
              // Re-learn the rev BEFORE onUpdate — the DB trigger bumped
              // updated_at, and onUpdate triggers a guarded full-row save
              // that would false-conflict against our own write otherwise.
              noteItemRevRows(r.data);
              if(p.onUpdate)p.onUpdate(currentId,{images:newImages,imageUrl:newImages[0]?newImages[0].url:''});
            }
          });
      } else {
        console.warn('[Images] No item ID - image saved to storage but not linked to item. Save the item first.');
      }
    }}
    onHeroChange={function(idx){
      var currentId=fRef.current.id;
      setF(function(prev){return Object.assign({},prev,{heroIndex:idx});});
      if(currentId){
        supabase.from('items').update({hero_index:idx}).eq('id',currentId).select('id,updated_at').then(function(r){
          if(!r.error){noteItemRevRows(r.data);if(p.onUpdate)p.onUpdate(currentId,{heroIndex:idx});}
        });
      }
    }}
  />;}
  if(activeTab==="history"){tabContent=<ItemHistory itemId={f.id} />;}
  if(activeTab==="messages"){tabContent=<ItemMessagesPanel itemId={f.id} />;}
  var comModal=null;if(comEmailChild){comModal=<COMEmailModal child={comEmailChild} parentItem={f.item} sidemark={sidemark} vendors={p.vendors} onClose={function(){setComEmailChild(null);}} />;}
  // "Ready to invoice" banner — surfaces items at Received/Installed without a
  // final invoice yet, so the bookkeeper can find them quickly. The bundling
  // happens in Client Invoices → Orders in Process.
  // Allocation-aware "ready to final" check (since 2026-05-25) —
  // reads payment_allocations from AppContext.
  var readyToFinal = f.id
    && isItemReadyForFinalInvoice(f, appCtx.invoices || [], appCtx.paymentAllocations || [], appCtx.items || [], appCtx.purchaseOrders || [], appCtx.vendorPayments || [])
    && !f.finalInvoiceId
    && canManageCancel;

  return <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:12,color:'#111111'}}>
    {draftBanner}
    {proposalLocked&&<div style={{padding:'9px 14px',background:'#FFF8E1',border:'0.5px solid #F5C800',borderRadius:0,fontSize:11,color:'#BF6800',display:'flex',alignItems:'center',gap:8}}><span style={{fontSize:13}}>🔒</span><span><strong>Proposal sent.</strong> This item is locked. Only the owner can edit it.</span></div>}
    {readyToFinal && (
      <div style={{padding:'9px 14px',background:'#EAF3EE',border:'0.5px solid #B8DEC4',borderRadius:0,fontSize:11,color:'#1A5C38',display:'flex',alignItems:'center',gap:8}}>
        <span style={{fontSize:13}}>✓</span>
        <span><strong>Ready to invoice the client.</strong> Bundle this item with others shipping in the same delivery: <em>Client Invoices → Orders in Process</em>, then Generate Final Invoice.</span>
      </div>
    )}
    {/* Lock state used to display an amber warning banner here. Removed
        2026-05-05: once an item is committed the form is intentionally a
        record (read-mode), not a draft -- the status pill in the header
        is the lock signal, and a quiet "Request Change Order" link sits
        next to Save. No banner needed. */}
    {showSavedToast&&<div style={{
      position:"fixed",top:24,left:"50%",transform:"translateX(-50%)",
      background:"#2e7d32",color:"#fff",padding:"10px 24px",borderRadius:8,
      fontSize:13,fontWeight:600,zIndex:9999,boxShadow:"0 4px 16px rgba(0,0,0,.18)",
      pointerEvents:"none",letterSpacing:0.3,
    }}>✓ Saved</div>}
    <div style={{display:'flex',alignItems:'center',height:44,
      padding:'0 14px',gap:8,borderBottom:'0.5px solid #D8D7D2',
      background:'#F7F7F5'}}>
      <button onClick={p.onCancel}
        style={{background:'none',border:'none',fontSize:11,
          color:'#888880',cursor:'pointer',fontFamily:'inherit',
          padding:0}}>
        ← Items
      </button>
      <span style={{color:'#D8D7D2',fontSize:11}}>/</span>
      <span style={{fontSize:13,fontWeight:500,color:'#111111',flex:1}}>
        {f.productName||f.item||'New item'}
      </span>
      {f.status && f.status !== '' && (
        <div style={{display:'flex',alignItems:'center',
          gap:5,fontSize:11,color:'#555552'}}>
          <div style={{width:7,height:7,borderRadius:'50%',
            background:statusColor(f.status),flexShrink:0}} />
          {f.status}
        </div>
      )}
      {/* Save state pill — DB-confirmed, drives "is it safe to refresh now"
          intuition. In Safari the upsert preflight retries take 1–6s, so
          this pill is the only honest signal; the previous "Saved" toast
          fired optimistically before the DB roundtrip. */}
      {saveState !== 'idle' && (
        <div style={{display:'inline-flex',alignItems:'center',gap:5,
          fontSize:11,
          color: saveState==='saving' ? '#A8005A' : saveState==='saved' ? '#1A5C38' : '#C8322A',
          fontWeight:500, whiteSpace:'nowrap'}}>
          <span style={{width:7,height:7,borderRadius:'50%',
            background: saveState==='saving' ? '#D4007A' : saveState==='saved' ? '#1A5C38' : '#C8322A'}} />
          {saveState==='saving' ? 'Saving…' : saveState==='saved' ? 'Saved' : 'Save failed'}
        </div>
      )}
      <div style={{width:'0.5px',height:18,background:'#D8D7D2',
        margin:'0 2px'}} />
      <ItemSheetButton item={f} vendors={p.vendors} projects={p.projects} />
      <PresentationSpecButton item={f} allItems={p.allItems} />
      {isLocked && (
        // Quiet text link, not a button. Material fields are read-only;
        // this is the path to edit them. Status pill in the row below is
        // the lock signal -- no banner. Cancelled items don't show this.
        <button
          data-action="request-change-order"
          onClick={function(){setCoModalOpen(true);}}
          style={{height:28,padding:'0 10px',background:'transparent',border:'none',
            cursor:'pointer',fontSize:11,fontWeight:500,color:'#555552',
            fontFamily:'inherit',whiteSpace:'nowrap',textDecoration:'underline',
            textUnderlineOffset:3}}>
          Request Change Order
        </button>
      )}
      <button onClick={handleSave} disabled={proposalLocked}
        style={{height:28,padding:'0 11px',borderRadius:5,
          border:'0.5px solid #111111',background:proposalLocked?'#888880':'#111111',
          cursor:proposalLocked?'not-allowed':'pointer',fontSize:11,fontWeight:500,
          color:'#FFFFFF',fontFamily:'inherit',whiteSpace:'nowrap'}}>
        {proposalLocked?'🔒 Locked':f.id?'Save changes':'Add to database'}
      </button>
    </div>
    {/* The Thread — full-size progress rail (item-tracking rebuild Phase 1,
        2026-07-13). Position = physical progress; hollow ring = money
        health derived from payment records. Saved items only — a new
        unsaved item has no records to derive from. */}
    {f.id && !f.parentId && f.procurementClass !== 'by_others' && (
      <div data-testid="item-thread" style={{padding:'12px 14px 8px',
        borderBottom:'0.5px solid #EFEFEC',background:'#FFFFFF',
        overflowX:'auto'}}>
        <Thread size="full" state={threadStateFor(f, {
          items: appCtx.items||[],
          invoices: appCtx.invoices||[],
          allocations: appCtx.paymentAllocations||[],
          vendorPayments: appCtx.vendorPayments||[],
          purchaseOrders: appCtx.purchaseOrders||[],
        }, (function(){var d=new Date();return d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0');})())} />
      </div>
    )}
    {(f.project||f.roomName||f.roomNumber||f.tag||true)&&(
      <div style={{display:'flex',alignItems:'center',
        height:'auto',minHeight:34,padding:'0 14px',
        borderBottom:'0.5px solid #D8D7D2',
        background:'#FFFFFF',gap:0,fontSize:11,
        color:'#555552',flexWrap:'wrap'}}>

        {/* Project pill */}
        <div style={{padding:'6px 12px 6px 0',
          borderRight:'0.5px solid #EFEFEC',marginRight:12,
          display:'flex',alignItems:'center',gap:4}}>
          <span style={{fontSize:10,color:'#888880'}}>Project</span>
          {ctxEditing==='project'
            ? <select
                autoFocus
                value={f.project}
                onChange={function(e){
                  set('project')(e.target.value);
                  set('roomName')('');
                  set('roomNumber')('');
                  setCtxEditing(null);
                }}
                onBlur={function(){setCtxEditing(null);}}
                style={{height:22,border:'0.5px solid #D8D7D2',
                  borderRadius:4,padding:'0 6px',fontSize:11,
                  fontFamily:'inherit',color:'#111111',
                  background:'#FFFFFF',outline:'none'}}>
                {activeProjects(p.projects, f.project).map(function(proj){
                  return <option key={proj.id||proj.label}
                    value={proj.label}>{proj.label}</option>;
                })}
              </select>
            : <span
                onClick={function(){setCtxEditing('project');}}
                style={{fontWeight:500,color:'#111111',cursor:'pointer'}}
                title='Click to change project'>
                {f.project||
                  <span style={{color:'#D4007A',fontStyle:'italic'}}>
                    set project
                  </span>}
              </span>
          }
        </div>

        {/* Room pill */}
        <div style={{padding:'6px 12px 6px 0',
          borderRight:'0.5px solid #EFEFEC',marginRight:12,
          display:'flex',alignItems:'center',gap:4,
          position:'relative'}}>
          <span style={{fontSize:10,color:'#888880'}}>Room</span>
          {ctxEditing==='room'
            ? (function(){
                var roomProject = p.projects.find(function(pr){
                  return pr.label===f.project;
                });
                var roomList = roomProject ? (roomProject.rooms||[]) : [];
                var roomFiltered = roomList.filter(function(r){
                  return !f.roomName ||
                    r.name.toLowerCase().indexOf(
                      f.roomName.toLowerCase()
                    ) >= 0;
                });
                return (
                  <div style={{position:'relative'}}>
                    <input
                      autoFocus
                      value={f.roomName}
                      onChange={function(e){
                        var val = e.target.value;
                        var exact = roomList.find(function(r){
                          return r.name.toLowerCase()===val.toLowerCase();
                        });
                        set('roomName')(val);
                        if(exact) set('roomNumber')(exact.number);
                      }}
                      onBlur={function(){
                        setTimeout(function(){setCtxEditing(null);},150);
                      }}
                      placeholder='Type room name…'
                      style={{height:22,border:'0.5px solid #D8D7D2',
                        borderRadius:4,padding:'0 6px',fontSize:11,
                        fontFamily:'inherit',color:'#111111',
                        background:'#FFFFFF',outline:'none',width:160}}
                    />
                    {roomFiltered.length>0&&
                      !roomList.find(function(r){
                        return r.name.toLowerCase()===
                          f.roomName.toLowerCase();
                      })&&(
                      <div style={{position:'absolute',top:'100%',
                        left:0,background:'#FFFFFF',
                        border:'0.5px solid #D8D7D2',
                        borderRadius:5,zIndex:200,
                        minWidth:220,maxHeight:180,
                        overflowY:'auto',marginTop:2}}>
                        {roomFiltered.map(function(r,i){
                          return (
                            <div key={i}
                              onMouseDown={function(){
                                set('roomName')(r.name);
                                set('roomNumber')(r.number);
                                setCtxEditing(null);
                              }}
                              style={{display:'flex',
                                justifyContent:'space-between',
                                alignItems:'center',
                                padding:'7px 12px',fontSize:11,
                                cursor:'pointer',
                                borderBottom:'0.5px solid #EFEFEC',
                                fontFamily:'inherit'}}>
                              <span>{r.name}</span>
                              <span style={{fontSize:10,
                                color:'#888880',fontWeight:500}}>
                                {r.number}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()
            : <span
                onClick={function(){setCtxEditing('room');}}
                style={{fontWeight:500,color:'#111111',cursor:'pointer'}}
                title='Click to change room'>
                {f.roomName
                  ? [f.roomNumber,f.roomName].filter(Boolean).join(' — ')
                  : <span style={{color:'#D4007A',fontStyle:'italic'}}>
                      set room
                    </span>
                }
              </span>
          }
        </div>

        {/* Tag pill */}
        <div style={{padding:'6px 12px 6px 0',
          borderRight:'0.5px solid #EFEFEC',marginRight:12,
          display:'flex',alignItems:'center',gap:4}}>
          <span style={{fontSize:10,color:'#888880'}}>Tag</span>
          {ctxEditing==='tag'
            ? <input
                autoFocus
                value={f.tag||''}
                onChange={function(e){set('tag')(e.target.value);}}
                onBlur={function(){setCtxEditing(null);}}
                onKeyDown={function(e){
                  if(e.key==='Enter') setCtxEditing(null);
                }}
                placeholder='e.g. L01-101'
                style={{height:22,width:100,
                  border:'0.5px solid #D8D7D2',
                  borderRadius:4,padding:'0 6px',fontSize:11,
                  fontFamily:'inherit',color:'#111111',
                  background:'#FFFFFF',outline:'none'}}
              />
            : <span
                onClick={function(){setCtxEditing('tag');}}
                style={{fontWeight:500,
                  color:f.tag?'#111111':'#D4007A',
                  cursor:'pointer',
                  fontStyle:f.tag?'normal':'italic'}}
                title='Click to set tag'>
                {f.tag||'+ add tag'}
              </span>
          }
        </div>

        {/* Schedule pill */}
        <div style={{padding:'6px 0',
          display:'flex',alignItems:'center',gap:4}}>
          <span style={{fontSize:10,color:'#888880'}}>Schedule</span>
          {ctxEditing==='schedule'
            ? <select
                autoFocus
                value={f.schedule||''}
                onChange={function(e){
                  set('schedule')(e.target.value);
                  setCtxEditing(null);
                }}
                onBlur={function(){setCtxEditing(null);}}
                style={{height:22,border:'0.5px solid #D8D7D2',
                  borderRadius:4,padding:'0 6px',fontSize:11,
                  fontFamily:'inherit',color:'#111111',
                  background:'#FFFFFF',outline:'none'}}>
                <option value=''>— Select —</option>
                {SCHEDULE_NAMES.map(function(s){
                  return <option key={s} value={s}>{s}</option>;
                })}
              </select>
            : <span
                onClick={function(){
                  if(!isFinanciallyCommitted(f.status)){
                    setCtxEditing('schedule');
                  }
                }}
                style={{fontWeight:500,
                  color:f.schedule?'#111111':'#D4007A',
                  cursor:isFinanciallyCommitted(f.status)
                    ?'default':'pointer',
                  fontStyle:f.schedule?'normal':'italic'}}
                title={isFinanciallyCommitted(f.status)
                  ?'Schedule locked after Client Approved'
                  :'Click to change schedule'}>
                {f.schedule||'+ set schedule'}
                {isFinanciallyCommitted(f.status)&&
                  <span style={{fontSize:10,marginLeft:4,opacity:0.5}}>
                    🔒
                  </span>}
              </span>
          }
        </div>

        {/* SKU pill — assignable at Client Approved */}
        <div style={{padding:'6px 12px 6px 12px',
          borderLeft:'0.5px solid #EFEFEC',
          display:'flex',alignItems:'center',gap:6}}>
          <span style={{fontSize:10,color:'#888880'}}>SKU</span>
          {ctxEditing==='sku'
            ? <div style={{display:'flex',alignItems:'center',gap:4}}>
                <input
                  autoFocus
                  value={skuEditVal}
                  onChange={function(e){
                    var val=e.target.value;
                    setSkuEditVal(val);
                    setSkuDupWarning(!!(val.trim()&&(p.allItems||[]).some(function(it){return it.id!==f.id&&it.itemNumber===val.trim();})));
                  }}
                  onKeyDown={function(e){
                    if(e.key==='Enter'){set('itemNumber')(skuEditVal.trim()||null);setCtxEditing(null);setSkuDupWarning(false);}
                    if(e.key==='Escape'){setCtxEditing(null);setSkuDupWarning(false);}
                  }}
                  placeholder='e.g. 26HOB-101-001'
                  style={{height:22,width:120,border:'0.5px solid #D8D7D2',
                    borderRadius:4,padding:'0 6px',fontSize:11,
                    fontFamily:'inherit',color:'#111111',
                    background:'#FFFFFF',outline:'none'}}
                />
                <button
                  onClick={function(){set('itemNumber')(skuEditVal.trim()||null);setCtxEditing(null);setSkuDupWarning(false);}}
                  style={{height:22,padding:'0 8px',borderRadius:4,border:'none',
                    background:skuDupWarning?'#D4007A':'#111111',color:'#FFFFFF',fontSize:10,
                    fontWeight:500,cursor:'pointer',fontFamily:'inherit'}}>
                  {skuDupWarning?'Save Anyway':'Save'}
                </button>
                <button
                  onClick={function(){setCtxEditing(null);setSkuDupWarning(false);}}
                  style={{height:22,padding:'0 8px',borderRadius:4,
                    border:'0.5px solid #D8D7D2',background:'#FFFFFF',
                    color:'#888880',fontSize:10,cursor:'pointer',fontFamily:'inherit'}}>
                  Cancel
                </button>
                {skuDupWarning&&<span style={{fontSize:10,color:'#D4007A',whiteSpace:'nowrap'}}>{'\u26A0 already assigned to another item'}</span>}
              </div>
            : f.itemNumber
              ? <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <span style={{fontWeight:500,color:'#111111',fontSize:11}}>{f.itemNumber}</span>
                  {p.isOwner&&<span
                    onClick={function(){setSkuEditVal(f.itemNumber||'');setSkuDupWarning(false);setCtxEditing('sku');}}
                    style={{fontSize:10,color:'#888880',cursor:'pointer',textDecoration:'underline'}}>
                    Edit
                  </span>}
                </div>
              : isFinanciallyCommitted(f.status)
                ? assigningSKU
                  ? <span style={{fontSize:11,color:'#888880',fontStyle:'italic'}}>Assigning…</span>
                  : <button
                      disabled={!f.id||!f.project||!f.roomNumber}
                      onClick={handleAssignSKU}
                      title={!f.id?'Save item first':!f.project?'Set project first':!f.roomNumber?'Set room first':'Auto-assign SKU from sequence'}
                      style={{height:22,padding:'0 9px',borderRadius:11,
                        border:'0.5px solid #D8D7D2',fontSize:10,fontWeight:500,
                        cursor:(!f.id||!f.project||!f.roomNumber)?'not-allowed':'pointer',
                        fontFamily:'inherit',
                        background:(!f.id||!f.project||!f.roomNumber)?'#F7F7F5':'#111111',
                        color:(!f.id||!f.project||!f.roomNumber)?'#888880':'#FFFFFF'}}>
                      Assign SKU
                    </button>
                : <span style={{fontSize:11,color:'#888880',fontStyle:'italic'}}>
                    Assigned at Client Approved
                  </span>
          }
        </div>

      </div>
    )}
    <div style={{display:'grid',gridTemplateColumns:'1fr minmax(0,320px)',gap:0,alignItems:'start'}}>
      <div style={{borderRight:'0.5px solid #D8D7D2',paddingBottom:60}}>
        <div style={{borderBottom:'0.5px solid #D8D7D2',
          padding:'0 14px',background:'#FFFFFF',
          display:'flex',overflowX:'auto'}}>
          {tabBarItems}
        </div>
        <div style={{padding:'16px 14px'}}>
          {dupWarningEl}
          {tabContent}
        </div>
      </div>

      <div style={{position:'sticky',top:0,padding:14,
        background:'#F7F7F5',minHeight:'100vh'}}>
        <div style={{display:'flex',justifyContent:'space-between',
          alignItems:'center',marginBottom:8}}>
          <div style={{fontSize:9,fontWeight:500,color:'#888880',
            letterSpacing:'0.08em',textTransform:'uppercase'}}>
            Tear sheet
          </div>
          <div style={{display:'flex',gap:3}}>
            {['client','internal'].map(function(m){
              return <button key={m} onClick={function(){setTsMode(m);}}
                style={{height:22,padding:'0 9px',borderRadius:11,
                  border:'0.5px solid #D8D7D2',fontSize:10,fontWeight:500,
                  cursor:'pointer',fontFamily:'inherit',
                  background:tsMode===m?'#111111':'#FFFFFF',
                  color:tsMode===m?'#FFFFFF':'#888880'}}>
                {m==='client'?'Client':'Internal'}
              </button>;
            })}
          </div>
        </div>
        <TearSheet item={f} mode={tsMode} isOwner={p.isOwner} projects={p.projects} vendors={p.vendors} />
      </div>
    </div>
    <div style={{position:'fixed',bottom:0,left:180,right:0,
      background:'#FFFFFF',borderTop:'0.5px solid #D8D7D2',
      padding:'10px 14px',display:'flex',gap:8,zIndex:100,
      fontFamily:"'DM Sans',sans-serif"}}>
      <button onClick={handleSave}
        style={{height:32,padding:'0 14px',borderRadius:5,
          background:'#111111',color:'#FFFFFF',
          border:'0.5px solid #111111',cursor:'pointer',
          fontSize:11,fontWeight:500,fontFamily:'inherit'}}>
        {f.id?'Save changes':'Add to database'}
      </button>
      {autoSavedAt && f.id && (
        <span style={{display:'inline-flex',alignItems:'center',gap:6,
          fontSize:10,color:'#888880',padding:'0 6px',fontFamily:'inherit'}}>
          <span style={{width:6,height:6,borderRadius:'50%',background:'#1A5C38'}} />
          Auto-saved
        </span>
      )}
      <button onClick={p.onCancel}
        style={{height:32,padding:'0 14px',borderRadius:5,
          background:'#FFFFFF',color:'#111111',
          border:'0.5px solid #D8D7D2',cursor:'pointer',
          fontSize:11,fontWeight:500,fontFamily:'inherit'}}>
        Close
      </button>
      {f.id ? (
        <button onClick={duplicateItem}
          style={{height:32,padding:'0 14px',borderRadius:5,
            background:'#FFFFFF',color:'#111111',
            border:'0.5px solid #D8D7D2',cursor:'pointer',
            fontSize:11,fontWeight:500,fontFamily:'inherit'}}>
          Duplicate
        </button>
      ) : null}
      {canManageCancel && f.id && f.status !== 'Cancelled' ? (
        <>
          <div style={{flex:1}} />
          <button onClick={function(){setCancelModalOpen(true);}}
            style={{height:32,padding:'0 14px',borderRadius:5,
              background:'#FFFFFF',color:'#C8322A',
              border:'0.5px solid #C8322A',cursor:'pointer',
              fontSize:11,fontWeight:500,fontFamily:'inherit'}}>
            Cancel Item
          </button>
        </>
      ) : null}
    </div>
    {cancelModalOpen ? <CancelItemModal item={f} onClose={function(){setCancelModalOpen(false);}} onCancelled={function(){setF(function(prev){return Object.assign({},prev,{status:'Cancelled'});});}} /> : null}
    {coModalOpen ? <ChangeOrderModal open={coModalOpen} onClose={function(){setCoModalOpen(false);}} onSaved={function(){setCoModalOpen(false);}} initialItems={[f]} projectLabel={f.project} /> : null}
    {dupToast ? (
      <div style={{position:'fixed',bottom:60,left:'50%',transform:'translateX(-50%)',
        background:'#111',color:'#fff',padding:'10px 18px',borderRadius:6,
        fontSize:12,fontFamily:"'DM Sans',sans-serif",zIndex:101,
        boxShadow:'0 4px 16px rgba(0,0,0,0.2)'}}>
        {dupToast}
      </div>
    ) : null}
    {comModal}
  </div>;
}

export { AssemblyCreateModal, AssemblyDeleteModal };
export default ItemForm;
